/**
 * Lightweight i18n — no external dependency.
 * English is the default; Korean is the first additional locale.
 *
 * Usage:
 *   import { t, useLocale, setLocale } from '../lib/i18n'
 *   const label = t('nav.dashboard')   // "Dashboard" or "대시보드"
 */

import { createElement, useSyncExternalStore, type ReactNode } from 'react'

export type Locale = 'en' | 'ko'

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

// Default locale is Korean (KO-first product). A stored preference, when present,
// overrides it. `localStorage` is guarded so this module is import-safe during
// Node-based SSG prerendering — on the server we fall back to the default ('ko'),
// which keeps prerendered marketing HTML Korean for SEO.
// 프라이빗 모드·쿠키 차단에서는 `typeof localStorage` 조차 던진다(접근자 자체가 throw).
// 모듈 초기화 경로라 여기서 새면 앱 부팅이 통째로 깨진다 — 실패는 기본값으로 흡수한다.
function readStoredLocale(): Locale | null {
  try {
    return (localStorage.getItem('neona-locale') as Locale) || null
  } catch {
    return null
  }
}

let _locale: Locale = readStoredLocale() || 'ko'
const _listeners = new Set<() => void>()

function _notify() { _listeners.forEach((fn) => fn()) }

export function setLocale(l: Locale, persist = true) {
  _locale = l
  // URL-driven marketing locale passes persist=false so it doesn't overwrite the
  // user's saved app-language preference (used by the authed app / account setting).
  if (persist) {
    try {
      localStorage.setItem('neona-locale', l)
    } catch {
      /* 저장 못 해도 이번 세션 표시 언어는 유효하다 */
    }
  }
  _notify()
}

export function getLocale(): Locale { return _locale }

export function useLocale(): Locale {
  return useSyncExternalStore(
    (cb) => { _listeners.add(cb); return () => _listeners.delete(cb) },
    () => _locale,
    () => _locale, // getServerSnapshot — required for SSG prerendering
  )
}

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------

const en: Record<string, string> = {
  // Nav
  'nav.dashboard': 'Dashboard',
  'nav.agents': 'Agents',
  'nav.sessions': 'Sessions',
  'nav.apiKeys': 'API keys',
  'nav.integrations': 'Integrations',
  'nav.docs': 'Docs',
  'nav.contact': 'Contact',
  'nav.pricing': 'Pricing',
  'nav.aicc': 'AICC ↗',
  'aicc.open.opening': 'Opening AICC…',
  'aicc.open.failed': 'Could not open AICC',
  'aicc.open.notEnabled': 'AICC is not enabled for your organization. Ask our team to turn it on.',
  'aicc.open.notConnected': 'AICC setup is not finished for your organization yet. Please let our team know.',
  'aicc.open.unavailable': 'AICC did not respond. Please try again in a moment.',
  'aicc.open.back': 'Back to dashboard',
  'blocked.title': 'This organization uses AICC only',
  'blocked.descAicc': 'The platform is turned off for your organization. Manage your calls in AICC.',
  'blocked.descNoAicc': 'The platform is turned off for your organization. Please contact our team.',
  'nav.team': 'Team',
  'nav.logOut': 'Log out',

  // Page headers
  'dashboard.title': 'Overview',
  'dashboard.desc': 'Usage, sessions, and system health across all your voice agents.',
  'dashboard.provisioningFailed': 'Part of your account setup (AICC connection, phone numbers, etc.) could not be applied when you signed up. Contact our team and we will apply it again.',
  'dashboard.dateRange': 'Date range',
  'dashboard.last7d': '7 days',
  'dashboard.last30d': '30 days',
  'dashboard.last90d': '90 days',
  'dashboard.custom': 'Custom',
  'dashboard.perAgent': 'Per-agent usage',
  'dashboard.agent': 'Agent',
  'dashboard.avgDuration': 'Avg duration',
  'dashboard.noUsageData': 'No usage data for this period.',
  'dashboard.anomalyAlert': 'session(s) this month have approximated or estimated durations — review before settlement.',
  // Usage by member (TASK-16727) — the same period's billed time, grouped by each call's related user
  'dashboard.perMember': 'Usage by member',
  'dashboard.member.colPerson': 'Person',
  'dashboard.member.colStatus': 'Status',
  'dashboard.member.colCalls': 'Calls',
  'dashboard.member.colAi': 'AI time',
  'dashboard.member.colOperator': 'Operator time',
  'dashboard.member.colBilled': 'Billed time',
  'dashboard.member.noUser': 'No person',
  'dashboard.member.otherOrg': 'User from another organization',
  'dashboard.member.former': 'Former member',
  'dashboard.member.total': 'Total',
  'dashboard.member.empty': 'No calls in this period.',
  'dashboard.member.bySource': 'Billed time by source',
  'dashboard.member.source.login': 'Sign-in',
  'dashboard.member.source.api_key': 'API key',
  'dashboard.member.source.widget_key': 'Widget',
  'dashboard.member.source.share_link': 'Share link',
  'dashboard.member.source.inbound_agent': 'Inbound',
  'dashboard.member.source.unknown': 'Unknown',
  'agents.title': 'Agents',
  'agents.desc': 'Create, configure, and manage your voice agents.',
  'agents.edit': 'Edit',
  'agents.delete': 'Delete',
  'agents.confirmDelete': 'Delete this agent?',
  'agents.deactivate': 'Deactivate',
  'agent.activateAgent': 'Activate agent',
  'agent.deleteForever': 'Delete permanently',
  'agents.activate': 'Activate',
  'agents.confirmDeactivate': 'Deactivate this agent? It stays in the list and can be re-activated.',
  'agents.confirmHardDelete': 'Permanently delete? It disappears from every list and cannot be undone.',
  'sessions.title': 'Sessions',
  'sessions.desc': 'View and monitor voice sessions across all your agents.',
  'sessions.anomalyFilter': 'Filtered to billing anomalies',
  'sessions.userFilter': 'Filtered to one person’s calls (all time)',
  'sessions.clearFilter': 'Clear filter',
  'apiKeys.title': 'API keys',
  'apiKeys.desc': 'Issue keys for server-side calls or browser widgets. Keys are shown once on creation.',
  'account.title': 'Account',
  'account.desc': 'Manage your profile, password, and account settings.',

  // Pricing
  'pricing.title': 'Pricing & usage',
  'pricing.desc': 'Monitor your usage and manage your plan.',
  'pricing.currentPlan': 'Current plan',
  'pricing.sessionsUsed': 'Sessions used',
  'pricing.minutesUsed': 'Minutes used',
  'pricing.unlimited': 'Unlimited',
  'pricing.perMonth': '/month',
  'pricing.upgrade': 'Upgrade',
  'pricing.usageWarning': 'You have used {pct}% of your monthly session quota.',
  'pricing.usageLimitReached': 'Monthly session limit reached. Upgrade your plan to continue.',
  'pricing.maxAgents': 'Max agents',
  'pricing.maxConcurrent': 'Max concurrent sessions',
  'pricing.maxSessions': 'Max sessions/month',
  'pricing.maxApiKeys': 'Max API keys',
  'pricing.free': 'Free',
  'pricing.thisMonth': 'This month',

  // Stat labels
  'stat.minutesThisMonth': 'Minutes this month',
  'stat.sessions': 'Sessions',
  'stat.sessionsThisMonth': 'Sessions this month',
  'stat.activeAgents': 'Active agents',
  'stat.activeNow': 'Active now',
  'stat.totalAgents': 'Total agents',
  'stat.totalKeys': 'Total keys',
  'stat.apiSecrets': 'API secrets',
  'stat.widgetKeys': 'Widget keys',
  'stat.lastUsed': 'Last used',

  // Actions
  'action.newAgent': '+ New agent',
  'action.manageAgents': 'Manage agents',
  'action.issueKey': 'Issue key',
  'action.authGuide': 'Auth guide',
  'action.startCall': 'Start call',
  'action.startChat': 'Start chat',
  'newAgent.chatAgent': 'Chat Agent (text only)',
  'agents.chatBadge': 'Chat',
  'chat.test.eyebrow': 'Chat test',
  'chat.test.desc':
    'Text rehearsal with this agent. If this agent is connected to AICC (Integrations › Chat), conversations here appear in the AICC inbox labelled as a chat test, and asking for a human agent rings your real agents. Otherwise nothing leaves this page.',
  'chat.test.aiBadge': 'AI',
  'chat.test.aiBadgeHint': 'Responses are generated by AI',
  'chat.test.introBody':
    'Chat with this agent exactly as a visitor would in the widget — same prompt, same model. Start whenever you are ready.',
  'chat.test.start': 'Start chat test',
  'chat.test.placeholder': 'Type a message…',
  'chat.test.send': 'Send',
  'chat.test.end': 'End test',
  'chat.test.ended': 'Test ended.',
  'chat.test.back': 'Back to agent',
  'chat.test.turnLimit': 'This session reached its turn limit. End the test and start a new one.',
  'chat.test.typing': 'Agent is typing',
  'chat.test.restart': 'Restart test',
  'chat.test.widgetNote': 'This is the exact widget visitors see — same bubbles, streaming, and typing indicator. Each run starts a fresh session.',
  'chat.test.error.start': 'Could not start the chat test. Please try again.',
  'chat.test.error.send': 'Could not get a reply. Your message was kept — try again.',
  'chat.test.error.agent': 'Could not load this agent.',
  'chat.test.code.session_ended': 'This session has ended.',
  'chat.test.code.turn_limit_reached': 'This session reached its turn limit.',
  'chat.test.code.llm_failed': 'The model failed to reply. Please try again.',
  'chat.test.code.llm_not_configured': 'Chat LLM is not configured for this environment.',
  'chat.test.code.rate_limit_exceeded': 'Too many requests — please wait a moment.',
  'chat.test.code.message_invalid': 'This message cannot be sent (empty or too long).',
  'action.startOutbound': 'Outbound call',
  'action.startOutboundCall': 'Place call',
  'action.save': 'Save changes',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.create': 'Create',
  'action.delete': 'Delete',
  'action.restore': 'Restore',
  'action.deactivate': 'Deactivate',
  'action.retry': 'Retry',
  'action.talkToUs': 'Talk to us',
  'action.talkToSales': 'Talk to sales',

  // Cookie consent
  'cookie.message': 'Neona uses cookies to analyze site usage, measure ad performance, and show personalized ads. If you accept, cookie identifiers, visit and usage records, and the email address or phone number you enter on the site (in hashed form) are transferred to and processed by Google LLC (United States). You can use the site without accepting. See our Privacy Policy for details.',
  'cookie.privacy': 'Privacy Policy',
  'cookie.accept': 'Accept',
  'cookie.decline': 'Decline',
  'cookie.settingsLabel': 'Cookie consent',
  'cookie.settingsDesc': 'Allow analytics and advertising cookies',

  // Quick actions
  'quick.createAgent': 'Create an agent',
  'quick.createAgentDesc': 'Start from a template or from scratch.',
  'quick.browseApi': 'Browse the API',
  'quick.browseApiDesc': 'REST, WebRTC, webhooks.',
  'quick.issueKey': 'Issue an API key',
  'quick.issueKeyDesc': 'Server or widget tokens.',
  'quick.talkToUs': 'Talk to us',
  'quick.talkToUsDesc': 'Questions, feedback, or enterprise inquiries.',

  // Auth
  'auth.welcomeBack': 'Welcome back',
  'auth.signIn': 'Sign in to your Neona account.',
  'auth.createAccount': 'Create your account',
  'auth.createAccountDesc': 'A workspace and a starter agent will be set up for you.',
  'auth.noAccount': 'No account?',
  'auth.createOne': 'Talk to sales',
  'auth.noAccountSuffix': ' and we will open one for you.',
  'auth.alreadyHave': 'Already have an account?',
  'auth.logIn': 'Log in',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.fullName': 'Full name',
  'auth.workEmail': 'Work email',
  'auth.rememberMe': 'Remember me',
  'auth.forgotPassword': 'Forgot password?',
  'auth.signUpGoogle': 'Sign up with Google',
  'auth.signInGoogle': 'Sign in with Google',
  'auth.googleSigningIn': 'Signing you in…',
  'auth.googleSignInFailed': "Google sign-in didn't complete",
  'auth.accountNotFound':
    'No Neona Agents account is linked to this email. **Talk to sales** below and we will open one for you.',
  'auth.invalidCredentials':
    'Invalid email or password. If you do not have an account yet, **talk to sales** below and we will open one for you.',
  'auth.loginFailed': "We couldn't sign you in. Check your connection and try again.",
  'auth.inviteRequiredNotice':
    'Sign-up needs an invite link. Contact us and we will send you one.',
  'auth.redirectingToContact': 'Taking you to the contact form…',
  'auth.googlePopupBlocked': 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
  'auth.googleTermsNotice': 'By continuing with Google you agree to our',
  'auth.or': 'or',
  'auth.agreeTerms': 'I agree to the',
  'auth.termsOfService': 'Terms of Service',
  'auth.and': 'and',
  'auth.privacyPolicy': 'Privacy Policy',

  // Contact form
  'contact.title': 'Talk to sales',
  'contact.eyebrow': 'Contact',
  'contact.name': 'Name',
  'contact.email': 'Email',
  'contact.company': 'Company',
  'contact.phone': 'Phone',
  'contact.message': 'Message',
  'contact.privacyAgree': 'I agree to the collection and use of personal information.',
  'contact.submit': 'Submit inquiry',
  'contact.success': 'Thank you!',
  'contact.successDesc': 'Your inquiry has been received. We will contact you soon.',
  'contact.close': 'Close',
  'contact.required': '(required)',
  'contact.namePlaceholder': 'John Doe',
  'contact.companyPlaceholder': 'Company',
  'contact.phonePlaceholder': '+1-000-000-0000',
  'contact.inquiryType': 'What do you need?',
  'contact.inquiryTypePlaceholder': 'Select one (optional)',
  'contact.messagePlaceholder': 'Tell us about your questions or inquiry.',
  'contact.errorPrivacy': 'Please agree to the privacy policy.',
  'contact.errorRequired': 'Name, email, company and phone are required.',
  'contact.errorEmail': 'Please enter a valid email address.',
  'contact.errorGeneric': 'Failed to submit. Please try again later.',

  // Auth editorial
  'auth.editorialQuote': '"What customers remember isn\'t the response speed — it\'s the feeling that their words truly reached someone."',

  // Misc
  'misc.loading': 'Loading…',
  'misc.noData': 'No data yet',
  'misc.vsLast': 'vs last',
  'misc.published': 'published',
  'misc.live': 'live',
  'misc.activeCalls': 'active calls',
  'misc.region': 'region · ap-northeast-2 · seoul',
  'misc.recentSessions': 'Recent sessions',
  'misc.viewAll': 'View all →',
  'misc.viewSessions': 'View sessions',
  'misc.minutesLastN': 'Minutes · last {n} days',
  'misc.minutesRange': 'Minutes · {start} – {end}',
  'misc.vsPrevPeriod': 'vs previous period',
  'misc.minutes': 'minutes',
  'misc.noSessions': 'No sessions yet',
  'misc.noAgents': 'No agents yet',
  'misc.createFirst': 'Create your first voice agent to get started.',

  // API Keys page
  'keys.issueNew': 'Issue new key',
  'keys.keyCreated': 'Key created — copy it now',
  'keys.copyKey': 'Copy key',
  'keys.expiresLabel': 'Expires',
  'keys.shownOnce': 'This is the only time the full key is shown.',
  'keys.active': 'active',
  'keys.serverSide': 'server-side',
  'keys.browserDomainBound': 'browser, domain-bound',
  'keys.mostRecentActivity': 'most recent activity',
  'keys.noActivityYet': 'no activity yet',
  'keys.searchKeys': 'Search keys…',
  'keys.loadingKeys': 'Loading…',
  'keys.loadError': 'Failed to load API keys. Please try again.',
  'keys.noKeysYet': 'No keys yet. Issue your first key above.',
  'keys.noKeysYetMember': 'No API keys yet. Organization owners and admins can issue keys.',
  'keys.noKeysMatch': 'No keys match your filter.',
  'keys.keyName': 'Key name',
  'keys.keyNamePlaceholder': 'e.g. Production server',
  'keys.keyType': 'Key type',
  'keys.apiSecret': 'API secret',
  'keys.widgetKey': 'Widget key',
  'keys.apiSecretDesc': 'Server-side, keep private',
  'keys.widgetKeyDesc': 'Browser, domain-restricted',
  'keys.allowedDomains': 'Allowed domains',
  'keys.domainsHint': 'Comma-separated. Supports wildcards like *.example.com.',
  'keys.domainsPlaceholder': '*.example.com, app.example.com',
  'keys.expiration': 'Expiration',
  'keys.never': 'Never',
  'keys.30days': '30 days',
  'keys.90days': '90 days',
  'keys.1year': '1 year',
  'keys.creating': 'Creating…',
  'keys.createKey': 'Create key',
  'keys.deactivating': 'Deactivating…',
  'keys.adminOnly': 'Only organization owners and admins can issue or deactivate API keys.',
  'keys.confirmDeactivateDesc': 'This action is permanent. Type the key name to confirm.',
  'keys.rename': 'Rename',
  'keys.saving': 'Saving…',
  'keys.domains': 'Domains',
  'keys.created': 'Created',
  'keys.inactive': 'inactive',

  // Agent new page
  'newAgent.breadcrumb': 'Agents → New agent',
  'newAgent.title': 'Create an agent',
  'newAgent.desc': 'Set up a new voice AI agent. You can adjust all settings after creation.',
  'newAgent.stepBasics': 'Basics',
  'Flow': 'Flow',
  'Save': 'Save',
  'Saving...': 'Saving...',
  'Save failed': 'Save failed',
  'Clear': 'Clear',
  'agent.voiceAgentFromFlow': 'Voice Agent (built as a flow)',
  'flow.retiredEyebrow': 'Single prompt',
  'flow.retiredTitle': 'This agent now answers from a single prompt',
  'flow.retiredBody':
    'The engine no longer runs saved flows — every call is handled by one prompt. Set what this agent should say in the Conversation tab.',
  'flow.retiredEyebrowUrgent': 'Setup needed',
  'flow.retiredTitleUrgent': 'This agent has no instructions yet',
  'flow.retiredBodyUrgent':
    'Its instructions used to live inside the flow, and the engine no longer reads flows — so this agent is answering calls with nothing to go on. Write its instructions in the Conversation tab.',
  'flow.retiredPrevious': 'The flow had {count} steps:',
  'flow.retiredCta': 'Go to Conversation',
  'newAgent.stepConversation': 'Prompt',
  'newAgent.stepVoice': 'Voice',
  'newAgent.stepReview': 'Review',
  'newAgent.identity': 'Identity',
  'newAgent.identityDesc': 'How your agent shows up in the dashboard.',
  'newAgent.name': 'Name',
  'newAgent.agentType': 'Agent type',
  'newAgent.voiceAgent': 'Single Agent',
  'newAgent.description': 'Description',
  'newAgent.primaryLang': 'Primary language',
  'newAgent.templateDesc': 'Start from a blueprint or from scratch.',
  'newAgent.blankDesc': 'Start from scratch.',
  'newAgent.supportDesc': 'Handle refund, delivery, account questions.',
  'newAgent.outboundDesc': 'Renewal reminders and surveys.',
  'newAgent.conversation': 'Conversation',
  'newAgent.conversationDesc': 'Shape how the agent thinks and opens the call.',
  'newAgent.systemPrompt': 'System prompt',
  'newAgent.systemPromptHint': 'Defines the agent\'s personality and behavior. Use {{variable}} for runtime substitution.',
  'newAgent.greeting': 'Greeting',
  'newAgent.greetingHint': 'First line spoken when a session starts.',
  'newAgent.voice': 'Voice',
  'newAgent.voiceDesc': 'The character your agent speaks with.',
  'newAgent.selectVoice': 'Click to select a voice',
  'newAgent.selectBaramCharacter': 'Select an avatar character',
  'newAgent.change': 'Change',
  'newAgent.models': 'Models',
  'newAgent.modelsDesc': 'The stack behind the conversation.',
  'newAgent.sttProvider': 'STT provider',
  'newAgent.llmProvider': 'LLM provider',
  'newAgent.llmModel': 'LLM model',
  'newAgent.reviewTitle': 'Review',
  'newAgent.reviewDesc': 'Last look before we provision it.',
  'newAgent.nameRequired': 'Agent name is required',
  'newAgent.systemPromptRequired': 'System prompt is required',
  'newAgent.continue': 'Continue →',
  'newAgent.createAgent': 'Create agent →',
  'newAgent.livePreview': 'Live preview',
  'newAgent.previewNote': 'This is a static preview of how your agent will appear. No live session is created.',

  // Account page
  'account.profile': 'Profile',
  'account.profileDesc': 'Your display name shown in the app.',
  'account.name': 'Name',
  'account.profileImage': 'Profile image URL',
  'account.saved': 'Saved.',
  'account.preferences': 'Preferences',
  'account.preferencesDesc': 'Customize your experience.',
  'account.language': 'Language',
  'account.changePassword': 'Change password',
  'account.changePasswordDesc': 'All existing sessions will be signed out after a successful change.',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.newPasswordHint': 'At least 8 characters.',
  'account.confirmPassword': 'Confirm new password',
  'account.passwordChanged': 'Password changed.',
  'account.passwordsNoMatch': 'New passwords do not match.',
  'account.passwordTooShort': 'New password must be at least 8 characters.',
  'account.deleteAccount': 'Delete account',
  'account.deleteDesc': 'Permanently delete your account. Your personal information will be removed, but some records (such as past invoices) are retained as required by law. This action cannot be undone.',
  'account.deleteConfirmDesc': 'This cannot be undone. To confirm, type your email and enter your current password.',
  'account.typeEmail': 'Type your email to confirm',
  'account.deleteButton': 'Delete my account',
  'account.deletePermanent': 'Permanently delete account',
  'account.emailNoMatch': 'Email does not match.',
  'account.passwordRequired': 'Password required.',
  'account.deleteOwnerHasMembers': 'You can’t delete the owner account while your organization has other members. Remove them on the Team page first, then try again.',

  // API Doc
  'apiDoc.logIn': 'Log in',
  'apiDoc.getStarted': 'Get started',
  'apiDoc.apiRef': 'API Reference',
  'apiDoc.heroTitle': 'Build voice AI agents with Neona.',
  'apiDoc.heroSub': 'REST endpoints for creating agents, browsing voices, and starting live voice sessions.',
  // 문서 블록의 고정 문구(TASK-16438) — 문서 내용은 서버가 언어별로 준다
  'apiDoc.pathParams': 'Path parameters',
  'apiDoc.queryParams': 'Query parameters',
  'apiDoc.bodyParams': 'Body parameters',
  'apiDoc.required': 'required',
  'apiDoc.authorization': 'Authorization:',
  'apiDoc.response': 'Response',
  'apiDoc.copy': 'Copy',
  'apiDoc.copied': 'Copied',
  'apiDoc.loadFailed': 'Could not load the API docs.',
  'apiDoc.retry': 'Try again',
  'apiDoc.questions': 'Questions?',
  'apiDoc.contactUs': 'Contact us →',

  // Login extras
  'auth.continueGoogle': 'Continue with Google',
  'auth.remember30': 'Remember me for 30 days',
  'auth.logInButton': 'Log in',

  // Signup extras
  'auth.createAccountButton': 'Create account',
  'auth.agreeTermsError': 'Please agree to the Terms of Service and Privacy Policy.',
  'auth.passwordMin8': 'Password must be at least 8 characters.',
  'auth.passwordHint': 'At least 8 characters.',
  'auth.birthdate': 'Date of birth',
  'auth.birthdateHint': 'You must be at least 14 to sign up.',
  'auth.birthdateRequired': 'Please enter your date of birth.',
  'auth.underageError': 'You must be at least 14 years old to sign up.',

  // Landing
  'landing.hero': 'Voice infrastructure for the',
  'landing.heroAccent': 'modern internet',
  'landing.heroSub': 'Ship production-grade voice AI agents with a single API. No transcription pipeline, no TTS stack, no infrastructure to manage — just an agent that speaks, listens, and gets things done.',
  'landing.startBuilding': 'Start building — free',
  'landing.documentation': 'Documentation',
  'landing.signIn': 'Sign in',
  'landing.pricing': 'Pricing',
  'landing.pricingTitle': 'Beta',
  'landing.pricingDesc': 'Neona is currently in beta. All features are available for free during the beta period. For enterprise inquiries or custom plans, reach out to our team.',

  // Landing extras
  'landing.howItWorks': 'How it works',
  'landing.threeSteps': 'Three steps to go live',
  'landing.threeStepsDesc': 'Create an agent, generate a widget, and deploy to your site. That\'s it.',
  'landing.useCases': 'Use cases',
  'landing.useCasesTitle': 'Built for every voice touchpoint',
  'landing.voices': 'Voices',
  'landing.voicesTitle': 'Natural voices in 500+ styles',
  'landing.voicesDesc': 'Choose from a library of expressive, low-latency voices across 28 languages — or bring your own with voice cloning.',
  'landing.readyToShip': 'Ready to ship your first voice agent?',
  'landing.noCardRequired': 'Sign up in seconds. No credit card required.',
  'landing.talkToSales': 'Talk to sales',
  'landing.privacy': 'Privacy',
  'landing.terms': 'Terms',

  // Share links
  'shareLinks.title': 'Share links',
  'shareLinks.desc': 'Generate a link to let anyone test this agent — no login required.',
  'shareLinks.create': 'Create link',
  'shareLinks.namePlaceholder': 'Link name (e.g. Demo for client A)',
  'shareLinks.copyLink': 'Copy link',
  'shareLinks.deactivate': 'Deactivate',
  'shareLinks.deactivateConfirm': 'Deactivate this share link?',
  'shareLinks.noLinks': 'No share links yet.',
  'shareLinks.untitled': 'Untitled link',

  // Try page
  'try.unavailable': 'This link is no longer available.',
  'try.unavailableDesc': 'The share link has been deactivated or does not exist.',
  'try.backHome': 'Back to home',
  'try.startCall': 'Start call',
  'try.startChat': 'Start chat',
  'try.endCall': 'End call',
  'try.connecting': 'Connecting\u2026',
  'try.connected': 'Connected',
  'try.speaking': 'Agent speaking',
  'try.listening': 'Listening\u2026',
  'try.ended': 'Call ended',
  'try.tryAgain': 'Try again',
  'try.poweredBy': 'Powered by Neona',

  // Onboarding

  // Team (TASK-16726 — members · invitations · removal)
  'team.eyebrow': 'Organization',
  'team.title': 'Team',
  'team.desc': 'Manage members and invitations. Members share this organization’s agents, calls and API keys.',
  'team.seats': 'Members {members} / limit {limit} ({pending} pending)',
  'team.unlimited': 'unlimited',
  'team.loadError': 'Could not load the team. Please refresh.',
  'team.inviteTitle': 'Invite a member',
  'team.inviteDesc': 'We email a sign-up link. The invitee joins this organization by signing up or signing in from it. The link works once, for 7 days.',
  'team.email': 'Email address',
  'team.role': 'Role',
  'team.sendInvite': 'Send invite',
  'team.noSeatsZero': 'This organization cannot invite members yet (limit 0). Contact Neona to raise the limit.',
  'team.noSeatsFull': 'No seats left. Revoke a pending invitation to free one, or contact Neona to raise the limit.',
  'team.inviteSent': 'Invitation email sent to {email}.',
  'team.inviteMailFailed': 'The invitation for {email} was created, but the email could not be sent. Use “Resend” under Sent invitations.',
  'team.inviteResent': 'Invitation resent to {email}. The previous link no longer works.',
  'team.inviteRevoked': 'Revoked the invitation for {email}.',
  'team.membersTitle': 'Members',
  'team.invitesTitle': 'Sent invitations',
  'team.invitesLoadError': 'Could not load invitations.',
  'team.noInvites': 'No invitations yet.',
  'team.col.name': 'Name',
  'team.col.email': 'Email',
  'team.col.role': 'Role',
  'team.col.status': 'Status',
  'team.col.mail': 'Delivery',
  'team.col.expires': 'Expires',
  'team.col.invitedBy': 'Invited by',
  'team.owner': 'Owner',
  'team.admin': 'Admin',
  'team.member': 'Member',
  'team.you': 'you',
  'team.roleFor': 'Role for {email}',
  'team.roleChanged': 'Changed the role of {email} to {role}.',
  'team.status.active': 'Active',
  'team.status.unapproved': 'Awaiting approval',
  'team.inviteStatus.pending': 'Pending',
  'team.inviteStatus.accepted': 'Accepted',
  'team.inviteStatus.revoked': 'Revoked',
  'team.inviteStatus.expired': 'Expired',
  'team.inviteStatus.removed': 'Removed',
  'team.inviteStatus.withdrawn': 'Left',
  'team.mail.sent': 'Sent',
  'team.mail.failed': 'Send failed',
  'team.resend': 'Resend',
  'team.revoke': 'Revoke',
  'team.revokeTitle': 'Revoke this invitation?',
  'team.revokeBody': 'The link sent to {email} stops working right away. To invite them again, send a new invitation.',
  'team.revokeConfirm': 'Revoke invitation',
  'team.remove': 'Remove',
  'team.removeTitle': 'Remove {name}?',
  'team.removeBody': '{email} will lose access to this organization’s console and API and will be signed out everywhere. Agents and calls they created stay with the organization. To come back they need a new invitation.',
  'team.removeKeysLoading': 'Checking the API keys this member created…',
  'team.removeKeysLoadError': 'Could not load the API keys this member created, so they cannot be removed yet. Close this and try again.',
  'team.removeNoKeys': 'This member has no active API keys.',
  'team.removeKeysIntro': 'This member created {count} active API key(s). Keys keep working after removal — choose any to revoke now.',
  'team.removeKeysKeepNote': 'Keys you don’t choose stay with the organization and keep working. Revoking a key used by an integration stops that integration.',
  'team.revokeNow': 'Revoke now',
  'team.keyLastUsed': 'Last used {when}',
  'team.keyNeverUsed': 'Never used',
  'team.removeConfirm': 'Remove',
  'team.removed': 'Removed {email}.',
  'team.removedRevoked': 'Revoked keys: {names}',
  'team.removedRevokeFailed': 'Could not revoke: {names} — revoke them on the API keys page.',
  'team.removedKept': 'Kept keys: {names} — they keep working. The member knows these keys, so revoke them on the API keys page if needed.',
  'team.noMembers': 'No team members yet.',
  'team.err.generic': 'Something went wrong. Please try again.',
  'team.err.adminOnly': 'Only an organization owner or admin can manage members.',
  'team.err.invalidEmail': 'Check the email address.',
  'team.err.noSeats': 'No seats left. Revoke a pending invitation, or contact Neona to raise the limit.',
  'team.err.tooManyRequests': 'Too many requests. Please try again in a moment.',
  'team.err.alreadyMember': 'This person is already a member of this organization.',
  'team.err.userInOtherOrg': 'This account belongs to another organization, so it cannot be invited. Contact Neona to move it.',
  'team.err.accountBlocked': 'This email belongs to a deactivated or unapproved account, so it cannot be invited. Contact Neona.',
  'team.err.provisioningPending': 'This email has a pending provisioning reservation, so it cannot be invited. Contact Neona.',
  'team.err.inviteConflict': 'Another invitation for this email was being created at the same time. Please try again.',
  'team.err.inviteChanged': 'This invitation changed in the meantime. The list has been refreshed.',
  'team.err.resendTooSoon': 'An invitation can be sent once a minute. Please try again in a minute.',
  'team.err.memberNotFound': 'This person is no longer in the organization. The list has been refreshed.',
  'team.err.memberChanged': 'Another admin changed this member in the meantime. The list has been refreshed.',
  'team.err.memberInCall': 'This member is on a call and cannot be removed. Try again after the call ends.',
  'team.err.apiKeyNotOwned': 'Some selected keys are no longer this member’s active keys. The key list has been refreshed — check it and try again.',
  'team.err.cannotRemoveSelf': 'You cannot remove yourself.',
  'team.err.cannotRemoveOwner': 'The organization owner cannot be removed.',
  'team.err.cannotChangeOwner': 'The owner’s role cannot be changed.',

  // No organization (TASK-16726 — a removed member signs in)
  'noOrg.title': 'You are not part of an organization',
  'noOrg.desc': 'Join with an invitation link. Ask your organization’s admin to invite you — the link arrives by email.',

  // Team invitation link — sign-up · sign-in · accept (TASK-16726)
  'teamInvite.createDesc': 'Create an account to join the organization that invited you.',
  'teamInvite.loading': 'Checking your invitation…',
  'teamInvite.banner': '**{org}** invited you as {role}.',
  'teamInvite.emailLocked': 'Only the invited email can sign up · valid until {date}',
  'teamInvite.haveAccount': 'Already have an account?',
  'teamInvite.loginAndJoin': 'Sign in and join',
  'teamInvite.loginNotice': 'Signing in will add you to the organization that invited you.',
  'teamInvite.signupWithInvite': 'Sign up with your invitation',
  'teamInvite.invalidTitle': 'This invitation is not valid',
  'teamInvite.reason.not_found': 'We could not find this invitation. If it was sent again, open the link in the newest email.',
  'teamInvite.reason.expired': 'The invitation has expired (7 days). Ask the admin who invited you to send it again.',
  'teamInvite.reason.revoked': 'The invitation was revoked. Contact the admin who invited you.',
  'teamInvite.reason.accepted': 'This invitation has already been used. Please sign in.',
  'teamInvite.reason.organization_inactive': 'The organization that invited you is no longer active.',
  'teamInvite.reason.unknown': 'The link has expired, was revoked, was already used, or was replaced by a newer email. Ask the admin who invited you to send it again.',
  'teamInvite.lookupFailed': 'We could not check the invitation. Please open the link in the email again in a moment.',
  'teamInvite.accepting': 'Accepting the invitation…',
  'teamInvite.joinedTitle': 'You joined the team',
  'teamInvite.joinedDesc': 'You can now use the {org} console.',
  'teamInvite.openConsole': 'Go to the console',
  'teamInvite.acceptFailedTitle': 'Could not join the team',
  'teamInvite.acceptFailed': 'Could not accept the invitation. Please try again in a moment.',
  'teamInvite.switchAccount': 'Sign out and use another account',
  'teamInvite.backToConsole': 'Back to the console',
  'teamInvite.err.emailMismatch': 'This account does not match the invited email. Continue with the account for the invited email.',
  'teamInvite.err.provisioningPending': 'This email has a pending provisioning reservation, so it cannot join through an invitation. Contact Neona.',
  'teamInvite.err.alreadyMember': 'You are already a member of this organization.',
  'teamInvite.err.userInOtherOrg': 'This account belongs to another organization, so it cannot join. Contact Neona to move it.',
  'teamInvite.err.accountBlocked': 'This account is not approved, so it cannot join. Contact Neona.',
  'teamInvite.err.emailRegistered': 'An account with this email already exists. Choose “Sign in and join” below.',
  'teamInvite.err.exclusive': 'This link cannot be combined with another sign-up link. Open the invitation link in the email again.',
  'teamInvite.err.noAccount': 'There is no account for this Google account yet. Sign up with your invitation below.',
  'teamInvite.err.tooManyRequests': 'Too many requests. Please try again in a moment.',

  // Agent detail - tabs
  'tab.overview': 'General',
  'tab.flow': 'Flow',
  'tab.conversation': 'Prompt',
  'tab.voice': 'Voice',
  'tab.settings': 'Settings',
  'tab.evaluation': 'Evaluation',
  'tab.deploy': 'Deploy',
  'tab.history': 'History',
  'tab.integration': 'Integrations',
  'callStart.title': 'Integrations',
  'callStart.intro': 'When a call or conversation starts, data is fetched from your APIs and passed to the AI agent as context — and shown to human agents too. Runs in the background; if a request fails, the call goes on without it.',
  'callStart.lookupTitle': 'Live Context',
  'callStart.extractHintShort': 'Response paths',
  'callStart.enabled': 'Run lookup when calls start (applies on save)',
  'callStart.switchOn': 'ON',
  'callStart.switchOff': 'OFF',
  'callStart.section.connect': 'Connection',
  'callStart.section.connectDesc': "Your API's base address and how we authenticate.",
  'callStart.section.request': 'Requests',
  'callStart.section.requestDesc': 'Up to 3 requests run in parallel. A request that uses a value from another request runs after it.',
  'callStart.section.extract': 'Values to extract',
  'callStart.section.extractDesc': 'Pick values from each response. Only values with a context name are passed — to the AI agent and the agent card — in this order.',
  'callStart.section.test': 'Full test',
  'callStart.section.testDesc': 'Run all requests with the current settings before saving. Nothing is saved.',
  'callStart.baseUrl': 'Base URL (https)',
  'callStart.authType': 'Authentication',
  'callStart.auth.none': 'None',
  'callStart.auth.header': 'Header',
  'callStart.auth.query': 'Query parameter',
  'callStart.authDesc.none': 'Calls are sent without credentials.',
  'callStart.authDesc.header': 'The secret is sent as-is in the header you name. For token auth use header "Authorization" with the value "Bearer <token>"; custom headers like x-api-key work too.',
  'callStart.authDesc.query': "The secret is sent in the URL and may remain in your server's access logs. Prefer a header if your API supports it.",
  'callStart.headerName': 'Header name',
  'callStart.queryParam': 'Query parameter name',
  'callStart.secret': 'Secret',
  'callStart.secretSet': 'Set {last4} · leave empty to keep',
  'callStart.secretHeaderHint': '`Bearer <token>` or the API key',
  'callStart.timeout': 'Timeout per call (ms)',
  'callStart.expand': 'Expand',
  'callStart.method': 'Method',
  'callStart.path': 'Path',
  'callStart.pathEmpty': '(no path yet)',
  'callStart.query': 'Query parameters',
  'callStart.body': 'JSON body',
  'callStart.templateHelp': 'Caller number: {{caller.phone|format}} (e164 · kr_local · kr_dash · digits). Values from other requests: {{variable}} — type it into the path too. Query and body values suggest these as you type.',
  'callStart.col.var': 'Variable',
  'callStart.col.path': 'Response path',
  'callStart.col.label': 'Context name',
  'callStart.col.require': 'Required',
  'callStart.col.memberName': 'Customer name',
  'callStart.extractLabelPlaceholder': 'Empty = not passed',
  'callStart.extractHint': 'Response path: dot path with array indexes, e.g. users.0.userId. "Required": if that value is empty, the lookup ends as "No data" and requests that use it are not sent (click again to clear). "Customer name": AICC fills an unnamed case\'s customer name with this value (click again to clear).',
  'callStart.extractNone': 'Nothing extracted from this request yet.',
  'callStart.addRow': 'Add',
  'callStart.removeRow': 'Remove',
  'callStart.testWarn': "Calls your system's API for real. Use a test number.",
  'callStart.testPhone': 'Caller number',
  'callStart.testRun': 'Run test',
  'callStart.testFailed': 'Could not run the test.',
  'callStart.needPhone': 'Enter a caller number to test with.',
  'callStart.test.noSteps': 'No request was sent.',
  'callStart.test.values': 'Extracted values',
  'callStart.test.card': 'Agent card preview',
  'callStart.test.cardEmpty': 'Nothing to show — the card appears only on success with values that have a context name.',
  'callStart.outcome.ok': 'OK',
  'callStart.result.success': 'Success',
  'callStart.result.failure': 'Failed',
  'callStart.result.other': 'Other',
  'callStart.outcome.found': 'Value passed',
  'callStart.outcome.not_found': 'No data',
  'callStart.outcome.skipped': 'No caller number',
  'callStart.outcome.http_error': 'HTTP error',
  'callStart.outcome.mapping_error': 'Unusable response',
  'callStart.outcome.timeout': 'Timed out',
  'callStart.outcome.network': 'Network error',
  'callStart.outcome.blocked_ssrf': 'Blocked address',
  'callStart.outcome.secret_error': 'Secret error',
  'callStart.outcome.overloaded': 'Too many lookups at once',
  'callStart.fixErrors': 'Please check the highlighted fields.',
  'callStart.err.required': 'Required.',
  'callStart.err.https': 'Must start with https://',
  'callStart.err.new_host': 'Re-enter the secret when changing the API host — the saved one is not sent to a new host.',
  'callStart.err.range': 'Enter 500–5000.',
  'callStart.err.path_format': "Must start with '/' — no ?, #, spaces or '..'.",
  'callStart.err.template': 'Only caller.phone|format or a variable name can go inside {{ }}.',
  'callStart.err.unknown_var': '{{{name}}} is not extracted by another request.',
  'callStart.err.key_format': 'Letters, digits and _ . - only.',
  'callStart.err.var_required': 'Enter a variable name.',
  'callStart.err.var_format': 'Start with a lowercase letter; lowercase letters, digits, _ only.',
  'callStart.err.var_dup': '{name} is already extracted.',
  'callStart.err.extract_required': 'Enter a response path.',
  'callStart.err.extract_format': 'Use a dot path, e.g. users.0.userId.',
  'callStart.deleted': 'Deleted',
  'callStart.delete': 'Delete',
  'callStart.deleteConfirm': "Delete this agent's live context? Its stored credentials are removed too, and from the next call on nothing is passed to the AI agent or agent console.",
  'callStart.forbidden': 'Only an organization owner or admin can manage integrations.',
  'callStart.lookupExists': 'This agent already has a live context. Reload the page.',
  'callStart.invalidInput': 'Please check the input values.',
  'callStart.saveFailed': 'Save failed. Please try again.',
  'callStart.loadError': 'Could not load integration settings.',
  'callStart.trigger.callStart': 'On call start',
  'callStart.addRequest': 'Add request',
  'callStart.removeRequest': 'Remove request',
  'callStart.usesValuesOf': 'Uses {names}',
  'callStart.testRequest': 'Test',
  'callStart.testRequestTip': "Call only this request and see its response. Calls your system's API for real.",
  'callStart.test.response': 'Response',
  'callStart.test.truncated': '…truncated',
  'callStart.test.context': 'Context passed to the agent',
  'callStart.test.contextEmpty': 'No context to pass.',
  'callStart.outcome.failed': 'Failed',
  'callStart.runs.title': 'Recent runs',
  'callStart.runs.desc': 'How this lookup ended on each call — the latest {n} calls. Expand a row to see each request.',
  'callStart.runs.refresh': 'Refresh',
  'callStart.runs.empty': 'No runs yet. A run is recorded each time a call starts.',
  'callStart.runs.loadError': 'Could not load recent runs.',
  'callStart.runs.latencyTip': "Slowest request — requests that don't use each other's values run at the same time.",
  'callStart.runs.revision': 'settings v{n}',
  'callStart.runs.openSession': 'Open session',
  'callStart.runs.col.request': 'Request',
  'callStart.runs.col.path': 'Path',
  'callStart.runs.col.status': 'HTTP status',
  'callStart.runs.col.latency': 'Time',
  'callStart.runs.col.outcome': 'Result',
  'callStart.runs.col.error': 'Error',
  'callStart.runs.openOrg': 'View across organization →',
  'nav.knowledge': 'Knowledge',
  'knowledge.title': 'Knowledge',
  'knowledge.desc': 'Browse and search the knowledge bases (RAG) linked to your organization. Read-only.',
  'knowledge.loadFailed': 'Failed to load knowledge bases.',
  'knowledge.empty': 'No knowledge base is linked to your organization yet.',
  'knowledge.select': 'Knowledge base',
  'knowledge.notIngested': 'Nothing has been ingested into this knowledge base yet.',
  'knowledge.meta.documents': '{n} documents',
  'knowledge.meta.generation': 'Generation {n}',
  'knowledge.meta.updated': 'Updated {when}',
  'knowledge.search.label': 'Search knowledge',
  'knowledge.search.placeholder': 'Describe what you are looking for',
  'knowledge.search.submit': 'Search',
  'knowledge.search.searching': 'Searching…',
  'knowledge.search.clear': 'Clear',
  'knowledge.search.hint': 'Finds documents by meaning. This is for looking up documents — a call searches differently (the agent rewrites the caller\'s words into its own queries), so results will not match what a call retrieves.',
  'knowledge.search.off': 'Search is not set up in this environment. You can still browse documents.',
  'knowledge.search.unavailable': 'Search is not available for this knowledge base right now.',
  'knowledge.search.failed': 'Search failed. Please try again.',
  'knowledge.search.score': 'Score',
  'knowledge.search.results': '{n} results',
  'knowledge.search.backToList': 'All documents',
  'knowledge.search.noResults': 'No documents matched.',
  'knowledge.docs.title': 'Documents',
  'knowledge.docs.all': 'All',
  'knowledge.docs.empty': 'This knowledge base has no documents.',
  'knowledge.docs.loadFailed': 'Failed to load documents.',
  'knowledge.docs.colDomain': 'Domain',
  'knowledge.docs.loadMore': 'Load more',
  'knowledge.doc.close': 'Close',
  'knowledge.doc.loadFailed': 'Failed to load this document.',
  'knowledge.doc.aliases': 'Also known as',
  'knowledge.doc.id': 'Document ID',
  'knowledge.doc.copy': 'Copy',
  'knowledge.doc.copied': 'Copied',
  'integrations.eyebrow': 'Organization',
  'integrations.title': 'Integrations',
  'integrations.desc': "See every agent's customer-API integration in one place. Edit settings in each agent's Integrations tab.",
  'integrations.forbidden': 'Only an organization owner or admin can view integrations.',
  'integrations.on': 'On',
  'integrations.off': 'Off',
  'integrations.lookups.title': 'Settings by agent',
  'integrations.lookups.desc': 'Recent-run figures count only the runs loaded below.',
  'integrations.lookups.empty': 'No agent has a real-time context lookup yet. Set one up in the agent’s Integrations tab.',
  'integrations.lookups.loadError': 'Could not load integration settings.',
  'integrations.col.agent': 'Agent',
  'integrations.col.status': 'Status',
  'integrations.col.host': 'Customer host',
  'integrations.col.revision': 'Settings version',
  'integrations.col.lastRun': 'Last run',
  'integrations.col.failures': 'Failed / runs',
  'integrations.runs.title': 'Recent runs across organization',
  'integrations.runs.more': 'Load more',
  'integrations.runs.noMatch': 'No runs match these filters.',
  'integrations.filter.agent': 'Agent',
  'integrations.filter.allAgents': 'All agents',
  'integrations.filter.result': 'Result',
  'integrations.filter.result.all': 'All',
  'integrations.filter.result.success': 'Success',
  'integrations.filter.result.failure': 'Failed',
  'integrations.filter.result.other': 'Other',
  'integrations.tab.phone': 'Phone numbers',
  'integrations.tab.chat': 'Chat',
  'chatAicc.title': 'Chat agents connected to AICC',
  'chatAicc.desc': 'Choose which chat agents send their conversations to AICC. Only connected agents create cases and can hand a visitor over to a human agent — whether the chat was opened in the widget, the chat test, or a share link. Agents you do not connect send nothing to AICC.',
  'chatAicc.notEnabled': 'This organization does not use AICC, so chat agents cannot be connected. Ask our team to enable AICC first.',
  'chatAicc.empty': 'There are no chat agents yet. Create a chat agent first.',
  'chatAicc.col.agent': 'Chat agent',
  'chatAicc.col.status': 'AICC',
  'chatAicc.col.connect': 'Connect',
  'chatAicc.connected': 'Connected',
  'chatAicc.notConnected': 'Not connected',
  'chatAicc.toggleLabel': 'Connect {name} to AICC',
  'chatAicc.note': 'Changes apply immediately, including to chats in progress. Chat tests with a connected agent also show up in AICC, labelled as a chat test.',
  'chatAicc.error.load': 'Could not load chat agents. Please try again.',
  'chatAicc.error.save': 'Could not save the change. Please try again.',
  'chatAicc.error.notEnabled': 'This organization does not use AICC, so the agent cannot be connected.',
  'integrations.tab.sms': 'SMS',
  'sms.loadError': 'Could not load SMS settings.',
  'sms.senders.title': 'Sender numbers',
  'sms.senders.empty': 'No SMS sender number is assigned yet. Ask our team, or connect your NHN account below and add your own.',
  'sms.col.number': 'Number',
  'sms.col.account': 'Source',
  'sms.account.neona': 'Provided by Neona',
  'sms.account.customer': 'My NHN',
  'sms.default': 'Default',
  'sms.default.label': 'Default sender',
  'sms.default.pick': 'Choose a number',
  'sms.default.save': 'Save',
  'sms.default.required': 'Texts are not sent until you choose a default sender.',
  'sms.sender.remove': 'Remove',
  'sms.sender.addLabel': 'Add a number',
  'sms.sender.add': 'Add',
  'sms.nhn.title': 'My NHN account',
  'sms.nhn.desc': 'Texts sent through your own NHN contract are billed by NHN directly.',
  'sms.nhn.appKey': 'App key',
  'sms.nhn.secretKey': 'Secret key',
  'sms.nhn.save': 'Save',
  'sms.nhn.verifiedAt': 'Verified {time}',
  'sms.nhn.change': 'Change keys',
  'sms.nhn.disconnect': 'Disconnect',
  'sms.nhn.disconnectTitle': 'Disconnect your NHN account?',
  'sms.nhn.disconnectBody': 'Your NHN keys and your own numbers ({n}) will be removed. Texts can no longer be sent from those numbers.',
  'sms.nhn.disconnectConfirm': 'Disconnect',
  'sms.error.nhn_account_invalid': 'NHN rejected these keys. Check the app key and secret key.',
  'sms.error.nhn_account_missing': 'Connect your NHN account first.',
  'sms.error.nhn_account_mismatch': 'Some of your sender numbers are not approved in the new NHN account. Remove them first, then change the keys.',
  'sms.error.nhn_unavailable': 'Could not reach NHN. Your keys were not changed. Try again in a moment.',
  'sms.error.sms_sender_not_approved': 'This number is not an approved sender in your NHN account.',
  'sms.error.invalid_number': 'Enter a valid phone number.',
  'sms.error.sms_sender_taken': 'This sender number is already registered.',
  'sms.error.sms_sender_managed': 'Numbers provided by Neona cannot be removed.',
  'sms.error.sms_sender_not_found': 'This number is not one of your organization’s sender numbers.',
  'sms.error.forbidden': 'Only an organization owner or admin can change SMS settings.',
  'sms.error.generic': 'Could not change SMS settings. Try again.',
  'integrations.tab.webhook': 'Webhooks',
  'integrations.webhook.title': 'Webhook',
  'integrations.webhook.desc': 'We notify this URL when a call starts, ends or expires, and when call analysis is ready.',
  'integrations.webhook.events': 'Events',
  'integrations.webhook.signature': 'Each request carries an X-Neona-Signature header (sha256 HMAC made with your signing secret).',
  'integrations.webhook.retry': 'If your endpoint fails with a network error, 5xx, 408 or 429, we retry up to 5 times over about 7 minutes (6 attempts in total).',
  'integrations.webhook.urlLabel': 'Endpoint URL',
  'integrations.webhook.urlHint': 'Public https:// addresses only.',
  'integrations.webhook.save': 'Save',
  'integrations.webhook.saved': 'Saved.',
  'integrations.webhook.secretLabel': 'Signing secret',
  'integrations.webhook.secretSet': 'Set',
  'integrations.webhook.secretNone': 'Not yet — created when you first save a URL',
  'integrations.webhook.secretTitle': 'Signing secret',
  'integrations.webhook.secretOnce': 'Shown only now. Once you close this, you cannot see it again.',
  'integrations.webhook.secretDismiss': 'Close',
  'integrations.webhook.copy': 'Copy',
  'integrations.webhook.copied': 'Copied',
  'integrations.webhook.copyFailed': 'Could not copy. Select the value and copy it yourself.',
  'integrations.webhook.manage': 'Manage',
  'integrations.webhook.rotate': 'Regenerate secret',
  'integrations.webhook.rotateConfirm': 'From now on, webhooks are signed with the new secret. The old secret will no longer verify them.',
  'integrations.webhook.rotateDo': 'Regenerate',
  'integrations.webhook.rotated': 'Created a new signing secret.',
  'integrations.webhook.delete': 'Delete webhook',
  'integrations.webhook.deleteConfirm': 'This removes the URL and its signing secret. Call result notifications stop.',
  'integrations.webhook.deleteDo': 'Delete',
  'integrations.webhook.deleted': 'Webhook deleted.',
  'integrations.webhook.loadFailed': 'Could not load webhook settings.',
  'integrations.webhook.error.aicc': 'Organizations that use AICC cannot change webhook settings here at this time.',
  'integrations.webhook.error.changed': 'Settings changed in the meantime. Check the reloaded values and try again.',
  'integrations.webhook.error.invalidUrl': 'Enter a valid URL that starts with https:// (up to 200 characters).',
  'integrations.webhook.error.notPublic': 'Only public internet addresses are allowed. Check the URL.',
  'integrations.webhook.error.notConfigured': 'Save an endpoint URL first.',
  'integrations.webhook.error.forbidden': 'Only an organization owner or admin can change webhooks.',
  'integrations.webhook.error.generic': 'Something went wrong. Try again in a moment.',
  'phone.preflight.title': 'Setup check',
  'phone.preflight.ready': 'Ready to take calls',
  'phone.preflight.notReady': 'Steps remaining',
  'phone.preflight.done': 'Done',
  'phone.preflight.todo': 'To do',
  'phone.preflight.agent': 'Create a voice agent',
  'phone.preflight.agent.todo': 'You need an active voice agent to answer calls.',
  'phone.preflight.agent.link': 'Create an agent',
  'phone.preflight.number': 'Get a phone number',
  'phone.preflight.number.todo': 'No number is assigned yet. Ask our team to assign one.',
  'phone.preflight.receiving': 'Choose who answers',
  'phone.preflight.receiving.todo': 'These numbers have no agent to answer — pick one below: {numbers}',
  'phone.preflight.cap': 'Call length of the answering agent',
  'phone.preflight.cap.todo': 'While only the AI is on the call, it ends without warning at the agent’s “Max session duration” — set it to {min} minutes or more (once a human agent joins this limit is lifted, and a phone call ends at 4 hours): {numbers}',
  'phone.preflight.line': 'Line registration',
  'phone.preflight.line.todo': 'Line setup is not finished for these numbers — let our team know: {numbers}',
  'phone.preflight.outbound': 'Outbound line',
  'phone.preflight.outbound.todo': 'None of your numbers can place calls yet. Ask our team to enable an outbound line.',
  'phone.preflight.test_call': 'Test call',
  'phone.preflight.test_call.todo': 'No recent connected call on these numbers — call them to confirm: {numbers}',
  'phone.preflight.optional': 'Optional',
  'phone.preflight.unknown': 'Unchecked',
  'phone.preflight.unknown.desc': 'Could not reach AICC to check this.',
  'phone.preflight.operators': 'Agent accounts in AICC',
  'phone.preflight.operators.todo': 'No one has an AICC account yet — open AICC to create your admin account, then invite agents in Settings › Agents.',
  'phone.preflight.operators.now': '{n} available now',
  'phone.preflight.operators.none': 'handoff calls ring with no one to answer, then become callbacks. Set your status to Available in AICC to take them.',
  'phone.preflight.webhook': 'Send results to your system (webhook)',
  'phone.preflight.webhook.optional': 'Only if you integrate — turn it on in AICC Settings › Webhook.',
  'phone.preflight.aiccHint': 'AICC is on — an agent must be signed in to AICC to take a handoff. Agents can also call customers from AICC.',
  'phone.preflight.aiccOpen': 'Open AICC',
  'phone.title': 'Phone numbers',
  'phone.desc': 'Pick which agent answers each number. Standby agents are connected and can take over in one click. Ask our team to assign new numbers.',
  'phone.empty': 'No phone numbers are assigned to this organization yet. Ask our team to assign one.',
  'phone.loadError': 'Could not load phone numbers.',
  'phone.col.number': 'Number',
  'phone.col.receiving': 'Answering agent',
  'phone.col.standby': 'Standby',
  'phone.col.trunk': 'Line',
  'phone.col.lastInbound': 'Last call',
  'phone.col.time': 'Time',
  'phone.col.result': 'Result',
  'phone.col.decidedBy': 'Routed by',
  'phone.col.agent': 'Agent',
  'phone.receivingNone': 'None',
  'phone.receivingElsewhere': 'answering {number}',
  'phone.standbyAdd': '+ Add standby',
  'phone.standbyRemove': 'Remove from standby',
  'phone.standbyPromote': 'Switch to answering',
  'phone.trunk.registered': 'Connected',
  'phone.trunk.unregistered': 'Not connected',
  'phone.trunk.error': 'Error',
  'phone.trunkRecheck': 'Recheck',
  'phone.trunkError.last_number': 'This is the last number on its line, so it was not removed (an empty line accepts every number). Our team will clean it up.',
  'phone.trunkError.trunk_not_found': 'The line for this number was not found. Contact our team.',
  'phone.trunkError.sync_failed': 'Could not sync the line. Recheck in a moment.',
  'phone.trunkError.busy': 'Another change to this line is in progress. Recheck in a moment.',
  'phone.trunkError.other': 'There is a problem with this line. Recheck or contact our team.',
  'phone.aiccPriority': 'AICC routing takes priority',
  'phone.history': 'Recent calls',
  'phone.historyEmpty': 'No calls yet.',
  'phone.result.routed': 'Answered',
  'phone.result.no_agent': 'No agent',
  'phone.result.rejected': 'Refused',
  'phone.result.limited': 'Limit reached',
  'phone.result.error': 'Failed',
  'phone.result.other': 'Other',
  'phone.decided.aicc': 'AICC routing',
  'phone.decided.number': 'Number setting',
  'phone.decided.agent': 'Agent setting',
  'phone.confirm.receiveTitle': 'Switch the answering agent?',
  'phone.confirm.receiveBody': 'From now on, calls to {number} will be answered by {name}.',
  'phone.confirm.keepPrevious': 'Keep {name} as standby',
  'phone.confirm.receive': 'Switch now',
  'phone.confirm.releaseTitle': 'Stop answering this number?',
  'phone.confirm.releaseBody': '{name} will stop answering {number}. Calls to this number will not be answered until you pick an agent.',
  'phone.confirm.release': 'Stop answering',
  'phone.error.number_changed': 'This number was just changed by someone else. Refresh and try again.',
  'phone.error.agent_busy': 'This agent already answers another number. Remove it there first.',
  'phone.error.agent_invalid': 'Only active voice agents in this organization can be connected.',
  'phone.error.already_receiving': 'This agent already answers this number.',
  'phone.error.number_not_found': 'This number is not assigned to your organization.',
  'phone.error.forbidden': 'Only an organization owner or admin can change phone numbers.',
  'phone.error.generic': 'Could not change the phone number. Try again.',
  'agent.phoneTakeTitle': '{name} is answering this number',
  'agent.phoneTakeBody': 'Take {number} over now, or add this agent as standby so you can switch later.',
  'agent.phoneTakeReleaseOld': 'This agent will stop answering {number}.',
  'agent.phoneTake': 'Take over now',
  'agent.phoneStandby': 'Add as standby',
  'agent.phoneTaken': 'This agent now answers {number}.',
  'agent.phoneStandbyAdded': 'Added as standby for {number}.',
  'agent.phoneNumberReadonly': 'Only an organization owner or admin can change the phone number.',
  'callStart.err.self_ref': '{{{name}}} is extracted by this request — use it in another request.',
  'callStart.err.cycle': 'Using {{{name}}} makes requests wait on each other.',
  'deploy.channelWidget': 'Website widget',
  'deploy.badgeOneLine': 'One script tag',
  'deploy.badgeDev': 'For developers',
  'deploy.intro': 'Ways to put this agent in front of users — share a link (no code), embed the widget (one script tag), or integrate via the REST API.',

  // Agent detail
  'agent.totalSessions': 'Total sessions',
  'agent.allTime': 'All time',
  'agent.avgDuration': 'Avg duration',
  'agent.perSession': 'Per session',
  'agent.dropRate': 'Drop rate',
  'agent.last30d': 'Last 30 days',
  'agent.totalMinutes': 'Total minutes',
  'agent.notFound': 'Agent not found.',
  'agent.loadError': 'Unable to load agent — please try again.',
  'agent.updateFailed': 'Update failed',
  'agent.actionFailed': "Couldn't complete that — try again",
  'agent.settings': 'Settings',
  'agent.openSettings': 'Open settings tab',
  'agent.integration': 'Integration',
  'agent.integrationDesc': 'Start a session from your server with a single API call.',
  'agent.viewApiDocs': 'View API docs',
  'agent.manageApiKeys': 'Manage API keys',
  'agent.conversationConfig': 'Conversation config',
  'agent.systemPrompt': 'System prompt',
  'agent.systemPromptHint': 'Defines the agent\'s personality and behavior. Use {{variable}} for runtime substitution.',
  'agent.silenceNudge': 'Speak first when the caller goes quiet',
  'agent.silenceNudgeHint': 'The agent re-opens the conversation after a stretch of silence instead of waiting indefinitely. Useful for tutoring and guided flows; leave it off for agents that hand off to a human, since it also speaks while the caller waits on hold.',
  'agent.silenceNudgeVoiceOnly': 'Voice agents only. Chat agents reply when the visitor sends a message, so this cannot be turned on here.',
  'agent.silenceNudgeDelay': 'Silence before each nudge (sec)',
  'agent.silenceNudgeDelayHint': 'Comma-separated, one entry per attempt — \"3, 5, 7\" nudges after 3s, then 5s, then 7s, then stays quiet (or hangs up if auto hang-up is on). Up to 5 entries, each 1–60s, measured from the end of the last speech (agent or caller). Leave empty to follow the platform default.',
  'agent.silenceNudgeDelayPlaceholder': 'Platform default (e.g. 3, 5, 7)',
  'agent.silenceAutoEnd': 'Hang up when the caller never answers',
  'agent.silenceAutoEndHint': 'After the last nudge goes unanswered, the agent says a closing line and ends the call. Keep it off for agents that hand off to a human.',
  'agent.silenceAutoEndDelay': 'Silence after the last nudge before hanging up (sec)',
  'agent.silenceAutoEndDelayHint': '1–120 seconds, measured from the end of the last nudge. Leave empty to follow the platform default (10s).',
  'agent.silenceAutoEndDelayPlaceholder': 'Platform default (10)',
  'agent.silenceAutoEndMessage': 'Closing line',
  'agent.silenceAutoEndMessageHint': 'Spoken right before the call ends. Leave empty to use the platform default for the agent language.',
  'agent.retentionTitle': 'Call records',
  'agent.retentionHint': 'What this agent keeps after a call ends. All on by default. Turn audio and traces off for public demo agents to match the privacy policy; the transcript alone still powers the demo report.',
  'agent.retentionVoiceOnly': 'Voice agents only. Chat keeps no audio recording or LLM traces, and always stores the conversation text.',
  'agent.retentionAudio': 'Record call audio',
  'agent.retentionAudioHint': 'Saves caller, agent, human-operator (when handed off) and stereo WAV files to storage at the end of the call.',
  'agent.retentionTranscript': 'Keep the transcript',
  'agent.retentionTranscriptHint': 'Stores the conversation text on the session. Off also removes the transcript view, on-demand evaluation and the demo report. Keep it on for agents that hand off to a human — the contact-center case is built from this transcript.',
  'agent.retentionTrace': 'Export LLM traces',
  'agent.retentionTraceHint': 'Sends per-turn LLM, STT and TTS traces (with full text) to the observability tool for latency and quality debugging.',
  'agent.silenceAutoEndMessagePlaceholder': 'e.g. I haven\'t heard back, so I\'ll end the call now.',
  'agent.keypadInput': 'Accept keypad input',
  'agent.keypadInputHint': 'During a phone call, the AI receives the digits the caller presses and uses them in the conversation. Input ends when the caller presses pound (#), fills the max digits, or 4 seconds pass after the last key. Pressing star (*) starts over.',
  'agent.keypadMaxDigits': 'Max digits',
  'agent.keypadMaxDigitsHint': 'Most digits taken in one entry (1–20). Leave empty to accept up to 20 digits.',
  'agent.keypadMaxDigitsPlaceholder': 'Platform default (20)',
  'agent.keypadInputWarning': 'Don\'t collect sensitive numbers such as national ID numbers, card numbers or passwords by keypad. The digits pressed stay as typed in the call record (transcript) and the human hand-off summary.',
  'agent.voiceTts': 'Voice / TTS',
  'agent.noVoice': 'No voice selected',
  'agent.changeVoice': 'Change voice',
  'agent.voiceSpeed': 'Speaking speed',
  'agent.voiceSpeedHint': 'Overall playback speed of the agent voice. 0.5–2.0, default 1.0.',
  'agent.voiceLoudness': 'Output loudness',
  'agent.voiceLoudnessHint': 'Target loudness in LUFS (−70 to 0). Higher is louder. Off means no normalization — the voice plays at its original level.',
  'agent.backgroundAudio': 'Background ambience',
  'agent.backgroundAudioOff': 'Off',
  'agent.backgroundAudioLoading': 'Loading clips…',
  'agent.backgroundAudioInactive': 'inactive',
  'agent.backgroundAudioPreview': 'Preview background ambience',
  'agent.backgroundAudioVolume': 'Background ambience volume',
  'agent.backgroundAudioHint': 'Plays a soft ambient track under the call (e.g. office chatter) so silence doesn\'t feel like a dropped line. Over 100% amplifies the clip (useful on narrowband phone calls); very high values may distort.',
  'agent.backgroundAudioDucking': 'Soften while the agent speaks (ducking)',
  'agent.backgroundAudioDuckingHint': 'Ducking gently lowers the ambience under the agent\'s voice and restores it between turns.',
  'agent.chooseVoice': 'Choose voice',
  'agent.general': 'General',
  'agent.name': 'Name',
  'agent.description': 'Description',
  'agent.descPlaceholder': 'What does this agent do?',
  'agent.language': 'Language',
  'agent.phoneNumber': 'Inbound phone number',
  'agent.phoneNumberNone': 'Not connected',
  'agent.phoneNumberInUse': 'in use by {name}',
  'agent.phoneNumberEmpty': 'No numbers allocated to your organization — contact us to request one.',
  'agent.phoneNumberDetached': 'no longer allocated to your organization',
  'agent.agentType': 'Agent type',
  'agent.maxSessionDuration': 'Max session duration (min)',
  'agent.maxSessionDurationHint': 'Hard cap per session. While only the AI is on the call, the call ends without warning at this limit (once a human agent joins this limit is lifted, and a phone call ends at 4 hours). 1–240 (4h). Default 10.',
  'agent.sessionExpiry': 'Session expiry (min)',
  'agent.sessionExpiryHint': 'How long a created-but-unconnected session and its join token stay valid before expiring. 1–30 min. Default 5.',
  'agent.chatMaxTurns': 'Messages per chat (limit)',
  'agent.chatMaxTurnsHint': 'How many visitor messages the AI answers in one chat. Past this, the input locks. 1–200. Leave blank for the default (30). Messages sent while a human agent is connecting or chatting are not counted.',
  'agent.chatContextMessages': 'Messages the AI remembers',
  'agent.chatContextMessagesHint': 'How many recent messages are sent to the AI each turn. Older ones are forgotten. 1–200. Leave blank for the default (40). Higher values cost more per reply.',
  'agent.agentId': 'Agent ID',
  'agent.agentIdHint': 'Use this identifier in API calls to reference this agent.',
  'agent.dangerZone': 'Danger zone',
  'agent.dangerDesc': 'Permanently deactivate this agent. Active sessions will be terminated.',
  'agent.deactivateAgent': 'Deactivate agent',
  'agent.areYouSure': 'Are you sure?',
  'agent.confirmDeactivate': 'Confirm deactivate',

  // Version history
  'version.justNow': 'just now',
  'version.minutesAgo': '{n}m ago',
  'version.hoursAgo': '{n}h ago',
  'version.daysAgo': '{n}d ago',
  'version.history': 'Version history',
  'version.count': 'versions',
  'version.loading': 'Loading versions…',
  'version.empty': 'No version history yet. Versions are created automatically when you save changes.',
  'version.label': 'Version',
  'version.current': 'current',
  'version.deleteConfirm': 'Delete this version?',

  // UI common
  'ui.copy': 'Copy',
  'ui.copied': 'Copied!',

  // Command palette
  'cmd.goTo': 'Go to',
  'cmd.create': 'Create',
  'cmd.searchPlaceholder': 'Type a command or search…',
  'cmd.noResults': 'No results',

  // Language options
  'lang.koKR': 'Korean (ko-KR)',
  'lang.enUS': 'English (en-US)',
  'lang.enGB': 'English (en-GB)',
  'lang.jaJP': 'Japanese (ja-JP)',

  // New agent extras
  'newAgent.createFailed': 'Create failed',
  'newAgent.namePlaceholder': 'e.g. English Tutor',
  'newAgent.reviewName': 'Name',
  'newAgent.reviewType': 'Type',
  'newAgent.reviewLang': 'Language',
  'newAgent.reviewVoice': 'Voice',
  'newAgent.untitled': 'Untitled agent',
  'newAgent.ready': 'ready',
  'newAgent.previewAgent': 'Agent',
  'newAgent.previewCaller': 'Caller',
  'newAgent.previewGreeting': 'Hi! How can I help you today?',

  // Agents page
  'agents.sort': 'Sort',
  'agents.sortUpdated': 'Last updated',
  'agents.sortName': 'A–Z',
  'agents.loadError': 'Failed to load agents. Please try again.',
  'agents.createNew': 'Create manually',
  'agents.autoGenerate': 'AI auto-generate (Beta)',
  'agents.createAnother': 'Create another agent',
  'agents.filterAll': 'All',
  'agents.filterPublished': 'Published',
  'agents.filterDraft': 'Draft',
  'agents.publishedLive': '{n} live',
  'agents.colAgent': 'Agent',
  'agents.colId': 'ID',
  'agents.colLanguage': 'Language',
  'agents.colCreated': 'Created',

  // Sessions page
  'sessions.colAgent': 'Agent',
  'sessions.colParticipant': 'Participant',
  'sessions.colStatus': 'Status',
  'sessions.colDuration': 'Duration',
  'sessions.colTime': 'Time',
  'sessions.qualityScore': 'Quality score',
  'sessions.loadMore': 'Load more',
  'sessions.channelVoice': 'Voice',
  'sessions.filterAll': 'All',
  'sessions.statusCreated': 'Created',
  'sessions.statusConnected': 'Connected',
  'sessions.statusActive': 'Active',
  'sessions.statusEnded': 'Ended',
  'sessions.statusExpired': 'Expired',
  'sessions.statusFailed': 'Failed',
  'sessions.channelChat': 'Chat',
  'sessions.colSource': 'Source',
  'sessions.colTurns': 'Turns',
  'sessions.chatSourceWidget': 'Widget',
  'sessions.chatSourceShare': 'Share link',
  'sessions.chatSourceConsole': 'Test',
  'sessions.chatHandoff': 'Operator joined',
  'sessions.chatHandoffRequested': 'Operator requested',
  'sessions.chatCallback': 'Callback requested',
  'sessions.noChatSessions': 'No chat sessions yet.',
  'sessions.chatLoadFailed': 'Failed to load chat sessions.',
  'chatDetail.eyebrow': 'Chat session',
  'chatDetail.back': 'Back to sessions',
  'chatDetail.loadFailed': 'Failed to load this chat session.',
  'chatDetail.empty': 'No messages in this session.',
  'chatDetail.turns': 'turns',
  'chatDetail.statusActive': 'Active',
  'chatDetail.statusEnded': 'Ended',
  'chatDetail.contact': 'Contact',
  'chatDetail.roleCustomer': 'Visitor',
  'chatDetail.roleAgent': 'Agent',
  'chatDetail.roleOperator': 'Operator',
  'chatDetail.endUserRequest': 'Ended by visitor',
  'chatDetail.endIdle': 'Ended by inactivity',
  'chatDetail.endTurnLimit': 'Turn limit reached',
  'chatDetail.endOperator': 'Ended by operator',
  'chatDetail.endUnknown': 'Ended',
  'sessions.callType': 'Call topic (from the call record)',
  'sessions.loadError': 'Failed to load sessions.',

  // Session summary
  'sessionSummary.status': 'Status',
  'sessionSummary.duration': 'Duration',
  'sessionSummary.started': 'Started',
  'sessionSummary.ended': 'Ended',
  'sessionSummary.participant': 'Participant',
  'sessionSummary.sessionId': 'Session ID',
  'sessionSummary.transcript': 'Transcript',
  'sessionSummary.updating': '(updating…)',
  'sessionSummary.noTranscript': 'No transcript available.',
  'sessionSummary.startAgain': 'Start again',
  'sessionSummary.loading': 'Loading…',
  'sessionSummary.loadError': 'Failed to load session.',
  'agent.saved': 'Saved ✓',
  'agent.evalCriteria': 'Evaluation criteria',
  'agent.evalTabDesc': 'Settings for the post-call quality evaluation. These do not affect the live conversation.',
  'agent.evalDefaultTitle': 'Default rubric (used when criteria is empty)',
  'agent.evalDefault1': 'Goal achievement (0-100): did the call achieve the goals the system prompt defines — judged by outcome, not effort. Unresolved/frustrated endings score low even if every rule was followed.',
  'agent.evalDefault2': 'Accuracy & policy (0-100): was the guidance factually correct and on-policy versus the system prompt.',
  'agent.evalDefault3': 'Strengths and guidance issues with quotes and severity (low/medium/high), plus a goal-gap flag (prompt coverage vs agent execution).',
  'agent.evalDefault4': 'Overall (0-100): reflects both axes and is capped at 60 when the goal went unmet.',

  'agent.evalCriteriaPlaceholder': 'e.g. The goal of this agent is completing a reservation. Score goal achievement below 50 when no reservation was made. Rate pricing misinformation as high severity.',
  'agent.evalCriteriaHint': 'Agent-specific criteria for the post-call quality evaluation. Leave empty to use the platform default rubric.',
  'sessionSummary.info': 'Session info',
  'sessionSummary.liveContext': 'Live context',
  'sessionSummary.liveContextStatus': 'Lookup result',
  'sessionSummary.liveContextFetched': 'Fetched at',
  'sessionSummary.liveContextItems': 'Passed to the AI agent',
  'sessionSummary.liveContextNotFound': 'The AI agent was told the lookup found no data.',
  'sessionSummary.liveContextEmpty': 'Nothing was passed to the AI agent.',
  'sessionSummary.extraction': 'Call record',
  'sessionSummary.extracting': 'Creating call record… this appears shortly after the call ends.',
  'sessionSummary.noExtraction': 'No call record for this session — it was not created or did not complete.',
  'sessionSummary.keyPoints': 'Key points',
  'sessionSummary.extractedBy': 'Extracted by',
  'sessionSummary.res.resolved': 'Resolved',
  'sessionSummary.res.unresolved': 'Unresolved',
  'sessionSummary.res.escalated': 'Escalated',
  'sessionSummary.res.abandoned': 'Abandoned',
  'sessionSummary.sent.positive': 'Positive',
  'sessionSummary.sent.neutral': 'Neutral',
  'sessionSummary.sent.negative': 'Negative',
  'sessionSummary.evaluation': 'Quality evaluation',
  'sessionSummary.noEvaluation': 'No quality evaluation for this session — it was not run or did not complete.',
  'sessionSummary.evaluating': 'Evaluating… this appears shortly after the call ends.',
  'sessionSummary.evaluatingNow': 'Running evaluation… this can take up to a minute.',
  'sessionSummary.runEvaluation': 'Run evaluation',
  'sessionSummary.reEvaluate': 'Re-evaluate',
  'sessionSummary.evaluateFailed': 'Evaluation failed — you can try again.',
  'sessionSummary.overallScore': 'Overall',
  'sessionSummary.goalAchievement': 'Goal achievement',
  'sessionSummary.gap.prompt_coverage': 'Gap: prompt coverage',
  'sessionSummary.gap.agent_execution': 'Gap: agent execution',
  'sessionSummary.accuracy': 'Accuracy & policy',
  'sessionSummary.strengths': 'Strengths',
  'sessionSummary.good': 'Good',
  'sessionSummary.guidanceIssues': 'Guidance issues',
  'sessionSummary.evaluatedBy': 'Evaluated by',
  'sessionSummary.criteria': 'Evaluation items',
  'sessionSummary.criteriaFailed': 'Not judged',
  'sessionSummary.criteriaPending': 'Judging evaluation items… this appears shortly after the call ends.',
  'sessionSummary.criteriaNone': 'No evaluation item results for this session — judging did not run or did not complete.',
  'sessionSummary.criteriaFailedReason': 'Evaluator did not return a result',
  'sessionSummary.criteriaYes': 'Yes',
  'sessionSummary.criteriaNo': 'No',
  'sessionSummary.criteriaUnknown': "Can't tell",
  'sessionSummary.criteriaStandard': "Default",
  'evaluation.loading': "Loading evaluation settings…",
  'evaluation.loadError': "Couldn't load evaluation settings.",
  'evaluation.standardsUnavailable': "The evaluation service didn't respond, so standard evaluations can't be shown right now.",
  'evaluation.standardBadge': "Default",
  'evaluation.copyStandard': "Duplicate",
  'evaluation.copyName': "{name} copy",
  'evaluation.languageLabel': "Explanation language",
  'evaluation.languageHint': "The language of the explanation shown with each result. It doesn't change how the call is judged.",
  'evaluation.language.auto': "Same as the call",
  'evaluation.language.ko-KR': "Korean",
  'evaluation.language.en-US': "English",
  'evaluation.language.ja-JP': "Japanese",
  'evaluation.standard.success': "Success",
  'evaluation.standard.success.desc': "Whether the customer's request was fully handled in this call",
  'evaluation.standard.task_completion': "Task completion",
  'evaluation.standard.task_completion.desc': "Whether the customer's request was fully handled in this call",
  'evaluation.standard.user_sentiment': "Customer sentiment",
  'evaluation.standard.user_sentiment.desc': "How the customer felt by the end of the call",
  'evaluation.value.resolved': "Resolved",
  'evaluation.value.unresolved': "Unresolved",
  'evaluation.value.positive': "Positive",
  'evaluation.value.neutral': "Neutral",
  'evaluation.value.negative': "Negative",
  'evaluation.value.unknown': "Can't tell",
  'evaluation.itemsEmpty': "No evaluation items attached yet.",
  'evaluation.type.categorical': "Choice",
  'evaluation.type.boolean': "Yes / No",
  'evaluation.type.numeric': "Score (0–100)",
  'evaluation.edit': "Edit",
  'evaluation.detach': "Remove",
  'evaluation.adminItem': "Set by Neona",
  'evaluation.standardViewNote': "Every agent has this evaluation, so it can't be removed. You can edit the criteria for this agent; the result is always Yes or No.",
  'evaluation.resetInstruction': "Restore default",
  'evaluation.viewNamed': "View {name}",
  'evaluation.languageOption': "Reasoning: {language}",
  'evaluation.view': "View",
  'evaluation.viewTitle': "Evaluation",
  'evaluation.addEmpty': "No evaluations yet — create one above.",
  'evaluation.codeEditNote': "This item is calculated automatically from the call record — you can only change its name.",
  'evaluation.search': "Search by name or criteria",
  'evaluation.searchEmpty': "No evaluation matches.",
  'evaluation.addOne': "Add",
  'evaluation.addNamed': "Add {name}",
  'evaluation.added': "Added ✓",
  'evaluation.removeOne': "Remove",
  'evaluation.removeNamed': "Remove {name}",
  'evaluation.createSection': "Start new",
  'evaluation.createNew': "New evaluation",
  'evaluation.createNewDesc': "Write it yourself or load a template.",
  'evaluation.loadTemplate': "Load template",
  'evaluation.templateApplied': "Filled in from ‘{name}’.",
  'evaluation.undo': "Undo",
  'evaluation.codeDetail': "Calculated automatically from the call record.",
  'evaluation.notAttached': "This evaluation isn't on this agent yet.",
  'evaluation.detached': "Removed ‘{name}’ from this agent.",
  'evaluation.copied': "Added the copy. The original ‘{name}’ is still on this agent, so both will run.",
  'evaluation.detachOriginal': "Remove original",
  'evaluation.existingSection': "Add an existing evaluation",
  'evaluation.value.true': "Yes",
  'evaluation.moreValues': "+{n} more",
  'evaluation.value.false': "No",
  'evaluation.title': "Post-call evaluation",
  'evaluation.desc': "When a call ends, it's judged on the items below.",
  'evaluation.saving': "Saving…",
  'evaluation.more': "More for {name}",
  'evaluation.add': "+ Add evaluation",
  'evaluation.addTitle': "Add evaluation",
  'evaluation.codeInstruction': "Calculated automatically from the call record ({template}).",
  'evaluation.kind.code': "Calculated automatically",
  'evaluation.saved': "Saved",
  'evaluation.readOnly': "Only organization owners and admins can change evaluation settings.",
  'evaluation.error.forbidden': "Only organization owners and admins can change evaluation settings.",
  'evaluation.error.unavailable': "The evaluation service didn't respond. Try again shortly.",
  'evaluation.error.generic': "Couldn't save. Try again.",
  'evaluation.deleteTitle': "Delete evaluation item?",
  'evaluation.deleteDesc': "“{name}” will be removed from every agent it's attached to. Past results stay.",
  'evaluation.delete': "Delete",
  'evaluation.editTitle': "Edit evaluation",
  'evaluation.newTitle': "New evaluation",
  'evaluation.editSharedNote': "Changes apply to every agent this item is attached to, from the next call.",
  'evaluation.name': "Name",
  'evaluation.instruction': "Criteria",
  'evaluation.instructionHint.boolean': "Say when the answer is ‘Yes’. The judge reads the whole call and the agent's system prompt.",
  'evaluation.instructionHint.categorical': "Say when to pick each choice. The judge reads the whole call and the agent's system prompt.",
  'evaluation.instructionHint.numeric': "Say what makes a call score high. The judge reads the whole call and the agent's system prompt.",
  'evaluationPreview.run': "Test",
  'evaluationPreview.rerun': "Test again",
  'evaluationPreview.judging': "Judging…",
  'evaluationPreview.colResult': "Result",
  'evaluationPreview.intro': "Judge one of this agent's recent calls with these criteria. Nothing is saved.",
  'evaluationPreview.timeout': "Judging is taking too long. Test again.",
  'evaluationPreview.needInstruction': "Write the criteria to test them.",
  'evaluationPreview.needCategories': "Add at least two choices to test them.",
  'evaluationPreview.failed': "Failed",
  'evaluationPreview.title': "Test on a recent call",
  'evaluationPreview.call': "Call",
  'evaluationPreview.noCalls': "No finished calls yet. Make a test call first.",
  'evaluationPreview.stale': "The criteria or call changed. The result below is from before.",
  'evaluationPreview.transcript': "Call transcript",
  'evaluationPreview.role.user': "Customer",
  'evaluationPreview.role.agent': "AI",
  'evaluationPreview.role.operator': "Operator",
  'evaluationPreview.noUserText': "(No customer speech)",
  'evaluationPreview.openCall': "Open call",
  'evaluationPreview.note': "Borderline calls can come out differently each time they're judged.",
  'evaluationPreview.saveNote': "Saved criteria apply from the next call.",
  'evaluation.typeLabel': "Result type",
  'evaluation.typeLocked': "The result type can't be changed after creation — make a new item instead.",
  'evaluation.categoriesChanged': "{names} will no longer be a choice. Past results keep the old choice, so they won't be counted together with the new ones.",
  'evaluation.categories': "Choices",
  'evaluation.categoryN': "Choice {n}",
  'evaluation.addCategory': "+ Add choice",
  'evaluation.categoriesTooFew': "Add at least two choices.",
  'evaluation.categoriesDuplicate': "{name} appears more than once.",
  'evaluation.unknownLabel': "Allow “Can't tell” when the call doesn't have enough information",
  'evaluation.createAndAttach': "Create and add",
  'sessionSummary.sev.high': 'High',
  'sessionSummary.sev.medium': 'Medium',
  'sessionSummary.sev.low': 'Low',

  // Early access
  'earlyAccess.revoking': 'Revoking your invite…',
  'earlyAccess.revoked': 'Your invite has been revoked.',
  'earlyAccess.revokeError': 'Something went wrong. The link may have expired.',

  // Session page
  'session.liveConversation': 'Live conversation',
  'session.startError': 'Failed to start session. Try again.',
  'session.agentSpeaking': 'Agent speaking',
  'session.listening': 'Listening…',
  'session.connecting': 'Connecting…',
  'session.avatarConnecting': 'Connecting avatar…',
  'session.mute': 'Mute',
  'session.unmute': 'Unmute',
  'session.endCall': 'End call',
  'session.backToAgent': '← Back to agent',
  'session.setupTitle': 'Before you start',
  'session.outboundSetupTitle': 'Place an outbound call',
  'session.destinationLabel': 'Destination number',
  'session.destinationHint': 'E.164 format, e.g. +821012345678',
  'session.callerIdLabel': 'Caller ID',
  'session.callerIdHint': 'Number shown to the callee — one of your organization’s numbers with an outbound line',
  'session.callerIdPlaceholder': 'Select a number…',
  'session.noOutboundLine': 'No outbound line yet — ask our team to set one up.',
  'session.callerLoadError': 'Could not load your numbers. Reload and try again.',
  'session.invalidPhone': 'Enter a valid E.164 number, e.g. +821012345678',
  'session.dynamicVarsLabel': 'Dynamic variables',
  'session.dynamicVarsHint': 'Values that replace {{key}} placeholders in this agent’s system prompt for this call only.',
  'session.dynamicVarsDetected': 'Pre-filled from {{…}} placeholders found in the prompt.',
  'session.noDynamicVars': 'This agent’s prompt has no {{placeholders}}. Add a key below to send a custom value, or just start the call.',
  'session.varKeyPlaceholder': 'key (e.g. user_name)',
  'session.varValuePlaceholder': 'value (e.g. Alice)',
  'session.addVar': '+ Add variable',
  'session.removeVar': 'Remove variable',
  'session.invalidVarKey': 'Letters, numbers, and underscores only.',
  'session.duplicateVarKey': 'Duplicate key — the last value will be used.',
  'session.loadingAgent': 'Loading agent…',

  // Agent auto-generate
  'autoGen.breadcrumb': 'AI auto-generate',
  'autoGen.title': 'Auto-generate agent',
  'autoGen.desc': 'Describe in one paragraph and AI will create a voice agent for you.',
  'autoGen.createManually': 'Create manually',
  'autoGen.inputLabel': 'What kind of conversation should this agent handle?',
  'autoGen.inputPlaceholder': 'e.g. A scenario where learners practice English for an airport immigration check. The agent acts as an immigration officer asking questions, and gives feedback on the learner\'s responses.',
  'autoGen.inputHint': 'Describe the scenario — who calls, why, and what the agent should do.',
  'autoGen.submit': 'Generate agent',
  'autoGen.examples': 'Example templates',
  'autoGen.generating': 'Creating your agent…',
  'autoGen.generatingHint': 'AI is generating the persona, voice, and system prompt. This takes about 10–20 seconds.',
  'autoGen.result': 'Generation result',
  'autoGen.fieldName': 'Name',
  'autoGen.fieldRole': 'Role',
  'autoGen.fieldGreeting': 'Greeting',
  'autoGen.fieldTone': 'Tone',
  'autoGen.fieldUseCase': 'Use case',
  'autoGen.fieldSampleQueries': 'Sample queries',
  'autoGen.recommendedVoice': 'Recommended voice',
  'autoGen.noVoiceMatch': 'No matching voice found. You can select one manually on the agent detail page.',
  'autoGen.voiceUpdating': 'Updating voice…',
  'autoGen.stop': 'Stop',
  'autoGen.publish': 'View agent',
  'autoGen.reset': 'Start over',
  'autoGen.generateError': 'Generation failed. Please try again.',
  'autoGen.agentLanguage': 'Agent language',
  'autoGen.agentLanguageHint': 'Choose the language your agent will speak.',
  'autoGen.exEnglish': 'English learning',
  'autoGen.exEnglishText': 'A scenario for practicing English at an airport immigration checkpoint. The agent plays an immigration officer asking questions like "What is the purpose of your visit?" and "How long will you stay?" The learner answers in English and receives feedback — encouraging even when mistakes are made, and correcting toward natural expressions.',
  'autoGen.exFaq': 'FAQ support',
  'autoGen.exFaqText': 'A scenario where a customer calls an online store asking about delivery status, refunds, or exchanges. The agent verifies the order number and provides guidance. Delivery takes 2–3 days, refunds within 7 days, exchanges are free.',
  'autoGen.exIntro': 'Self intro',
  'autoGen.exIntroText': 'A scenario where the agent introduces a vocal coach on their behalf. When callers ask about lessons, pricing, or how to book, the agent answers. Specializes in vocal projection and mix voice technique, regular lessons at $60/session, first consultation free.',
  'autoGen.female': 'Female',
  'autoGen.male': 'Male',
  'autoGen.youngAdult': '20s–30s',
  'autoGen.middleAge': '30s–40s',
  'autoGen.senior': '50s+',
  'autoGen.remaining': 'remaining',
  'autoGen.quotaExhausted': 'Auto-generation limit reached. Contact your administrator.',
  'autoGen.preview': 'Preview',
  'autoGen.pickOtherVoice': 'Choose a different voice',
  'autoGen.voiceChangeAnytime': 'You can change the voice anytime in agent settings.',
  'autoGen.fieldSystemPrompt': 'System Prompt',
  'autoGen.chars': 'chars',
  'autoGen.publishError': 'Failed to activate agent. Please try again.',
  'autoGen.urlLabel': 'Reference URL (optional)',
  'autoGen.urlHint': 'Enter a homepage or FAQ page URL to use its content as reference.',
  'autoGen.edit': 'Edit',
  'autoGen.save': 'Save',
  'autoGen.cancel': 'Cancel',
  'autoGen.pickTemplate': 'What kind of agent would you like to create?',
  'autoGen.tplFaq': 'FAQ / Support',
  'autoGen.tplAppointment': 'Appointment',
  'autoGen.tplIntro': 'Self introduction',
  'autoGen.tplEducation': 'Learning coach',
  'autoGen.tplCustom': 'Custom',
  'autoGen.qIndustry': 'What industry?',
  'autoGen.qIndustryOpts': 'IT / SaaS,Shopping mall,Cafe / Restaurant,Real estate,Education,Beauty / Salon',
  'autoGen.qTopics': 'What topics will it answer?',
  'autoGen.qTopicsOpts': 'Shipping / Returns,Price / Payment,Hours / Location,Service info,Account / Membership,Reservation / Cancellation',
  'autoGen.qTone': 'What tone should it use?',
  'autoGen.qToneOpts': 'Warm and friendly,Professional and trustworthy,Bright and energetic,Calm and polite',
  'autoGen.qBusinessType': 'What type of business?',
  'autoGen.qBusinessTypeOpts': 'Hair / Nail salon,Hospital / Dental,Restaurant,Hotel / Pension,Studio / Photography,Academy / PT',
  'autoGen.qInfo': 'What info should it provide?',
  'autoGen.qInfoOpts': 'Hours / Holidays,Menu / Services,Pricing,Location / Parking,Staff selection,Precautions',
  'autoGen.qWho': 'Who are you introducing?',
  'autoGen.qWhoOpts': 'Freelancer / Creator,Coach / Instructor,Artist / Musician,Entrepreneur,Expert / Consultant',
  'autoGen.qHighlight': 'What should it highlight?',
  'autoGen.qHighlightOpts': 'Bio / Career,Expertise,Portfolio / Works,Pricing / Booking,Contact / SNS',
  'autoGen.qIntroTone': 'What tone for the introduction?',
  'autoGen.qIntroToneOpts': 'Professional and composed,Warm and friendly,Confident and energetic,Humorous',
  'autoGen.qSubject': 'What subject or topic?',
  'autoGen.qSubjectOpts': 'English conversation,Korean,Japanese,Math,Coding,Business skills',
  'autoGen.qAudience': 'Who is the learner?',
  'autoGen.qAudienceOpts': 'Elementary school,Middle / High school,College student,Working professional,Senior,Foreigner',
  'autoGen.qStyle': 'Teaching style?',
  'autoGen.qStyleOpts': 'Conversation practice (roleplay),Quiz / Problem solving,Lecture-style explanation,Encouragement-focused',
  'autoGen.confirmTitle': 'Review & create',
  'autoGen.confirmLang': 'Language',
  'autoGen.confirmComment': 'How should the agent respond?',
  'autoGen.confirmCommentPh': 'e.g. Always answer in 2 sentences or less, speak casually, mention our free trial',
  'autoGen.confirmCommentHint': 'The more specific you are, the better the agent performs.',
  'autoGen.confirmUrlLabel': 'Add your website for a smarter agent',
  'autoGen.confirmUrlHint': 'The agent will learn your business info, hours, pricing, and FAQ from the page.',
  'autoGen.confirmCreate': 'Create agent',
  'autoGen.confirmBack': '← Back',
  'autoGen.templateBack': '← Choose template',
  'autoGen.prevStep': '← Previous',
  'autoGen.next': 'Next',
  'autoGen.selected': 'Selected',
  'autoGen.customInput': 'Type your own',
  'autoGen.customConfirm': 'OK',
  'autoGen.addUrl': '+ Add URL',
  'autoGen.answerRequired': 'Please select at least one option.',

  // Widget embed
  'widget.title': 'Widget embed',
  'widget.desc': 'Add a single script tag to your website to show a floating button. Click it to start a call with this agent.',
  'widget.themeColor': 'Theme color',
  'widget.colorHint': 'Use a hex color like #FE7E43.',
  'widget.position': 'Position',
  'widget.posBottomRight': 'Bottom right',
  'widget.posBottomLeft': 'Bottom left',
  'widget.posCenter': 'Bottom center',
  'widget.cornerRadius': 'Corner radius',
  'widget.radiusSharp': 'Sharp',
  'widget.radiusSoft': 'Soft',
  'widget.radiusRound': 'Round',
  'widget.radiusPill': 'Pill',
  'widget.showAgentName': 'Show agent name',
  'widget.muteButton': 'Mute button',
  'widget.liveTranscript': 'Live transcript',
  'widget.postCallRating': 'Post-call rating',
  'widget.whitelabel': 'Whitelabel',
  'widget.brandName': 'Brand name',
  'widget.brandNamePlaceholder': 'Custom brand name (empty = agent name)',
  'widget.logoUrl': 'Logo URL',
  'widget.logoUrlPlaceholder': 'https://... (empty = default icon)',
  'widget.hidePoweredBy': 'Hide \'Powered by Neona\' attribution',
  'widget.saveSettings': 'Save widget settings',
  'widget.embedCode': 'Embed code',
  'widget.widgetKey': 'Widget key',
  'widget.noWidgetKey': 'Issue a Widget key on the {link} page first (register your site domain as an allowed domain).',
  'widget.apiKeysLink': 'API Keys',
  'widget.allowedDomains': 'Allowed domains for this key: {domains}',
  'widget.noDomains': '(none)',
  'widget.copyCode': 'Copy code',
  'widget.replaceKeyHint': 'Replace nsk_wgt_YOUR_KEY_HERE with the actual key you were given. Keys are shown only once at creation.',
  'widget.preview': 'Preview',
  'widget.readyToTalk': 'Ready to talk.',

  // Graph inspector
  'inspector.start': 'Start',
  'inspector.routerModel': 'Router Model',
  'inspector.routerModelHint': 'LLM that decides which agent handles each turn',
  'inspector.defaultVoice': 'Default Voice',
  'inspector.defaultVoiceHint': 'All agents inherit this voice unless overridden individually',
  'inspector.routingRules': 'Routing Rules',
  'inspector.addRule': '+ Add rule',
  'inspector.rulePlaceholder': 'When user asks for...',
  'inspector.outputMode': 'Output Mode',
  'inspector.fullHistory': 'Full History',
  'inspector.lastMessage': 'Last Message',
  'inspector.fullHistoryHint': 'Agents receive all conversation messages',
  'inspector.lastMessageHint': 'Agents receive only the last message',
  'inspector.agent': 'Agent',
  'inspector.name': 'Name',
  'inspector.nameHint': 'Lowercase, alphanumeric, underscores. Must start with a letter.',
  'inspector.namePlaceholder': 'lowercase_name',
  'inspector.model': 'Model',
  'inspector.systemPrompt': 'System Prompt',
  'inspector.promptPlaceholder': 'Describe this agent\'s role and behavior...',
  'inspector.tools': 'Tools',
  'inspector.toolsHint': 'Let this agent call tools during the conversation. Requires the matching deployment key to be set.',
  'tools.webSearch.desc': 'Web search (Brave/Tavily)',
  'tools.readOnly': 'read-only',
  'tools.requiresKey': 'Requires deployment key',
  'tools.whenToUsePlaceholder': 'When should this agent use it? e.g. "Search for prices, weather, news — don\'t say you don\'t know."',
  'inspector.voice': 'Voice',
  'inspector.voiceHint': 'Override the default voice for this agent (leave empty to inherit)',
  'inspector.voiceInherit': 'Default (inherit)',
  'inspector.selectVoice': 'Select voice...',
  'inspector.confirm': 'Confirm',
  'inspector.removeAgent': 'Remove Agent',
  'inspector.handler': 'Handler',
  'inspector.selectHandler': 'Select handler...',
  'inspector.maxAttempts': 'Max Attempts',
  'inspector.vocabItems': 'Vocabulary Items',
  'inspector.wordPlaceholder': 'Word (EN)',
  'inspector.meaningPlaceholder': 'Meaning (KO)',
  'inspector.exprItems': 'Expression Items',
  'inspector.exprEnPlaceholder': 'English',
  'inspector.exprKoPlaceholder': 'Korean',
  'inspector.configJson': 'Config (JSON)',
  'inspector.invalidJson': 'Invalid JSON',

  // ---------------------------------------------------------------------------
  // Landing page (lp.*) — current marketing landing. Rich values may contain a
  // small whitelist of trusted inline HTML (<b>, <br>, <span class="coral">,
  // <span class="cursor"></span>, <span class="news-tag">) rendered via the
  // LandingPage `rich()` helper. Content is static/trusted (no user input).
  // *Tags values are comma-separated lists split at render time.
  // ---------------------------------------------------------------------------
  'lp.nav.manifesto': 'Manifesto',
  'lp.nav.useCases': 'Use Cases',
  'lp.nav.how': 'How it works',
  'lp.nav.voices': 'Voices',
  'lp.nav.signin': 'Sign in',
  'lp.nav.early': 'Early Access',
  'lp.nav.switchTo': 'Switch to Korean',

  'lp.hero.eyebrow': '<b>EARLY ACCESS</b> · NOW OPEN · 2026',
  'lp.hero.title': 'Content that<br /><span class="coral">talks back.</span><span class="cursor"></span>',
  'lp.hero.lead': 'The era of one-way content is over. Build <b>voice agents</b> that talk with your learners, engage your audience, and counsel your customers — agents that <b>tell stories</b>, not just play them.',
  'lp.hero.emailPlaceholder': 'work@yourcompany.com',
  'lp.hero.meta1': 'STT · LLM · TTS in one pipeline',
  'lp.hero.meta2': '700+ character voices',
  'lp.hero.meta3': 'No-code',
  'lp.hero.cardTag': 'LIVE · Neona Agent',
  'lp.hero.cardName': 'Mia',
  'lp.hero.cardRole': 'English Tutor',
  'lp.hero.cardPersona': 'Persona · Friendly, Encouraging',
  'lp.hero.cardTranscript': "Hey! Let's go over what we'll learn today. Ready to try ordering a coffee in English?",

  'lp.cta.request': 'Request Early Access',

  'lp.trust.label': "Powered by <b>Neosapience</b>'s voice AI — also the makers of <b>Typecast</b>",
  'lp.trust.stat1': 'Active users',
  'lp.trust.stat2': 'Countries',
  'lp.trust.stat3': 'Character voices',
  'lp.trust.stat4': 'Pre-IPO funding',

  'lp.manifesto.eyebrow': 'The Shift',
  'lp.manifesto.title': 'From content that broadcasts,<br />to content that <span class="coral">converses</span>.',
  'lp.manifesto.blurb': 'Video, audio, text — playing back the same way, every time — is no longer enough. Neona turns content into a <b>Storyteller</b> that builds the story together with the user.',
  'lp.manifesto.before': 'Before',
  'lp.manifesto.beforeH': 'The old content',
  'lp.manifesto.beforeP': 'Made once by the creator, delivered the same way to everyone. Viewer reactions, questions, and skill levels never reach the content. Engagement stops at likes and comments.',
  'lp.manifesto.after': 'With Neona',
  'lp.manifesto.afterH': 'Content that <span class="coral">converses</span>',
  'lp.manifesto.afterP': 'A conversation that builds the story, delivers information, teaches, and counsels — together. Emotion-expressive voice creates immersive, two-way experiences.',

  'lp.cases.eyebrow': 'Use Cases',
  'lp.cases.title': 'Built for content makers,<br />operators, and <span class="coral">PMs</span>.',
  'lp.cases.blurb': 'Teams in <b>education, marketing, media, and entertainment</b> design, deploy, and iterate without writing code.',
  'lp.cases.eduNum': '01 · Education',
  'lp.cases.eduH': 'AI tutors that <span class="coral">talk with</span> every learner',
  'lp.cases.eduDesc': 'Drop in a textbook or lesson script. The AI tutor reads the material and runs preview, review, and 1-on-1 sessions with each student. Unlimited practice, adaptive feedback at every level.',
  'lp.cases.eduTags': 'Language,Test prep,Preview & review,1:1 tutoring',
  'lp.cases.mktNum': '02 · Marketing',
  'lp.cases.mktH': 'Marketing that <span class="coral">explains</span> your product',
  'lp.cases.mktDesc': 'Voice agents that talk with prospects, qualify their needs, and walk them through your product in context. Static landing pages become living brand experiences.',
  'lp.cases.mktTags': 'Product demo,Lead qualifying,Onboarding,FAQ',
  'lp.cases.medNum': '03 · Media',
  'lp.cases.medH': 'From listening to news, to <span class="coral">talking</span> with it',
  'lp.cases.medDesc': 'Audio that converses with the listener. Ask follow-ups, dig deeper into context, and turn passive consumption into active inquiry.',
  'lp.cases.medTags': 'News & podcasts,Audiobooks,Documentary,Journalism',
  'lp.cases.entNum': '04 · Entertainment',
  'lp.cases.entH': 'Characters that <span class="coral">live</span> inside the story',
  'lp.cases.entDesc': 'Game NPCs, interactive fiction, character chatbots — design living characters with 700+ voices and a persona builder, then let fans talk with them directly.',
  'lp.cases.entTags': 'Game NPC,Character IP,Interactive fiction,Virtual companion',
  'lp.cases.exploreAll': 'Explore all 10 use cases',

  'lp.how.eyebrow': 'How it works',
  'lp.how.title': 'Write a persona, drop in your content, <span class="coral">deploy</span>.',
  'lp.how.blurb': 'No development required. STT, LLM, TTS — every voice technology you need, in <b>one pipeline</b> built end-to-end by Neosapience.',
  'lp.how.step1Num': 'Step 01',
  'lp.how.step1H': 'Write the persona',
  'lp.how.step1P': "Define voice, tone, and personality with a prompt. Choose from 700+ character voices or clone your brand's own voice.",
  'lp.how.step2Num': 'Step 02',
  'lp.how.step2H': 'Connect your content',
  'lp.how.step2P': 'Upload existing textbooks, manuals, scripts, FAQs. The agent understands the material and leads the conversation on its own.',
  'lp.how.step3Num': 'Step 03',
  'lp.how.step3H': 'Deploy & operate',
  'lp.how.step3P': 'Embed on web, app, or as a widget. Track conversation logs, drop-off points, and satisfaction in your dashboard. Update personas in real time.',

  'lp.voices.eyebrow': 'Voice Library',
  'lp.voices.title': '<span class="coral">700+</span> character voices,<br />ready for any story.',
  'lp.voices.desc': "Pick a voice that fits the story you're telling — narrators, tutors, hosts, NPCs. Or clone one of your own in minutes.",

  'lp.role.tutor': 'Tutor',
  'lp.role.storyteller': 'Storyteller',
  'lp.role.coach': 'Coach',
  'lp.role.narrator': 'Narrator',
  'lp.role.anchor': 'Anchor',
  'lp.role.instructor': 'Instructor',
  'lp.role.companion': 'Companion',
  'lp.role.gameNpc': 'Game NPC',
  'lp.role.counselor': 'Counselor',
  'lp.role.host': 'Host',
  'lp.role.mentor': 'Mentor',
  'lp.role.news': 'News',
  'lp.role.wellness': 'Wellness',
  'lp.role.trainer': 'Trainer',

  'lp.why.eyebrow': 'Why Neona',
  'lp.why.title': 'Immersion lives in the <span class="coral">details</span>.',
  'lp.why.blurb': "Our years of work on <b>emotion-expressive voice</b> are what make conversation feel real. This isn't a stack of third-party APIs glued together.",
  'lp.why.d1H': 'Smart Emotion',
  'lp.why.d1P': 'Praise, encouragement, surprise, focus — emotion shifts in real time as the conversation unfolds, the way a real person speaks.',
  'lp.why.d1S': 'Voice with feeling',
  'lp.why.d2H': 'Streaming response',
  'lp.why.d2P': 'Zero-latency two-way conversation. Pacing that feels like talking with a person, not an interface.',
  'lp.why.d2S': 'No more waiting',
  'lp.why.d3H': 'Multilingual & code-switching',
  'lp.why.d3P': 'Switches between languages mid-sentence the way a real bilingual speaker would. Korean-English transitions stay seamless.',
  'lp.why.d3S': '12+ languages',
  'lp.why.d4H': 'No-code operation',
  'lp.why.d4P': 'Content teams build, deploy, and iterate without developers. Personas, scripts, and voices update live.',
  'lp.why.d4S': 'Built by content teams',
  'lp.why.d5H': 'Document-aware RAG',
  'lp.why.d5P': 'Use your existing textbooks, manuals, and scripts as-is. The agent understands them and runs the session autonomously.',
  'lp.why.d5S': 'Your content, as-is',
  'lp.why.d6H': 'Full-stack, in-house',
  'lp.why.d6P': 'STT, LLM, TTS — all built by us, served as a single pipeline. No third-party API dependencies, predictable quality and cost.',
  'lp.why.d6S': 'End-to-end voice AI',

  'lp.study.eyebrow': 'Case Study',
  'lp.study.title': 'Already <span class="coral">live</span><br />in real classrooms.',
  'lp.study.partner': 'Yoon Sunsaeng × Neosapience · Tuit launch July 2026',
  'lp.study.quote': "For <span class=\"coral\">5–10 minutes</span> before each English class, students chat with an AI tutor built on Neona to preview the lesson. Just like a human teacher leading a session from the textbook, the AI tutor <span class=\"coral\">follows the day's lesson document</span> to guide preview with each student.",
  'lp.study.source': "<b>Yoon Sunsaeng</b> integrates Neona into 'Tuit', its native-speaker video English service<span class=\"news-tag\">The Electronic Times · 2026.05.19</span>",
  'lp.study.f1H': 'Smart Emotion',
  'lp.study.f1D': 'Praise and encouragement reflected in voice in real time',
  'lp.study.f2H': 'Streaming voice',
  'lp.study.f2D': 'Zero-latency, natural two-way conversation',
  'lp.study.f3H': 'Kid-friendly personas',
  'lp.study.f3D': 'Voice design tuned for student engagement',
  'lp.study.f4H': 'Autonomous sessions',
  'lp.study.f4D': "Reflects Yoon Sunsaeng's teaching method",

  'lp.final.eyebrow': 'Early Access',
  'lp.final.title': 'Make your content<br /><span class="coral">converse</span>.',
  'lp.final.lead': "We're recruiting content teams from education, marketing, media, and entertainment. Early access cohorts get 1:1 onboarding and full integration support.",
  'lp.final.fine': '<b>Limited cohort</b> · Domain-prioritized selection · Free 4-week PoC',

  'lp.foot.blurb': 'Interactive Storytelling Voice Agent — a product of Neosapience, the team behind Typecast.',
  'lp.foot.product': 'Product',
  'lp.foot.why': 'Why Neona',
  'lp.foot.company': 'Company',
  'lp.foot.contact': 'Contact',
  'lp.foot.sales': 'Talk to sales',
  'lp.foot.copyright': '© {year} <b>Neosapience, Inc.</b> All rights reserved.',
}

const ko: Record<string, string> = {
  'nav.dashboard': '대시보드',
  'nav.agents': '에이전트',
  'nav.sessions': '세션',
  'nav.apiKeys': 'API 키',
  'nav.integrations': '연동',
  'nav.docs': '문서',
  'nav.contact': '문의',
  'nav.pricing': '요금',
  'nav.aicc': 'AICC ↗',
  'aicc.open.opening': 'AICC 로 들어가는 중…',
  'aicc.open.failed': 'AICC 를 열지 못했어요',
  'aicc.open.notEnabled': '이 조직은 AICC 를 쓰고 있지 않아요. 운영팀에 요청하세요.',
  'aicc.open.notConnected': 'AICC 연결이 아직 끝나지 않았어요. 운영팀에 알려 주세요.',
  'aicc.open.unavailable': 'AICC 가 응답하지 않아요. 잠시 후 다시 시도해 주세요.',
  'aicc.open.back': '대시보드로 돌아가기',
  'blocked.title': '이 조직은 AICC 만 사용합니다',
  'blocked.descAicc': '이 조직은 플랫폼을 쓰지 않도록 설정돼 있어요. 상담은 AICC 에서 관리하세요.',
  'blocked.descNoAicc': '이 조직은 플랫폼을 쓰지 않도록 설정돼 있어요. 운영팀에 문의해 주세요.',
  'nav.team': '팀',
  'nav.logOut': '로그아웃',

  // Pricing
  'pricing.title': '요금 및 사용량',
  'pricing.desc': '사용량을 모니터링하고 플랜을 관리합니다.',
  'pricing.currentPlan': '현재 플랜',
  'pricing.sessionsUsed': '사용 세션',
  'pricing.minutesUsed': '사용 시간(분)',
  'pricing.unlimited': '무제한',
  'pricing.perMonth': '/월',
  'pricing.upgrade': '업그레이드',
  'pricing.usageWarning': '월간 세션 할당량의 {pct}%를 사용했습니다.',
  'pricing.usageLimitReached': '월간 세션 한도에 도달했습니다. 플랜을 업그레이드하세요.',
  'pricing.maxAgents': '최대 에이전트',
  'pricing.maxConcurrent': '최대 동시 세션',
  'pricing.maxSessions': '월 최대 세션',
  'pricing.maxApiKeys': '최대 API 키',
  'pricing.free': '무료',
  'pricing.thisMonth': '이번 달',

  'dashboard.title': '개요',
  'dashboard.desc': '모든 음성 에이전트의 사용량, 세션, 시스템 상태를 확인합니다.',
  'dashboard.provisioningFailed': '가입할 때 개통 설정(AICC 연결·전화번호 등) 중 일부를 적용하지 못했어요. 운영팀에 문의해 주시면 다시 적용해 드려요.',
  'dashboard.dateRange': '기간',
  'dashboard.last7d': '7일',
  'dashboard.last30d': '30일',
  'dashboard.last90d': '90일',
  'dashboard.custom': '직접 설정',
  'dashboard.perAgent': '에이전트별 사용량',
  'dashboard.agent': '에이전트',
  'dashboard.avgDuration': '평균 시간',
  'dashboard.noUsageData': '해당 기간에 사용 데이터가 없습니다.',
  'dashboard.anomalyAlert': '건의 이번 달 세션이 추정/근사 측정값입니다 — 정산 전 검토가 필요합니다.',
  // 팀원별 사용량(TASK-16727) — 같은 기간의 청구 시간을 통화의 관련 사용자로 묶은 것
  'dashboard.perMember': '팀원별 사용량',
  'dashboard.member.colPerson': '사람',
  'dashboard.member.colStatus': '상태',
  'dashboard.member.colCalls': '통화 수',
  'dashboard.member.colAi': 'AI 시간',
  'dashboard.member.colOperator': '상담원 시간',
  'dashboard.member.colBilled': '청구 시간',
  'dashboard.member.noUser': '사람 없음',
  'dashboard.member.otherOrg': '다른 조직 사용자',
  'dashboard.member.former': '전 팀원',
  'dashboard.member.total': '합계',
  'dashboard.member.empty': '기간 안에 통화가 없습니다.',
  'dashboard.member.bySource': '경로별 청구 시간',
  'dashboard.member.source.login': '로그인',
  'dashboard.member.source.api_key': 'API 키',
  'dashboard.member.source.widget_key': '위젯',
  'dashboard.member.source.share_link': '공유 링크',
  'dashboard.member.source.inbound_agent': '수신',
  'dashboard.member.source.unknown': '미상',
  'agents.title': '에이전트',
  'agents.desc': '음성 에이전트를 생성, 설정, 관리합니다.',
  'agents.edit': '수정',
  'agents.delete': '삭제',
  'agents.confirmDelete': '이 에이전트를 삭제할까요?',
  'agents.deactivate': '비활성화',
  'agent.activateAgent': '에이전트 활성화',
  'agent.deleteForever': '영구 삭제',
  'agents.activate': '활성화',
  'agents.confirmDeactivate': '비활성화할까요? 목록에 남고 언제든 다시 활성화할 수 있습니다.',
  'agents.confirmHardDelete': '영구 삭제할까요? 모든 목록에서 사라지고 되돌릴 수 없습니다.',
  'sessions.title': '세션',
  'sessions.desc': '모든 에이전트의 음성 세션을 조회하고 모니터링합니다.',
  'sessions.anomalyFilter': '정산 이상치만 필터링됨',
  'sessions.userFilter': '선택한 사람의 통화만 필터링됨 (전체 기간)',
  'sessions.clearFilter': '필터 해제',
  'apiKeys.title': 'API 키',
  'apiKeys.desc': '서버 호출 또는 브라우저 위젯용 키를 발급합니다. 키는 생성 시 한 번만 표시됩니다.',
  'account.title': '계정',
  'account.desc': '프로필, 비밀번호, 계정 설정을 관리합니다.',

  'stat.minutesThisMonth': '이번 달 사용량(분)',
  'stat.sessions': '세션',
  'stat.sessionsThisMonth': '이번 달 세션',
  'stat.activeAgents': '활성 에이전트',
  'stat.activeNow': '현재 활성',
  'stat.totalAgents': '전체 에이전트',
  'stat.totalKeys': '전체 키',
  'stat.apiSecrets': 'API 시크릿',
  'stat.widgetKeys': '위젯 키',
  'stat.lastUsed': '마지막 사용',

  'action.newAgent': '+ 새 에이전트',
  'action.manageAgents': '에이전트 관리',
  'action.issueKey': '키 발급',
  'action.authGuide': '인증 가이드',
  'action.startCall': '통화 시작',
  'action.startChat': '채팅 테스트',
  'newAgent.chatAgent': '채팅 에이전트 (텍스트 전용)',
  'agents.chatBadge': '채팅',
  'chat.test.eyebrow': '채팅 테스트',
  'chat.test.desc':
    '이 에이전트와의 텍스트 리허설입니다. 이 에이전트를 AICC에 연결했다면(연동 › 채팅) 여기서 나눈 대화가 AICC 인박스에 「채팅 테스트」로 들어가고, 상담원 연결을 요청하면 실제 상담원에게 벨이 울립니다. 연결하지 않았다면 아무것도 나가지 않습니다.',
  'chat.test.aiBadge': 'AI',
  'chat.test.aiBadgeHint': 'AI가 생성하는 응답입니다',
  'chat.test.introBody':
    '방문자가 위젯에서 만나는 것과 같은 프롬프트·모델로 대화해 봅니다. 준비되면 시작하세요.',
  'chat.test.start': '채팅 테스트 시작',
  'chat.test.placeholder': '메시지를 입력하세요…',
  'chat.test.send': '전송',
  'chat.test.end': '테스트 종료',
  'chat.test.ended': '테스트가 종료됐습니다.',
  'chat.test.back': '에이전트로 돌아가기',
  'chat.test.turnLimit': '이 세션의 턴 상한에 도달했습니다. 테스트를 종료하고 새로 시작하세요.',
  'chat.test.typing': '에이전트가 응답 중',
  'chat.test.restart': '테스트 재시작',
  'chat.test.widgetNote': '방문자가 보는 실제 위젯 화면 그대로입니다 — 말풍선·스트리밍·타이핑 동일. 실행마다 새 세션으로 시작합니다.',
  'chat.test.error.start': '채팅 테스트를 시작하지 못했습니다. 다시 시도해 주세요.',
  'chat.test.error.send': '응답을 받지 못했습니다. 입력한 내용은 남아 있으니 다시 시도해 주세요.',
  'chat.test.error.agent': '에이전트 정보를 불러오지 못했습니다.',
  'chat.test.code.session_ended': '세션이 종료됐습니다.',
  'chat.test.code.turn_limit_reached': '이 세션의 턴 상한에 도달했습니다.',
  'chat.test.code.llm_failed': '모델 응답에 실패했습니다. 다시 시도해 주세요.',
  'chat.test.code.llm_not_configured': '이 환경에는 채팅 LLM 이 설정돼 있지 않습니다.',
  'chat.test.code.rate_limit_exceeded': '요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.',
  'chat.test.code.message_invalid': '보낼 수 없는 메시지입니다 (빈 내용 또는 길이 초과).',
  'action.startOutbound': '아웃바운드 통화',
  'action.startOutboundCall': '전화 걸기',
  'action.save': '변경사항 저장',
  'action.cancel': '취소',
  'action.close': '닫기',
  'action.back': '뒤로',
  'action.next': '다음',
  'action.create': '생성',
  'action.delete': '삭제',
  'action.restore': '복원',
  'action.deactivate': '비활성화',
  'action.retry': '다시 시도',
  'action.talkToUs': '문의하기',
  'action.talkToSales': '영업팀 문의',

  'cookie.message': '네오나는 서비스 이용 분석과 광고 성과 측정, 맞춤형 광고 제공을 위해 쿠키를 사용합니다. 동의하시면 쿠키 식별자, 방문·이용 기록, 사이트에 입력하신 이메일·전화번호(해시 처리)가 Google LLC(미국)로 이전되어 처리됩니다. 동의하지 않아도 사이트 이용에는 제한이 없습니다. 자세한 내용은 개인정보 처리방침에서 확인하실 수 있습니다.',
  'cookie.privacy': '개인정보 처리방침',
  'cookie.accept': '동의',
  'cookie.decline': '거부',
  'cookie.settingsLabel': '쿠키 동의',
  'cookie.settingsDesc': '분석 및 광고 쿠키 허용',

  'quick.createAgent': '에이전트 생성',
  'quick.createAgentDesc': '템플릿 또는 처음부터 시작합니다.',
  'quick.browseApi': 'API 둘러보기',
  'quick.browseApiDesc': 'REST, WebRTC, 웹훅.',
  'quick.issueKey': 'API 키 발급',
  'quick.issueKeyDesc': '서버 또는 위젯 토큰.',
  'quick.talkToUs': '문의하기',
  'quick.talkToUsDesc': '질문, 피드백, 엔터프라이즈 문의.',

  'auth.welcomeBack': '돌아오셨군요',
  'auth.signIn': 'Neona 계정에 로그인하세요.',
  'auth.createAccount': '계정 만들기',
  'auth.createAccountDesc': '워크스페이스와 기본 에이전트가 자동으로 설정됩니다.',
  'auth.noAccount': '계정이 없으신가요?',
  'auth.createOne': '도입문의',
  'auth.noAccountSuffix': '를 남겨주시면 안내해드려요.',
  'auth.alreadyHave': '이미 계정이 있으신가요?',
  'auth.logIn': '로그인',
  'auth.email': '이메일',
  'auth.password': '비밀번호',
  'auth.fullName': '이름',
  'auth.workEmail': '업무용 이메일',
  'auth.rememberMe': '로그인 유지',
  'auth.forgotPassword': '비밀번호를 잊으셨나요?',
  'auth.signUpGoogle': 'Google로 가입',
  'auth.signInGoogle': 'Google로 로그인',
  'auth.googleSigningIn': '로그인 중입니다…',
  'auth.googleSignInFailed': 'Google 로그인을 완료하지 못했습니다',
  'auth.accountNotFound':
    '이 이메일로 만들어진 Neona Agents 계정이 없습니다. 하단의 **도입 문의를** 남겨주시면 계정을 열어드립니다.',
  'auth.invalidCredentials':
    '이메일 또는 비밀번호가 올바르지 않습니다. 계정이 아직 없다면 하단의 **도입 문의를** 남겨주시면 계정을 열어드립니다.',
  'auth.loginFailed': '로그인하지 못했습니다. 연결 상태를 확인한 뒤 다시 시도해 주세요.',
  'auth.inviteRequiredNotice':
    '가입은 초대 링크로만 가능합니다. 도입 문의를 남겨주시면 링크를 보내드립니다.',
  'auth.redirectingToContact': '도입 문의 화면으로 이동합니다…',
  'auth.googlePopupBlocked': '브라우저가 로그인 창을 차단했습니다. 이 사이트의 팝업을 허용한 뒤 다시 시도해 주세요.',
  'auth.googleTermsNotice': 'Google로 계속하면 다음에 동의하는 것으로 봅니다:',
  'auth.or': '또는',
  'auth.agreeTerms': '동의합니다:',
  'auth.termsOfService': '이용약관',
  'auth.and': '및',
  'auth.privacyPolicy': '개인정보 처리방침',

  'contact.title': '영업팀 문의',
  'contact.eyebrow': '문의',
  'contact.name': '이름',
  'contact.email': '이메일',
  'contact.company': '회사명',
  'contact.phone': '전화번호',
  'contact.message': '문의 내용',
  'contact.privacyAgree': '개인정보 수집 및 이용에 동의합니다.',
  'contact.submit': '문의하기',
  'contact.success': '감사합니다!',
  'contact.successDesc': '문의가 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.',
  'contact.close': '닫기',
  'contact.required': '(필수)',
  'contact.namePlaceholder': '홍길동',
  'contact.companyPlaceholder': '회사명',
  'contact.phonePlaceholder': '010-0000-0000',
  'contact.inquiryType': '어떤 것이 필요하신가요?',
  'contact.inquiryTypePlaceholder': '선택해 주세요 (선택)',
  'contact.messagePlaceholder': '궁금하신 점이나 도입 문의 사항을 자유롭게 적어주세요.',
  'contact.errorPrivacy': '개인정보 수집 및 이용에 동의해 주세요.',
  'contact.errorRequired': '이름·이메일·회사명·전화번호는 필수입니다.',
  'contact.errorEmail': '올바른 이메일 주소를 입력해 주세요.',
  'contact.errorGeneric': '문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.',

  'auth.editorialQuote': '"고객이 기억하는 건 응답 속도가 아니라, 자신의 말이 닿았다는 느낌입니다."',

  'misc.loading': '로딩 중…',
  'misc.noData': '데이터 없음',
  'misc.vsLast': '전월 대비',
  'misc.published': '배포됨',
  'misc.live': '라이브',
  'misc.activeCalls': '활성 통화',
  'misc.region': '리전 · ap-northeast-2 · 서울',
  'misc.recentSessions': '최근 세션',
  'misc.viewAll': '전체 보기 →',
  'misc.viewSessions': '세션 보기',
  'misc.minutesLastN': '사용량 · 최근 {n}일 (분)',
  'misc.minutesRange': '사용량 · {start} ~ {end} (분)',
  'misc.vsPrevPeriod': '이전 같은 기간 대비',
  'misc.minutes': '분',
  'misc.noSessions': '세션이 없습니다',
  'misc.noAgents': '에이전트가 없습니다',
  'misc.createFirst': '첫 번째 음성 에이전트를 만들어 시작하세요.',

  // API Keys page
  'keys.issueNew': '새 키 발급',
  'keys.keyCreated': '키가 생성되었습니다 — 지금 복사하세요',
  'keys.copyKey': '키 복사',
  'keys.expiresLabel': '만료',
  'keys.shownOnce': '전체 키는 이때만 표시됩니다.',
  'keys.active': '활성',
  'keys.serverSide': '서버 측',
  'keys.browserDomainBound': '브라우저, 도메인 제한',
  'keys.mostRecentActivity': '가장 최근 활동',
  'keys.noActivityYet': '활동 없음',
  'keys.searchKeys': '키 검색…',
  'keys.loadingKeys': '로딩 중…',
  'keys.loadError': 'API 키를 불러오지 못했습니다. 다시 시도해 주세요.',
  'keys.noKeysYet': '키가 없습니다. 위에서 첫 번째 키를 발급하세요.',
  'keys.noKeysYetMember': '아직 발급된 API 키가 없어요. 키 발급은 조직 소유자·관리자가 해요.',
  'keys.noKeysMatch': '필터에 맞는 키가 없습니다.',
  'keys.keyName': '키 이름',
  'keys.keyNamePlaceholder': '예: 프로덕션 서버',
  'keys.keyType': '키 유형',
  'keys.apiSecret': 'API 시크릿',
  'keys.widgetKey': '위젯 키',
  'keys.apiSecretDesc': '서버 측, 비공개 유지',
  'keys.widgetKeyDesc': '브라우저, 도메인 제한',
  'keys.allowedDomains': '허용 도메인',
  'keys.domainsHint': '쉼표로 구분. *.example.com 같은 와일드카드 지원.',
  'keys.domainsPlaceholder': '*.example.com, app.example.com',
  'keys.expiration': '만료 기간',
  'keys.never': '없음',
  'keys.30days': '30일',
  'keys.90days': '90일',
  'keys.1year': '1년',
  'keys.creating': '생성 중…',
  'keys.createKey': '키 생성',
  'keys.deactivating': '비활성화 중…',
  'keys.adminOnly': 'API 키 발급·비활성화는 조직 소유자·관리자만 할 수 있어요.',
  'keys.confirmDeactivateDesc': '이 작업은 되돌릴 수 없습니다. 확인하려면 키 이름을 입력하세요.',
  'keys.rename': '이름 변경',
  'keys.saving': '저장 중…',
  'keys.domains': '도메인',
  'keys.created': '생성일',
  'keys.inactive': '비활성',

  // Agent new page
  'newAgent.breadcrumb': '에이전트 → 새 에이전트',
  'newAgent.title': '에이전트 생성',
  'newAgent.desc': '새 음성 AI 에이전트를 설정합니다. 생성 후에도 모든 설정을 변경할 수 있습니다.',
  'newAgent.stepBasics': '기본 정보',
  'Flow': '플로우',
  'Save': '저장',
  'Saving...': '저장 중...',
  'Save failed': '저장 실패',
  'Clear': '지우기',
  'agent.voiceAgentFromFlow': '음성 에이전트 (플로우로 생성됨)',
  'flow.retiredEyebrow': '단일 프롬프트',
  'flow.retiredTitle': '이 에이전트는 이제 단일 프롬프트로 응대합니다',
  'flow.retiredBody':
    '엔진이 저장된 플로우를 더 이상 실행하지 않고, 통화는 프롬프트 하나로 처리합니다. 응대 내용은 ‘대화’ 탭에서 설정해 주세요.',
  'flow.retiredEyebrowUrgent': '설정 필요',
  'flow.retiredTitleUrgent': '이 에이전트에는 아직 응대 지시문이 없습니다',
  'flow.retiredBodyUrgent':
    '지시문이 플로우 안에 들어 있었는데 엔진이 플로우를 읽지 않게 되면서, 지금은 아무 지시 없이 통화를 받고 있습니다. ‘대화’ 탭에서 지시문을 작성해 주세요.',
  'flow.retiredPrevious': '이전 플로우 구성 {count}단계:',
  'flow.retiredCta': '대화 탭으로 이동',
  'newAgent.stepConversation': '프롬프트',
  'newAgent.stepVoice': '음성',
  'newAgent.stepReview': '검토',
  'newAgent.identity': '기본 정보',
  'newAgent.identityDesc': '대시보드에 표시되는 에이전트 정보입니다.',
  'newAgent.name': '이름',
  'newAgent.agentType': '에이전트 유형',
  'newAgent.voiceAgent': '단일 에이전트',
  'newAgent.description': '설명',
  'newAgent.primaryLang': '기본 언어',
  'newAgent.templateDesc': '청사진으로 시작하거나 처음부터 만듭니다.',
  'newAgent.blankDesc': '처음부터 시작합니다.',
  'newAgent.supportDesc': '환불, 배송, 계정 관련 질문을 처리합니다.',
  'newAgent.outboundDesc': '갱신 알림 및 설문조사.',
  'newAgent.conversation': '대화',
  'newAgent.conversationDesc': '에이전트의 사고 방식과 통화 시작 방법을 설정합니다.',
  'newAgent.systemPrompt': '시스템 프롬프트',
  'newAgent.systemPromptHint': '에이전트의 성격과 행동을 정의합니다. {{variable}}로 런타임 치환이 가능합니다.',
  'newAgent.greeting': '인사말',
  'newAgent.greetingHint': '세션 시작 시 첫 번째로 말하는 문장입니다.',
  'newAgent.voice': '음성',
  'newAgent.voiceDesc': '에이전트가 사용할 캐릭터 음성입니다.',
  'newAgent.selectVoice': '음성을 선택하세요',
  'newAgent.selectBaramCharacter': '아바타 캐릭터를 선택하세요',
  'newAgent.change': '변경',
  'newAgent.models': '모델',
  'newAgent.modelsDesc': '대화를 처리하는 기술 스택입니다.',
  'newAgent.sttProvider': 'STT 제공자',
  'newAgent.llmProvider': 'LLM 제공자',
  'newAgent.llmModel': 'LLM 모델',
  'newAgent.reviewTitle': '검토',
  'newAgent.reviewDesc': '생성 전 마지막 확인입니다.',
  'newAgent.nameRequired': '에이전트 이름은 필수입니다',
  'newAgent.systemPromptRequired': '시스템 프롬프트는 필수입니다',
  'newAgent.continue': '계속 →',
  'newAgent.createAgent': '에이전트 생성 →',
  'newAgent.livePreview': '미리보기',
  'newAgent.previewNote': '에이전트가 어떻게 표시될지 보여주는 미리보기입니다. 실제 세션은 생성되지 않습니다.',

  // Account page
  'account.profile': '프로필',
  'account.profileDesc': '앱에 표시되는 이름입니다.',
  'account.name': '이름',
  'account.profileImage': '프로필 이미지 URL',
  'account.saved': '저장됨.',
  'account.preferences': '환경 설정',
  'account.preferencesDesc': '사용 환경을 맞춤 설정합니다.',
  'account.language': '언어',
  'account.changePassword': '비밀번호 변경',
  'account.changePasswordDesc': '변경 후 기존 세션은 모두 로그아웃됩니다.',
  'account.currentPassword': '현재 비밀번호',
  'account.newPassword': '새 비밀번호',
  'account.newPasswordHint': '8자 이상.',
  'account.confirmPassword': '새 비밀번호 확인',
  'account.passwordChanged': '비밀번호가 변경되었습니다.',
  'account.passwordsNoMatch': '새 비밀번호가 일치하지 않습니다.',
  'account.passwordTooShort': '새 비밀번호는 8자 이상이어야 합니다.',
  'account.deleteAccount': '계정 삭제',
  'account.deleteDesc': '계정을 영구적으로 삭제합니다. 개인 정보는 삭제되지만, 일부 기록(청구서 등)은 법적 요구에 따라 보관됩니다. 이 작업은 취소할 수 없습니다.',
  'account.deleteConfirmDesc': '취소할 수 없습니다. 확인을 위해 이메일을 입력하고 현재 비밀번호를 입력하세요.',
  'account.typeEmail': '확인을 위해 이메일을 입력하세요',
  'account.deleteButton': '계정 삭제하기',
  'account.deletePermanent': '영구적으로 계정 삭제',
  'account.emailNoMatch': '이메일이 일치하지 않습니다.',
  'account.passwordRequired': '비밀번호가 필요합니다.',
  'account.deleteOwnerHasMembers': '팀원이 있는 동안은 소유자 계정을 삭제할 수 없습니다. 팀 화면에서 팀원을 먼저 내보낸 뒤 다시 시도하세요.',

  // API Doc
  'apiDoc.logIn': '로그인',
  'apiDoc.getStarted': '시작하기',
  'apiDoc.apiRef': 'API 레퍼런스',
  'apiDoc.heroTitle': 'Neona로 음성 AI 에이전트를 구축하세요.',
  'apiDoc.heroSub': '에이전트 생성, 음성 탐색, 라이브 음성 세션을 위한 REST 엔드포인트.',
  // 문서 블록의 고정 문구(TASK-16438) — 문서 내용은 서버가 언어별로 준다
  'apiDoc.pathParams': '경로 파라미터',
  'apiDoc.queryParams': '쿼리 파라미터',
  'apiDoc.bodyParams': '본문 파라미터',
  'apiDoc.required': '필수',
  'apiDoc.authorization': '인증:',
  'apiDoc.response': '응답',
  'apiDoc.copy': '복사',
  'apiDoc.copied': '복사됨',
  'apiDoc.loadFailed': 'API 문서를 불러오지 못했습니다.',
  'apiDoc.retry': '다시 시도',
  'apiDoc.questions': '질문이 있으신가요?',
  'apiDoc.contactUs': '문의하기 →',

  // Login extras
  'auth.continueGoogle': 'Google로 계속하기',
  'auth.remember30': '30일 동안 로그인 유지',
  'auth.logInButton': '로그인',

  // Signup extras
  'auth.createAccountButton': '계정 만들기',
  'auth.agreeTermsError': '이용약관 및 개인정보 처리방침에 동의해 주세요.',
  'auth.passwordMin8': '비밀번호는 8자 이상이어야 합니다.',
  'auth.passwordHint': '8자 이상.',
  'auth.birthdate': '생년월일',
  'auth.birthdateHint': '만 14세 이상만 가입할 수 있어요.',
  'auth.birthdateRequired': '생년월일을 입력해 주세요.',
  'auth.underageError': '만 14세 미만은 가입할 수 없습니다.',

  'landing.hero': '현대 인터넷을 위한',
  'landing.heroAccent': '음성 인프라',
  'landing.heroSub': '단 하나의 API로 프로덕션 수준의 음성 AI 에이전트를 출시하세요. 인프라 관리 없이, 듣고 말하고 일을 처리하는 에이전트를 바로 만들 수 있습니다.',
  'landing.startBuilding': '무료로 시작하기',
  'landing.documentation': '문서',
  'landing.signIn': '로그인',
  'landing.pricing': '요금',
  'landing.pricingTitle': '베타',
  'landing.pricingDesc': 'Neona는 현재 베타 서비스입니다. 베타 기간 동안 모든 기능을 무료로 사용할 수 있습니다. 엔터프라이즈 문의 또는 커스텀 플랜은 영업팀에 문의해 주세요.',

  // Landing extras
  'landing.howItWorks': '작동 방식',
  'landing.threeSteps': '세 단계로 시작하기',
  'landing.threeStepsDesc': '에이전트를 만들고, 위젯을 생성하고, 사이트에 배포하세요. 그게 전부입니다.',
  'landing.useCases': '활용 사례',
  'landing.useCasesTitle': '모든 음성 접점을 위한 솔루션',
  'landing.voices': '음성',
  'landing.voicesTitle': '500개 이상의 자연스러운 음성',
  'landing.voicesDesc': '28개 언어의 풍부하고 저지연 음성 라이브러리에서 선택하거나, 음성 복제로 나만의 음성을 사용하세요.',
  'landing.readyToShip': '첫 번째 음성 에이전트를 출시할 준비가 되셨나요?',
  'landing.noCardRequired': '몇 초면 가입 완료. 신용카드 불필요.',
  'landing.talkToSales': '영업팀 문의',
  'landing.privacy': '개인정보처리방침',
  'landing.terms': '이용약관',

  // Share links
  'shareLinks.title': '공유 링크',
  'shareLinks.desc': '누구나 이 에이전트를 테스트할 수 있는 링크를 생성합니다. 로그인이 필요 없습니다.',
  'shareLinks.create': '링크 생성',
  'shareLinks.namePlaceholder': '링크 이름 (예: 고객 A 데모용)',
  'shareLinks.copyLink': '링크 복사',
  'shareLinks.deactivate': '비활성화',
  'shareLinks.deactivateConfirm': '이 공유 링크를 비활성화하시겠습니까?',
  'shareLinks.noLinks': '공유 링크가 없습니다.',
  'shareLinks.untitled': '이름 없는 링크',

  // Try page
  'try.unavailable': '이 링크는 더 이상 사용할 수 없습니다.',
  'try.unavailableDesc': '공유 링크가 비활성화되었거나 존재하지 않습니다.',
  'try.backHome': '홈으로 돌아가기',
  'try.startCall': '통화 시작',
  'try.startChat': '채팅 시작',
  'try.endCall': '통화 종료',
  'try.connecting': '연결 중…',
  'try.connected': '연결됨',
  'try.speaking': '에이전트 말하는 중',
  'try.listening': '듣는 중…',
  'try.ended': '통화 종료됨',
  'try.tryAgain': '다시 통화',
  'try.poweredBy': 'Powered by Neona',

  // Onboarding

  // Team (TASK-16726 — 팀원 · 초대 · 내보내기)
  'team.eyebrow': '조직',
  'team.title': '팀',
  'team.desc': '팀원과 초대를 관리합니다. 팀원은 이 조직의 에이전트·통화·API 키를 함께 씁니다.',
  'team.seats': '팀원 {members} / 한도 {limit} (대기 {pending})',
  'team.unlimited': '무제한',
  'team.loadError': '팀 정보를 불러오지 못했습니다. 새로 고쳐 주세요.',
  'team.inviteTitle': '팀원 초대',
  'team.inviteDesc': '가입 링크를 메일로 보냅니다. 받은 사람이 그 링크로 가입하거나 로그인하면 이 조직에 합류합니다. 링크는 7일 동안 한 번만 쓸 수 있습니다.',
  'team.email': '이메일 주소',
  'team.role': '역할',
  'team.sendInvite': '초대 보내기',
  'team.noSeatsZero': '이 조직은 아직 팀원을 초대할 수 없습니다(한도 0명). 한도를 늘리려면 네오나에 문의하세요.',
  'team.noSeatsFull': '남은 자리가 없습니다. 대기 중인 초대를 취소하면 자리가 생기고, 한도를 늘리려면 네오나에 문의하세요.',
  'team.inviteSent': '{email} 님에게 초대 메일을 보냈습니다.',
  'team.inviteMailFailed': '{email} 님의 초대를 만들었지만 메일을 보내지 못했습니다. 아래 「보낸 초대」에서 「다시 보내기」를 누르세요.',
  'team.inviteResent': '{email} 님에게 초대 메일을 다시 보냈습니다. 예전 링크는 더 이상 쓸 수 없습니다.',
  'team.inviteRevoked': '{email} 님의 초대를 취소했습니다.',
  'team.membersTitle': '팀원',
  'team.invitesTitle': '보낸 초대',
  'team.invitesLoadError': '초대 목록을 불러오지 못했습니다.',
  'team.noInvites': '보낸 초대가 없습니다.',
  'team.col.name': '이름',
  'team.col.email': '이메일',
  'team.col.role': '역할',
  'team.col.status': '상태',
  'team.col.mail': '발송',
  'team.col.expires': '만료',
  'team.col.invitedBy': '초대한 사람',
  'team.owner': '소유자',
  'team.admin': '관리자',
  'team.member': '멤버',
  'team.you': '나',
  'team.roleFor': '{email} 역할',
  'team.roleChanged': '{email} 님의 역할을 {role}로 바꿨습니다.',
  'team.status.active': '활성',
  'team.status.unapproved': '승인 대기',
  'team.inviteStatus.pending': '대기',
  'team.inviteStatus.accepted': '수락됨',
  'team.inviteStatus.revoked': '취소됨',
  'team.inviteStatus.expired': '만료',
  'team.inviteStatus.removed': '내보냄',
  'team.inviteStatus.withdrawn': '탈퇴',
  'team.mail.sent': '보냄',
  'team.mail.failed': '발송 실패',
  'team.resend': '다시 보내기',
  'team.revoke': '취소',
  'team.revokeTitle': '초대를 취소할까요?',
  'team.revokeBody': '{email} 님에게 보낸 링크는 바로 쓸 수 없게 됩니다. 다시 초대하려면 새로 초대를 보내세요.',
  'team.revokeConfirm': '초대 취소',
  'team.remove': '내보내기',
  'team.removeTitle': '{name} 님을 내보낼까요?',
  'team.removeBody': '{email} 님은 이 조직의 콘솔과 API 를 더 쓸 수 없고, 로그인된 모든 곳에서 로그아웃됩니다. 이 사람이 만든 에이전트와 통화 기록은 조직에 남습니다. 다시 들어오려면 새 초대를 받아야 합니다.',
  'team.removeKeysLoading': '이 사람이 만든 API 키를 확인하는 중…',
  'team.removeKeysLoadError': '이 사람이 만든 API 키를 불러오지 못해 아직 내보낼 수 없습니다. 창을 닫고 다시 시도하세요.',
  'team.removeNoKeys': '이 사람이 만든 사용 중인 API 키는 없습니다.',
  'team.removeKeysIntro': '이 사람이 만든 사용 중인 API 키가 {count}개 있습니다. 내보내도 키는 계속 동작합니다 — 지금 폐기할 키를 고르세요.',
  'team.removeKeysKeepNote': '고르지 않은 키는 조직에 남아 계속 동작합니다. 운영 연동에 쓰는 키를 폐기하면 그 연동이 멈춥니다.',
  'team.revokeNow': '지금 폐기',
  'team.keyLastUsed': '마지막 사용 {when}',
  'team.keyNeverUsed': '사용 기록 없음',
  'team.removeConfirm': '내보내기',
  'team.removed': '{email} 님을 내보냈습니다.',
  'team.removedRevoked': '폐기한 키: {names}',
  'team.removedRevokeFailed': '폐기하지 못한 키: {names} — API 키 화면에서 다시 폐기하세요.',
  'team.removedKept': '남긴 키: {names} — 계속 동작합니다. 그 사람이 키 값을 알고 있으니 필요하면 API 키 화면에서 폐기하세요.',
  'team.noMembers': '팀원이 없습니다.',
  'team.err.generic': '요청을 처리하지 못했습니다. 잠시 뒤 다시 시도하세요.',
  'team.err.adminOnly': '팀원 관리는 조직 소유자·관리자만 할 수 있습니다.',
  'team.err.invalidEmail': '이메일 주소를 확인하세요.',
  'team.err.noSeats': '남은 자리가 없습니다. 대기 중인 초대를 취소하거나 네오나에 한도를 늘려 달라고 요청하세요.',
  'team.err.tooManyRequests': '요청이 많습니다. 잠시 뒤 다시 시도하세요.',
  'team.err.alreadyMember': '이미 이 조직의 팀원입니다.',
  'team.err.userInOtherOrg': '다른 조직에 속한 계정이라 초대할 수 없습니다. 조직을 옮기려면 네오나에 문의하세요.',
  'team.err.accountBlocked': '비활성 또는 미승인 계정의 이메일이라 초대할 수 없습니다. 네오나에 문의하세요.',
  'team.err.provisioningPending': '개통 예약이 걸린 이메일이라 초대할 수 없습니다. 네오나에 문의하세요.',
  'team.err.inviteConflict': '같은 이메일의 초대가 동시에 만들어지는 중이었습니다. 다시 시도하세요.',
  'team.err.inviteChanged': '그 사이 초대가 바뀌었습니다. 목록을 새로 고쳤습니다.',
  'team.err.resendTooSoon': '같은 초대는 1분에 한 번만 보낼 수 있습니다. 1분 뒤 다시 보내세요.',
  'team.err.memberNotFound': '이미 이 조직에 없는 사람입니다. 목록을 새로 고쳤습니다.',
  'team.err.memberChanged': '그 사이 다른 관리자가 이 사람을 바꿨습니다. 목록을 새로 고쳤습니다.',
  'team.err.memberInCall': '통화 중이라 내보낼 수 없습니다. 통화가 끝난 뒤 다시 시도하세요.',
  'team.err.apiKeyNotOwned': '고른 키 중 이 사람이 만든 사용 중인 키가 아닌 것이 있습니다. 키 목록을 새로 읽었으니 확인하고 다시 누르세요.',
  'team.err.cannotRemoveSelf': '자기 자신은 내보낼 수 없습니다.',
  'team.err.cannotRemoveOwner': '조직 소유자는 내보낼 수 없습니다.',
  'team.err.cannotChangeOwner': '소유자의 역할은 바꿀 수 없습니다.',

  // 조직 없음(TASK-16726 — 내보내진 사람이 로그인)
  'noOrg.title': '소속 조직이 없습니다',
  'noOrg.desc': '초대 링크로 합류하세요. 조직 관리자에게 초대를 요청하면 메일로 링크를 받습니다.',

  // 팀원 초대 링크 — 가입 · 로그인 · 수락(TASK-16726)
  'teamInvite.createDesc': '초대받은 조직에 합류할 계정을 만듭니다.',
  'teamInvite.loading': '초대를 확인하는 중…',
  'teamInvite.banner': '**{org}** 조직이 {role} 역할로 초대했습니다.',
  'teamInvite.emailLocked': '초대받은 이메일로만 가입할 수 있습니다 · {date}까지 유효',
  'teamInvite.haveAccount': '이미 계정이 있어요.',
  'teamInvite.loginAndJoin': '로그인하고 합류',
  'teamInvite.loginNotice': '로그인하면 초대받은 조직에 합류합니다.',
  'teamInvite.signupWithInvite': '초대로 가입하기',
  'teamInvite.invalidTitle': '초대가 유효하지 않습니다',
  'teamInvite.reason.not_found': '초대를 찾을 수 없습니다. 초대를 다시 받았다면 가장 최근 메일의 링크를 여세요.',
  'teamInvite.reason.expired': '초대 기한(7일)이 지났습니다. 초대한 관리자에게 다시 보내 달라고 요청하세요.',
  'teamInvite.reason.revoked': '초대가 취소되었습니다. 초대한 관리자에게 문의하세요.',
  'teamInvite.reason.accepted': '이미 수락한 초대입니다. 로그인하세요.',
  'teamInvite.reason.organization_inactive': '초대한 조직이 더 이상 운영되지 않습니다.',
  'teamInvite.reason.unknown': '링크가 만료됐거나, 취소됐거나, 이미 쓰였거나, 다시 보낸 메일의 링크로 바뀌었습니다. 초대한 관리자에게 다시 보내 달라고 요청하세요.',
  'teamInvite.lookupFailed': '초대를 확인하지 못했습니다. 잠시 뒤 메일의 링크를 다시 열어 주세요.',
  'teamInvite.accepting': '초대를 수락하는 중…',
  'teamInvite.joinedTitle': '팀에 합류했습니다',
  'teamInvite.joinedDesc': '이제 {org} 조직의 콘솔을 쓸 수 있습니다.',
  'teamInvite.openConsole': '콘솔로 가기',
  'teamInvite.acceptFailedTitle': '팀에 합류하지 못했습니다',
  'teamInvite.acceptFailed': '초대를 수락하지 못했습니다. 잠시 뒤 다시 시도하세요.',
  'teamInvite.switchAccount': '로그아웃하고 다른 계정으로',
  'teamInvite.backToConsole': '콘솔로 돌아가기',
  'teamInvite.err.emailMismatch': '초대받은 이메일과 다른 계정입니다. 초대받은 이메일의 계정으로 진행하세요.',
  'teamInvite.err.provisioningPending': '이 이메일에 개통 예약이 있어 초대로 합류할 수 없습니다. 네오나에 문의하세요.',
  'teamInvite.err.alreadyMember': '이미 이 조직의 팀원입니다.',
  'teamInvite.err.userInOtherOrg': '다른 조직에 속한 계정이라 합류할 수 없습니다. 조직을 옮기려면 네오나에 문의하세요.',
  'teamInvite.err.accountBlocked': '승인되지 않은 계정이라 합류할 수 없습니다. 네오나에 문의하세요.',
  'teamInvite.err.emailRegistered': '이미 가입된 이메일입니다. 아래 「로그인하고 합류」를 누르세요.',
  'teamInvite.err.exclusive': '이 링크는 다른 가입 링크와 함께 쓸 수 없습니다. 초대 메일의 링크를 다시 여세요.',
  'teamInvite.err.noAccount': '이 구글 계정으로 만든 계정이 없습니다. 아래 「초대로 가입하기」로 가입하세요.',
  'teamInvite.err.tooManyRequests': '요청이 많습니다. 잠시 뒤 다시 시도하세요.',

  // Agent detail - tabs
  'tab.overview': '일반',
  'tab.flow': '플로우',
  'tab.conversation': '프롬프트',
  'tab.voice': '음성',
  'tab.settings': '설정',
  'tab.evaluation': '평가',
  'tab.deploy': '배포',
  'tab.history': '기록',
  'tab.integration': 'API 연동',
  'callStart.title': 'API 연동',
  'callStart.intro': '통화·대화가 시작되면 고객사 API 에서 데이터를 불러와 AI 에이전트에게 컨텍스트로 전달하고, 상담원 화면에도 보여줘요. 백그라운드로 실행되어 실패해도 통화는 그대로 진행돼요.',
  'callStart.lookupTitle': '실시간 컨텍스트',
  'callStart.extractHintShort': '응답 경로 쓰는 법',
  'callStart.enabled': '통화 시작 시 조회 실행 (저장해야 반영)',
  'callStart.switchOn': 'ON',
  'callStart.switchOff': 'OFF',
  'callStart.section.connect': '접속',
  'callStart.section.connectDesc': '고객사 API 의 기본 주소와 인증 방식이에요.',
  'callStart.section.request': '요청',
  'callStart.section.requestDesc': '최대 3개 요청이 동시에 실행돼요. 다른 요청에서 꺼낸 값을 쓰는 요청은 그 요청이 끝난 뒤에 실행돼요.',
  'callStart.section.extract': '꺼낼 값',
  'callStart.section.extractDesc': '응답에서 꺼낼 값을 정해요. 전달 이름을 적은 값만 이 순서대로 AI 에이전트와 상담원 카드에 전달돼요.',
  'callStart.section.test': '최종 테스트',
  'callStart.section.testDesc': '저장하기 전에 지금 설정으로 모든 요청을 실행해 봐요. 아무것도 저장되지 않아요.',
  'callStart.baseUrl': '기본 주소 (https)',
  'callStart.authType': '인증 방식',
  'callStart.auth.none': '없음',
  'callStart.auth.header': '헤더',
  'callStart.auth.query': '쿼리 파라미터',
  'callStart.authDesc.none': '인증 정보 없이 호출해요.',
  'callStart.authDesc.header': '지정한 헤더에 시크릿을 그대로 실어요. 토큰 인증은 헤더 이름 "Authorization", 값 "Bearer <토큰>" 으로 넣고, x-api-key 같은 이름도 쓸 수 있어요.',
  'callStart.authDesc.query': '시크릿이 URL 에 실려 고객사 서버 접근 로그에 남을 수 있어요. 가능하면 헤더 방식을 쓰세요.',
  'callStart.headerName': '헤더 이름',
  'callStart.queryParam': '쿼리 파라미터 이름',
  'callStart.secret': '시크릿',
  'callStart.secretSet': '설정됨 {last4} · 비우면 유지',
  'callStart.secretHeaderHint': '`Bearer <토큰>` 또는 API 키 값',
  'callStart.timeout': '호출당 타임아웃 (ms)',
  'callStart.expand': '펼치기',
  'callStart.method': '메서드',
  'callStart.path': '경로',
  'callStart.pathEmpty': '(경로 없음)',
  'callStart.query': '쿼리 파라미터',
  'callStart.body': 'JSON 본문',
  'callStart.templateHelp': '발신번호는 {{caller.phone|형식}} (e164 · kr_local · kr_dash · digits), 다른 요청 값은 {{변수}} 로 넣어요. 경로에도 직접 입력할 수 있고, 쿼리·본문 값 칸은 입력하면 제안 목록이 떠요.',
  'callStart.col.var': '변수',
  'callStart.col.path': '응답 경로',
  'callStart.col.label': '전달 이름',
  'callStart.col.require': '필수 값',
  'callStart.col.memberName': '고객 이름',
  'callStart.extractLabelPlaceholder': '비우면 전달 안 함',
  'callStart.extractHint': '응답 경로는 점 경로 + 배열 번호, 예: users.0.userId. 필수 값: 그 값이 비면 "데이터 없음"으로 끝내고 그 값을 쓰는 요청은 부르지 않아요 (다시 누르면 해제). 고객 이름: AICC 가 이름이 빈 상담의 고객 이름을 이 값으로 채워요 (다시 누르면 해제).',
  'callStart.extractNone': '이 요청에서 꺼낸 값이 아직 없어요.',
  'callStart.addRow': '추가',
  'callStart.removeRow': '삭제',
  'callStart.testWarn': '고객사 API 를 실제로 호출합니다. 테스트용 번호를 쓰세요.',
  'callStart.testPhone': '발신번호',
  'callStart.testRun': '테스트 실행',
  'callStart.testFailed': '테스트를 실행하지 못했어요.',
  'callStart.needPhone': '테스트할 발신번호를 입력하세요.',
  'callStart.test.noSteps': '요청을 보내지 않았어요.',
  'callStart.test.values': '꺼낸 값',
  'callStart.test.card': '상담원 카드 미리보기',
  'callStart.test.cardEmpty': '보일 항목이 없어요 — 성공했고 전달 이름을 적은 값이 있을 때만 카드가 떠요.',
  'callStart.outcome.ok': '성공',
  'callStart.result.success': '성공',
  'callStart.result.failure': '실패',
  'callStart.result.other': '기타',
  'callStart.outcome.found': '값 전달',
  'callStart.outcome.not_found': '데이터 없음',
  'callStart.outcome.skipped': '발신번호 없음',
  'callStart.outcome.http_error': 'HTTP 오류',
  'callStart.outcome.mapping_error': '응답 형식 오류',
  'callStart.outcome.timeout': '시간 초과',
  'callStart.outcome.network': '네트워크 오류',
  'callStart.outcome.blocked_ssrf': '내부 주소 차단',
  'callStart.outcome.secret_error': '키 오류',
  'callStart.outcome.overloaded': '동시 조회 과다',
  'callStart.fixErrors': '표시된 칸을 확인해 주세요.',
  'callStart.err.required': '필수 항목이에요.',
  'callStart.err.https': 'https:// 로 시작해야 해요.',
  'callStart.err.new_host': '주소의 호스트를 바꾸면 키를 다시 입력해야 해요 — 저장된 키를 새 호스트로 보내지 않아요.',
  'callStart.err.range': '500–5000 사이로 입력하세요.',
  'callStart.err.path_format': "'/' 로 시작해야 하고 ?, #, 공백, '..' 은 쓸 수 없어요.",
  'callStart.err.template': '{{ }} 안에는 caller.phone|형식 또는 변수 이름만 쓸 수 있어요.',
  'callStart.err.unknown_var': '{{{name}}} 은(는) 다른 요청에서 꺼낸 값이 아니에요.',
  'callStart.err.key_format': '영문·숫자·_ . - 만 쓸 수 있어요.',
  'callStart.err.var_required': '변수 이름을 입력하세요.',
  'callStart.err.var_format': '영문 소문자로 시작하고 소문자·숫자·_ 만 쓸 수 있어요.',
  'callStart.err.var_dup': '{name} 은(는) 이미 꺼낸 변수예요.',
  'callStart.err.extract_required': '응답 경로를 입력하세요.',
  'callStart.err.extract_format': '점 경로로 입력하세요 (예: users.0.userId).',
  'callStart.deleted': '삭제했어요',
  'callStart.delete': '삭제',
  'callStart.deleteConfirm': '이 에이전트의 실시간 컨텍스트를 삭제할까요? 저장된 인증 정보도 함께 지워지고, 다음 통화부터 AI 에이전트와 상담원 화면에 전달되지 않아요.',
  'callStart.forbidden': '조직 소유자·관리자만 연동 설정을 관리할 수 있어요.',
  'callStart.lookupExists': '이 에이전트에는 이미 실시간 컨텍스트가 있어요. 페이지를 새로고침하세요.',
  'callStart.invalidInput': '입력값을 확인해 주세요.',
  'callStart.saveFailed': '저장하지 못했어요. 다시 시도해 주세요.',
  'callStart.loadError': '연동 설정을 불러오지 못했어요.',
  'callStart.trigger.callStart': '통화 시작 시',
  'callStart.addRequest': '요청 추가',
  'callStart.removeRequest': '요청 삭제',
  'callStart.usesValuesOf': '{names} 의 값을 씀',
  'callStart.testRequest': '테스트',
  'callStart.testRequestTip': '이 요청만 불러 응답을 봐요. 고객사 API 를 실제로 호출합니다.',
  'callStart.test.response': '응답 본문',
  'callStart.test.truncated': '…잘림',
  'callStart.test.context': '에이전트에게 전달될 컨텍스트',
  'callStart.test.contextEmpty': '전달할 컨텍스트가 없어요.',
  'callStart.outcome.failed': '실패',
  'callStart.runs.title': '최근 실행',
  'callStart.runs.desc': '통화가 시작될 때마다 이 조회가 어떻게 끝났는지 보여줘요 — 최근 {n}통화. 펼치면 요청별 결과가 보여요.',
  'callStart.runs.refresh': '새로고침',
  'callStart.runs.empty': '아직 실행 기록이 없어요. 통화가 시작될 때마다 여기에 쌓여요.',
  'callStart.runs.loadError': '최근 실행 기록을 불러오지 못했어요.',
  'callStart.runs.latencyTip': '가장 오래 걸린 요청 — 서로의 값을 쓰지 않는 요청은 동시에 돌아요.',
  'callStart.runs.revision': '설정 v{n}',
  'callStart.runs.openSession': '세션 보기',
  'callStart.runs.col.request': '요청',
  'callStart.runs.col.path': '경로',
  'callStart.runs.col.status': 'HTTP 상태',
  'callStart.runs.col.latency': '시간',
  'callStart.runs.col.outcome': '결과',
  'callStart.runs.col.error': '오류',
  'callStart.runs.openOrg': '조직 전체 보기 →',
  'nav.knowledge': '지식',
  'knowledge.title': '지식',
  'knowledge.desc': '조직에 연결된 지식(RAG)을 살펴보고 검색해요. 읽기 전용이에요.',
  'knowledge.loadFailed': '지식을 불러오지 못했어요.',
  'knowledge.empty': '조직에 연결된 지식이 아직 없어요.',
  'knowledge.select': '지식',
  'knowledge.notIngested': '이 지식에는 아직 적재된 내용이 없어요.',
  'knowledge.meta.documents': '문서 {n}건',
  'knowledge.meta.generation': '세대 {n}',
  'knowledge.meta.updated': '{when} 갱신',
  'knowledge.search.label': '지식 검색',
  'knowledge.search.placeholder': '찾고 싶은 내용을 적어 보세요',
  'knowledge.search.submit': '검색',
  'knowledge.search.searching': '검색 중…',
  'knowledge.search.clear': '지우기',
  'knowledge.search.hint': '뜻이 가까운 문서를 찾아요. 문서를 찾아보기 위한 검색이고, 통화 중 에이전트의 검색과는 방식이 달라요(에이전트는 대화를 보고 검색어를 새로 써요). 통화에서 찾는 결과와 같지 않아요.',
  'knowledge.search.off': '이 환경에는 검색이 설정돼 있지 않아요. 문서는 살펴볼 수 있어요.',
  'knowledge.search.unavailable': '지금은 이 지식을 검색할 수 없어요.',
  'knowledge.search.failed': '검색하지 못했어요. 다시 시도해 주세요.',
  'knowledge.search.score': '점수',
  'knowledge.search.results': '검색 결과 {n}건',
  'knowledge.search.backToList': '전체 문서 보기',
  'knowledge.search.noResults': '맞는 문서가 없어요.',
  'knowledge.docs.title': '문서',
  'knowledge.docs.all': '전체',
  'knowledge.docs.empty': '이 지식에는 문서가 없어요.',
  'knowledge.docs.loadFailed': '문서를 불러오지 못했어요.',
  'knowledge.docs.colDomain': '분류',
  'knowledge.docs.loadMore': '더 보기',
  'knowledge.doc.close': '닫기',
  'knowledge.doc.loadFailed': '이 문서를 불러오지 못했어요.',
  'knowledge.doc.aliases': '다른 이름',
  'knowledge.doc.id': '문서 ID',
  'knowledge.doc.copy': '복사',
  'knowledge.doc.copied': '복사됨',
  'integrations.eyebrow': '조직',
  'integrations.title': '연동',
  'integrations.desc': '모든 에이전트의 고객사 API 연동을 한곳에서 봐요. 설정은 각 에이전트의 API 연동 탭에서 바꿔요.',
  'integrations.forbidden': '조직 소유자·관리자만 연동 현황을 볼 수 있어요.',
  'integrations.on': '켜짐',
  'integrations.off': '꺼짐',
  'integrations.lookups.title': '에이전트별 설정 현황',
  'integrations.lookups.desc': '최근 실행 지표는 아래에 불러온 실행만 세요.',
  'integrations.lookups.empty': '아직 실시간 컨텍스트를 설정한 에이전트가 없어요. 에이전트의 API 연동 탭에서 설정해요.',
  'integrations.lookups.loadError': '연동 설정을 불러오지 못했어요.',
  'integrations.col.agent': '에이전트',
  'integrations.col.status': '상태',
  'integrations.col.host': '고객사 호스트',
  'integrations.col.revision': '설정 버전',
  'integrations.col.lastRun': '최근 실행',
  'integrations.col.failures': '실패 / 전체',
  'integrations.runs.title': '조직 전체 최근 실행',
  'integrations.runs.more': '더 보기',
  'integrations.runs.noMatch': '조건에 맞는 실행이 없어요.',
  'integrations.filter.agent': '에이전트',
  'integrations.filter.allAgents': '전체 에이전트',
  'integrations.filter.result': '결과',
  'integrations.filter.result.all': '전체',
  'integrations.filter.result.success': '성공',
  'integrations.filter.result.failure': '실패',
  'integrations.filter.result.other': '기타',
  'integrations.tab.phone': '전화번호',
  'integrations.tab.chat': '채팅',
  'chatAicc.title': 'AICC에 연결한 채팅 에이전트',
  'chatAicc.desc': 'AICC로 대화를 보낼 채팅 에이전트를 고릅니다. 연결한 에이전트의 대화만 AICC에 케이스로 들어가고 상담원에게 연결될 수 있습니다 — 위젯, 채팅 테스트, 공유 링크 어디서 열렸든 같습니다. 연결하지 않은 에이전트는 AICC로 아무것도 보내지 않습니다.',
  'chatAicc.notEnabled': '이 조직은 AICC를 쓰지 않아 채팅 에이전트를 연결할 수 없습니다. 먼저 운영팀에 AICC 개통을 요청하세요.',
  'chatAicc.empty': '아직 채팅 에이전트가 없습니다. 먼저 채팅 에이전트를 만드세요.',
  'chatAicc.col.agent': '채팅 에이전트',
  'chatAicc.col.status': 'AICC',
  'chatAicc.col.connect': '연결',
  'chatAicc.connected': '연결됨',
  'chatAicc.notConnected': '연결 안 함',
  'chatAicc.toggleLabel': '{name} 에이전트를 AICC에 연결',
  'chatAicc.note': '바꾸면 진행 중인 대화에도 바로 적용됩니다. 연결한 에이전트의 채팅 테스트도 AICC에 「채팅 테스트」로 표시되어 들어갑니다.',
  'chatAicc.error.load': '채팅 에이전트를 불러오지 못했습니다. 다시 시도해 주세요.',
  'chatAicc.error.save': '변경을 저장하지 못했습니다. 다시 시도해 주세요.',
  'chatAicc.error.notEnabled': '이 조직은 AICC를 쓰지 않아 연결할 수 없습니다.',
  'integrations.tab.sms': '문자',
  'sms.loadError': '문자 설정을 불러오지 못했어요.',
  'sms.senders.title': '발신번호',
  'sms.senders.empty': '배정된 문자 발신번호가 없어요. 운영팀에 요청하거나, 아래에서 내 NHN 계정을 연결해 번호를 추가하세요.',
  'sms.col.number': '번호',
  'sms.col.account': '구분',
  'sms.account.neona': '네오나 제공',
  'sms.account.customer': '내 NHN',
  'sms.default': '기본',
  'sms.default.label': '기본 발신번호',
  'sms.default.pick': '번호 선택',
  'sms.default.save': '저장',
  'sms.default.required': '기본 발신번호를 골라야 문자가 나가요.',
  'sms.sender.remove': '삭제',
  'sms.sender.addLabel': '번호 추가',
  'sms.sender.add': '추가',
  'sms.nhn.title': '내 NHN 계정',
  'sms.nhn.desc': '내 NHN 계약으로 보내는 문자는 NHN 에 직접 과금돼요.',
  'sms.nhn.appKey': 'appKey',
  'sms.nhn.secretKey': 'secretKey',
  'sms.nhn.save': '저장',
  'sms.nhn.verifiedAt': '확인 {time}',
  'sms.nhn.change': '키 바꾸기',
  'sms.nhn.disconnect': '연결 해제',
  'sms.nhn.disconnectTitle': '내 NHN 계정 연결을 해제할까요?',
  'sms.nhn.disconnectBody': '내 NHN 키와 내 NHN 번호({n}개)를 지워요. 그 번호로는 문자를 보낼 수 없게 돼요.',
  'sms.nhn.disconnectConfirm': '연결 해제',
  'sms.error.nhn_account_invalid': 'NHN 이 키를 거절했어요. appKey 와 secretKey 를 확인하세요.',
  'sms.error.nhn_account_missing': '내 NHN 계정을 먼저 연결하세요.',
  'sms.error.nhn_account_mismatch': '새 NHN 계정에 승인되지 않은 내 발신번호가 있어요. 그 번호를 먼저 지운 뒤 키를 바꾸세요.',
  'sms.error.nhn_unavailable': 'NHN 에 연결하지 못했어요. 설정은 바뀌지 않았어요. 잠시 뒤 다시 시도하세요.',
  'sms.error.sms_sender_not_approved': '내 NHN 계정에서 승인된 발신번호가 아니에요.',
  'sms.error.invalid_number': '올바른 전화번호를 입력하세요.',
  'sms.error.sms_sender_taken': '이미 등록된 발신번호예요.',
  'sms.error.sms_sender_managed': '네오나 제공 번호는 지울 수 없어요.',
  'sms.error.sms_sender_not_found': '이 조직의 문자 발신번호가 아니에요.',
  'sms.error.forbidden': '조직 소유자·관리자만 문자 설정을 바꿀 수 있어요.',
  'sms.error.generic': '문자 설정을 바꾸지 못했어요. 다시 시도하세요.',
  'integrations.tab.webhook': '웹훅',
  'integrations.webhook.title': '웹훅',
  'integrations.webhook.desc': '통화가 시작·종료·만료되거나 통화 분석이 끝나면 이 주소로 알려 드려요.',
  'integrations.webhook.events': '보내는 이벤트',
  'integrations.webhook.signature': '요청마다 X-Neona-Signature 헤더에 서명 비밀값으로 만든 서명(sha256 HMAC)을 실어 보내요.',
  'integrations.webhook.retry': '네트워크 오류나 5xx·408·429 로 답하면 약 7분 동안 5번 더 보내요(처음 전송 포함 최대 6번).',
  'integrations.webhook.urlLabel': '받을 주소',
  'integrations.webhook.urlHint': 'https:// 로 시작하는 공인 주소만 받아요.',
  'integrations.webhook.save': '저장',
  'integrations.webhook.saved': '저장했어요.',
  'integrations.webhook.secretLabel': '서명 비밀값',
  'integrations.webhook.secretSet': '있음',
  'integrations.webhook.secretNone': '아직 없어요. 주소를 처음 저장하면 만들어져요',
  'integrations.webhook.secretTitle': '서명 비밀값',
  'integrations.webhook.secretOnce': '이 값은 지금만 보여 드려요. 닫으면 다시 볼 수 없어요.',
  'integrations.webhook.secretDismiss': '닫기',
  'integrations.webhook.copy': '복사',
  'integrations.webhook.copied': '복사했어요',
  'integrations.webhook.copyFailed': '복사하지 못했어요. 값을 직접 선택해 복사해 주세요.',
  'integrations.webhook.manage': '관리',
  'integrations.webhook.rotate': '비밀값 다시 만들기',
  'integrations.webhook.rotateConfirm': '지금부터 보내는 웹훅은 새 비밀값으로 서명돼요. 이전 비밀값으로는 서명을 확인할 수 없게 돼요.',
  'integrations.webhook.rotateDo': '다시 만들기',
  'integrations.webhook.rotated': '서명 비밀값을 새로 만들었어요.',
  'integrations.webhook.delete': '웹훅 삭제',
  'integrations.webhook.deleteConfirm': '주소와 서명 비밀값을 함께 지워요. 지우면 통화 결과 알림을 더 보내지 않아요.',
  'integrations.webhook.deleteDo': '삭제',
  'integrations.webhook.deleted': '웹훅을 지웠어요.',
  'integrations.webhook.loadFailed': '웹훅 설정을 불러오지 못했어요.',
  'integrations.webhook.error.aicc': 'AICC 를 쓰는 조직은 지금 이 화면에서 웹훅을 바꿀 수 없어요.',
  'integrations.webhook.error.changed': '그사이 설정이 바뀌었어요. 새로 불러온 값을 확인하고 다시 시도해 주세요.',
  'integrations.webhook.error.invalidUrl': 'https:// 로 시작하는 올바른 주소를 넣어 주세요(200자 이하).',
  'integrations.webhook.error.notPublic': '인터넷에서 닿는 공인 주소만 받을 수 있어요. 주소를 다시 확인해 주세요.',
  'integrations.webhook.error.notConfigured': '받을 주소를 먼저 저장해 주세요.',
  'integrations.webhook.error.forbidden': '조직 소유자·관리자만 웹훅을 바꿀 수 있어요.',
  'integrations.webhook.error.generic': '처리하지 못했어요. 잠시 뒤 다시 시도해 주세요.',
  'phone.preflight.title': '개통 점검',
  'phone.preflight.ready': '전화를 받을 준비가 됐어요',
  'phone.preflight.notReady': '남은 단계가 있어요',
  'phone.preflight.done': '완료',
  'phone.preflight.todo': '할 일',
  'phone.preflight.agent': '음성 에이전트 만들기',
  'phone.preflight.agent.todo': '전화를 받을 활성 음성 에이전트가 필요해요.',
  'phone.preflight.agent.link': '에이전트 만들기',
  'phone.preflight.number': '번호 받기',
  'phone.preflight.number.todo': '아직 배정된 번호가 없어요. 운영팀에 요청하세요.',
  'phone.preflight.receiving': '받을 에이전트 지정',
  'phone.preflight.receiving.todo': '받을 에이전트가 없는 번호가 있어요 — 아래에서 골라 주세요: {numbers}',
  'phone.preflight.cap': '받는 에이전트의 통화 시간',
  'phone.preflight.cap.todo': 'AI 혼자 응대하는 통화는 에이전트의 「세션 최대 지속 시간」에서 안내 없이 끊겨요 — {min}분 이상으로 늘려 주세요(상담원이 연결되면 이 상한은 풀리고, 전화는 4시간에 끊겨요): {numbers}',
  'phone.preflight.line': '회선 등록',
  'phone.preflight.line.todo': '회선 등록이 끝나지 않은 번호가 있어요 — 운영팀에 알려 주세요: {numbers}',
  'phone.preflight.outbound': '발신 회선',
  'phone.preflight.outbound.todo': '아직 전화를 걸 수 있는 번호가 없어요. 운영팀에 발신 회선을 요청하세요.',
  'phone.preflight.test_call': '시험 통화',
  'phone.preflight.test_call.todo': '최근에 연결된 통화가 없어요 — 전화를 걸어 확인해 보세요: {numbers}',
  'phone.preflight.optional': '선택',
  'phone.preflight.unknown': '확인 못 함',
  'phone.preflight.unknown.desc': 'AICC 에 연결하지 못해 확인하지 못했어요.',
  'phone.preflight.operators': 'AICC 상담원 계정',
  'phone.preflight.operators.todo': 'AICC 에 상담원 계정이 아직 없어요 — 「AICC 열기」로 들어가면 관리자 계정이 생기고, 설정 › 상담원에서 동료를 초대할 수 있어요.',
  'phone.preflight.operators.now': '지금 응대 가능 {n}명',
  'phone.preflight.operators.none': '지금은 상담원 연결 통화가 울려도 받을 사람이 없어 콜백으로 넘어가요. AICC 에서 근무 상태를 「응대 가능」으로 바꾸면 받아요.',
  'phone.preflight.webhook': '상담 결과를 고객사 시스템으로 보내기(웹훅)',
  'phone.preflight.webhook.optional': '연동할 때만 해요 — AICC 설정 › 웹훅 연동에서 켜요.',
  'phone.preflight.aiccHint': '상담원 연결(AICC)을 쓰고 있어요 — 상담원이 AICC 에 접속해 있어야 인계를 받아요. 상담원이 AICC 에서 고객에게 전화를 걸 수도 있어요.',
  'phone.preflight.aiccOpen': 'AICC 열기',
  'phone.title': '전화번호',
  'phone.desc': '번호마다 전화를 받을 에이전트를 골라요. 대기 에이전트는 연결만 해 두고 클릭 한 번으로 수신을 바꿀 수 있어요. 새 번호 배정은 운영팀에 요청하세요.',
  'phone.empty': '아직 조직에 배정된 번호가 없어요. 번호 배정은 운영팀에 요청하세요.',
  'phone.loadError': '전화번호를 불러오지 못했어요.',
  'phone.col.number': '번호',
  'phone.col.receiving': '수신 에이전트',
  'phone.col.standby': '대기',
  'phone.col.trunk': '회선',
  'phone.col.lastInbound': '최근 인입',
  'phone.col.time': '시각',
  'phone.col.result': '결과',
  'phone.col.decidedBy': '정한 곳',
  'phone.col.agent': '에이전트',
  'phone.receivingNone': '없음',
  'phone.receivingElsewhere': '{number} 수신 중',
  'phone.standbyAdd': '+ 대기 추가',
  'phone.standbyRemove': '대기에서 빼기',
  'phone.standbyPromote': '수신으로 전환',
  'phone.trunk.registered': '등록됨',
  'phone.trunk.unregistered': '미등록',
  'phone.trunk.error': '오류',
  'phone.trunkRecheck': '다시 확인',
  'phone.trunkError.last_number': '회선의 마지막 번호라 해제하지 않았어요(번호가 비면 회선이 모든 번호를 받아요). 운영팀이 정리해요.',
  'phone.trunkError.trunk_not_found': '이 번호의 회선을 찾지 못했어요. 운영팀에 문의하세요.',
  'phone.trunkError.sync_failed': '회선 동기화에 실패했어요. 잠시 후 다시 확인하세요.',
  'phone.trunkError.busy': '이 회선에 다른 변경이 진행 중이에요. 잠시 후 다시 확인하세요.',
  'phone.trunkError.other': '회선에 문제가 있어요. 다시 확인하거나 운영팀에 문의하세요.',
  'phone.aiccPriority': 'AICC 라우팅 우선 적용 중',
  'phone.history': '최근 인입',
  'phone.historyEmpty': '아직 인입이 없어요.',
  'phone.result.routed': '연결됨',
  'phone.result.no_agent': '받을 에이전트 없음',
  'phone.result.rejected': '거절됨',
  'phone.result.limited': '한도 초과',
  'phone.result.error': '실패',
  'phone.result.other': '기타',
  'phone.decided.aicc': 'AICC 라우팅',
  'phone.decided.number': '번호 설정',
  'phone.decided.agent': '에이전트 설정',
  'phone.confirm.receiveTitle': '수신 에이전트를 바꿀까요?',
  'phone.confirm.receiveBody': '지금부터 {number} 로 오는 전화는 {name} 이(가) 받아요.',
  'phone.confirm.keepPrevious': '{name} 은(는) 대기로 남기기',
  'phone.confirm.receive': '지금 전환',
  'phone.confirm.releaseTitle': '이 번호의 수신을 해제할까요?',
  'phone.confirm.releaseBody': '{name} 이(가) 더 이상 {number} 를 받지 않아요. 에이전트를 고르기 전까지 이 번호로 오는 전화는 받지 않아요.',
  'phone.confirm.release': '수신 해제',
  'phone.error.number_changed': '방금 다른 사람이 이 번호를 바꿨어요. 새로고침 후 다시 시도하세요.',
  'phone.error.agent_busy': '이 에이전트는 이미 다른 번호를 받고 있어요. 그 번호에서 먼저 해제하세요.',
  'phone.error.agent_invalid': '이 조직의 켜진 음성 에이전트만 연결할 수 있어요.',
  'phone.error.already_receiving': '이미 이 번호를 받고 있는 에이전트예요.',
  'phone.error.number_not_found': '조직에 배정된 번호가 아니에요.',
  'phone.error.forbidden': '조직 소유자·관리자만 전화번호를 바꿀 수 있어요.',
  'phone.error.generic': '전화번호를 바꾸지 못했어요. 다시 시도하세요.',
  'agent.phoneTakeTitle': '{name} 이(가) 이 번호를 받고 있어요',
  'agent.phoneTakeBody': '{number} 를 지금 가져오거나, 대기로 추가해 두고 나중에 전환할 수 있어요.',
  'agent.phoneTakeReleaseOld': '이 에이전트가 받던 {number} 는 해제돼요.',
  'agent.phoneTake': '가져오기 (지금 수신 전환)',
  'agent.phoneStandby': '대기로 추가',
  'agent.phoneTaken': '이제 이 에이전트가 {number} 를 받아요.',
  'agent.phoneStandbyAdded': '{number} 의 대기로 추가했어요.',
  'agent.phoneNumberReadonly': '조직 소유자·관리자만 전화번호를 바꿀 수 있어요.',
  'callStart.err.self_ref': '{{{name}}} 은(는) 이 요청이 꺼내는 값이라 여기서 쓸 수 없어요.',
  'callStart.err.cycle': '{{{name}}} 을(를) 쓰면 요청끼리 서로의 값을 기다리게 돼요.',
  'deploy.channelWidget': '웹사이트 위젯',
  'deploy.badgeOneLine': '스크립트 한 줄',
  'deploy.badgeDev': '개발자용',
  'deploy.intro': '에이전트를 사용자에게 내보내는 방법 — 링크 공유(노코드), 위젯 임베드(스크립트 한 줄), REST API 연동(개발자) 순서로 준비되어 있어요.',

  // Agent detail
  'agent.totalSessions': '전체 세션',
  'agent.allTime': '전체 기간',
  'agent.avgDuration': '평균 시간',
  'agent.perSession': '세션당',
  'agent.dropRate': '이탈률',
  'agent.last30d': '최근 30일',
  'agent.totalMinutes': '전체 시간(분)',
  'agent.notFound': '에이전트를 찾을 수 없습니다.',
  'agent.loadError': '에이전트를 불러올 수 없습니다. 다시 시도해 주세요.',
  'agent.updateFailed': '업데이트 실패',
  'agent.actionFailed': '처리하지 못했습니다 — 다시 시도해 주세요',
  'agent.settings': '설정',
  'agent.openSettings': '설정 탭 열기',
  'agent.integration': '연동',
  'agent.integrationDesc': '단일 API 호출로 서버에서 세션을 시작합니다.',
  'agent.viewApiDocs': 'API 문서 보기',
  'agent.manageApiKeys': 'API 키 관리',
  'agent.conversationConfig': '대화 설정',
  'agent.systemPrompt': '시스템 프롬프트',
  'agent.systemPromptHint': '에이전트의 성격과 행동을 정의합니다. {{variable}}로 런타임 치환이 가능합니다.',
  'agent.silenceNudge': '사용자가 말이 없으면 먼저 말 걸기',
  'agent.silenceNudgeHint': '침묵이 이어지면 에이전트가 무한정 기다리지 않고 먼저 대화를 다시 엽니다. 튜터링·단계형 안내에 유용합니다. 상담원 인계가 있는 에이전트는 켜지 마세요 — 상담원을 기다리는 대기 구간에서도 말을 겁니다.',
  'agent.silenceNudgeVoiceOnly': '음성 에이전트에서만 쓸 수 있습니다. 채팅 에이전트는 방문자가 메시지를 보내야 답하므로 여기서는 켤 수 없습니다.',
  'agent.silenceNudgeDelay': '회차별 침묵 시간 (초)',
  'agent.silenceNudgeDelayHint': '쉼표로 구분해 회차마다 적용됩니다 — \"3, 5, 7\"이면 3초 뒤 1차, 다시 5초 뒤 2차, 다시 7초 뒤 3차 후 조용히 기다립니다(자동 종료를 켜면 그 뒤 끊습니다). 최대 5개, 각 1~60초, 마지막 발화가 끝난 시점부터 잽니다. 비워 두면 플랫폼 기본값.',
  'agent.silenceNudgeDelayPlaceholder': '플랫폼 기본값 (예: 3, 5, 7)',
  'agent.silenceAutoEnd': '응답이 없으면 통화 자동 종료',
  'agent.silenceAutoEndHint': '마지막 되묻기에도 응답이 없으면 종료 안내를 말하고 통화를 끊습니다. 상담원 인계가 있는 에이전트는 켜지 마세요.',
  'agent.silenceAutoEndDelay': '마지막 되묻기 후 종료까지 (초)',
  'agent.silenceAutoEndDelayHint': '1~120초, 마지막 되묻기 발화가 끝난 시점부터 잽니다. 비워 두면 플랫폼 기본값(10초).',
  'agent.silenceAutoEndDelayPlaceholder': '플랫폼 기본값 (10)',
  'agent.silenceAutoEndMessage': '종료 안내 문구',
  'agent.silenceAutoEndMessageHint': '통화를 끊기 직전에 말합니다. 비워 두면 에이전트 언어의 플랫폼 기본 문구.',
  'agent.retentionTitle': '통화 기록 보관',
  'agent.retentionHint': '통화가 끝난 뒤 이 에이전트가 남기는 것. 기본은 모두 켜짐입니다. 공개 데모 에이전트는 개인정보처리방침에 맞춰 음성·트레이스를 끄세요. 전사본만 있어도 데모 리포트는 만들어집니다.',
  'agent.retentionVoiceOnly': '음성 에이전트에서만 쓰는 설정입니다. 채팅은 음성 녹음과 LLM 트레이스를 남기지 않고, 대화 내용은 항상 저장합니다.',
  'agent.retentionAudio': '통화 음성 녹음',
  'agent.retentionAudioHint': '통화 종료 시 이용자·에이전트·상담원(인계 시)·스테레오 WAV 를 스토리지에 저장합니다.',
  'agent.retentionTranscript': '대화 전사본 저장',
  'agent.retentionTranscriptHint': '대화 텍스트를 세션에 저장합니다. 끄면 콘솔 대화 기록·수동 평가·데모 리포트도 없어집니다. 상담원 인계가 있는 에이전트는 끄지 마세요 — 상담 케이스가 이 전사로 만들어집니다.',
  'agent.retentionTrace': 'LLM 트레이스 전송',
  'agent.retentionTraceHint': '턴별 LLM·STT·TTS 트레이스(텍스트 전문 포함)를 관측 도구로 보냅니다. 지연·품질 디버깅용입니다.',
  'agent.silenceAutoEndMessagePlaceholder': '예: 응답이 없어 통화를 종료할게요. 필요하시면 다시 연락해 주세요.',
  'agent.keypadInput': '키패드 입력 받기',
  'agent.keypadInputHint': '전화 통화 중 고객이 누른 숫자를 AI 가 받아 대화에 씁니다. 우물 정(#)을 누르거나, 최대 자리 수를 채우거나, 마지막으로 누르고 4초가 지나면 입력을 마칩니다. 별표(*)를 누르면 처음부터 다시 받습니다.',
  'agent.keypadMaxDigits': '최대 자리 수',
  'agent.keypadMaxDigitsHint': '한 번에 받을 최대 자리 수입니다(1~20). 비우면 20자리까지 받습니다.',
  'agent.keypadMaxDigitsPlaceholder': '플랫폼 기본값 (20)',
  'agent.keypadInputWarning': '주민등록번호·카드번호·비밀번호처럼 민감한 번호는 키패드로 받지 마세요. 누른 숫자는 통화 기록(전사)과 상담원 인계 요약에 그대로 남습니다.',
  'agent.voiceTts': '음성 / TTS',
  'agent.noVoice': '음성이 선택되지 않았습니다',
  'agent.changeVoice': '음성 변경',
  'agent.voiceSpeed': '말하기 속도',
  'agent.voiceSpeedHint': '에이전트 음성의 전체 재생 속도입니다. 0.5~2.0, 기본값 1.0.',
  'agent.voiceLoudness': '출력 음량',
  'agent.voiceLoudnessHint': '목표 라우드니스(LUFS, −70~0). 값이 클수록 크게 들립니다. 끄면 정규화 없이 원본 그대로 나갑니다.',
  'agent.backgroundAudio': '배경 앰비언스',
  'agent.backgroundAudioOff': '사용 안 함',
  'agent.backgroundAudioLoading': '클립 불러오는 중…',
  'agent.backgroundAudioInactive': '비활성',
  'agent.backgroundAudioPreview': '배경 앰비언스 미리듣기',
  'agent.backgroundAudioVolume': '배경 앰비언스 볼륨',
  'agent.backgroundAudioHint': '통화에 은은한 배경음(사무실 소음 등)을 깔아 침묵이 끊긴 전화처럼 느껴지지 않게 합니다. 100%를 넘기면 원본을 증폭합니다(협대역 전화에서 유용). 너무 높이면 소리가 깨질 수 있습니다.',
  'agent.backgroundAudioDucking': '에이전트 발화 중 살짝 줄이기 (덕킹)',
  'agent.backgroundAudioDuckingHint': '덕킹을 켜면 에이전트가 말하는 동안 배경음을 부드럽게 낮추고, 말 사이에는 되돌립니다.',
  'agent.chooseVoice': '음성 선택',
  'agent.general': '일반',
  'agent.name': '이름',
  'agent.description': '설명',
  'agent.descPlaceholder': '이 에이전트는 무엇을 하나요?',
  'agent.language': '언어',
  'agent.phoneNumber': '인입 전화번호',
  'agent.phoneNumberNone': '연결 안 함',
  'agent.phoneNumberInUse': '{name} 사용 중',
  'agent.phoneNumberEmpty': '조직에 할당된 번호가 없습니다 — 번호 연결은 문의로 요청해주세요.',
  'agent.phoneNumberDetached': '조직 할당이 해제된 번호',
  'agent.agentType': '에이전트 유형',
  'agent.maxSessionDuration': '세션 최대 지속 시간 (분)',
  'agent.maxSessionDurationHint': '세션당 강제 종료 기준. AI 혼자 응대하는 동안 이 시간이 지나면 안내 없이 통화가 끝납니다(상담원이 연결되면 이 상한은 풀리고, 전화는 4시간에 끊깁니다). 1–240(4시간). 기본값 10.',
  'agent.sessionExpiry': '세션 만료 시간 (분)',
  'agent.sessionExpiryHint': '생성됐지만 아직 연결되지 않은 세션과 입장 토큰이 만료되기까지 유효한 시간입니다. 1–30분. 기본값 5.',
  'agent.chatMaxTurns': '대화당 메시지 상한',
  'agent.chatMaxTurnsHint': '한 대화에서 AI가 답하는 방문자 메시지 수입니다. 넘으면 입력창이 잠깁니다. 1–200. 비워 두면 기본값(30). 상담원 연결 중·응대 중에 보낸 메시지는 세지 않습니다.',
  'agent.chatContextMessages': 'AI가 기억하는 메시지 수',
  'agent.chatContextMessagesHint': '매 응답마다 AI에게 보내는 최근 메시지 수입니다. 그보다 오래된 메시지는 잊습니다. 1–200. 비워 두면 기본값(40). 값이 클수록 응답당 비용이 늘어납니다.',
  'agent.agentId': '에이전트 ID',
  'agent.agentIdHint': 'API 호출에서 이 에이전트를 참조할 때 사용하는 식별자입니다.',
  'agent.dangerZone': '위험 구역',
  'agent.dangerDesc': '이 에이전트를 영구적으로 비활성화합니다. 활성 세션이 종료됩니다.',
  'agent.deactivateAgent': '에이전트 비활성화',
  'agent.areYouSure': '정말로 진행하시겠습니까?',
  'agent.confirmDeactivate': '비활성화 확인',

  // Version history
  'version.justNow': '방금',
  'version.minutesAgo': '{n}분 전',
  'version.hoursAgo': '{n}시간 전',
  'version.daysAgo': '{n}일 전',
  'version.history': '버전 기록',
  'version.count': '개 버전',
  'version.loading': '버전 로딩 중…',
  'version.empty': '아직 버전 기록이 없습니다. 변경사항을 저장하면 자동으로 버전이 생성됩니다.',
  'version.label': '버전',
  'version.current': '현재',
  'version.deleteConfirm': '이 버전을 삭제하시겠습니까?',

  // UI common
  'ui.copy': '복사',
  'ui.copied': '복사됨!',

  // Command palette
  'cmd.goTo': '이동',
  'cmd.create': '생성',
  'cmd.searchPlaceholder': '명령어 입력 또는 검색…',
  'cmd.noResults': '결과 없음',

  // Language options
  'lang.koKR': '한국어 (ko-KR)',
  'lang.enUS': '영어 (en-US)',
  'lang.enGB': '영어 (en-GB)',
  'lang.jaJP': '일본어 (ja-JP)',

  // New agent extras
  'newAgent.createFailed': '생성 실패',
  'newAgent.namePlaceholder': '예: 영어 튜터',
  'newAgent.reviewName': '이름',
  'newAgent.reviewType': '유형',
  'newAgent.reviewLang': '언어',
  'newAgent.reviewVoice': '음성',
  'newAgent.untitled': '이름 없는 에이전트',
  'newAgent.ready': '준비됨',
  'newAgent.previewAgent': '에이전트',
  'newAgent.previewCaller': '발화자',
  'newAgent.previewGreeting': '안녕하세요! 무엇을 도와드릴까요?',

  // Agents page
  'agents.sort': '정렬',
  'agents.sortUpdated': '최근 수정',
  'agents.sortName': '가나다순',
  'agents.loadError': '에이전트를 불러오지 못했습니다. 다시 시도해 주세요.',
  'agents.createNew': '직접 만들기',
  'agents.autoGenerate': 'AI 자동 생성 (Beta)',
  'agents.createAnother': '에이전트 추가 생성',
  'agents.filterAll': '전체',
  'agents.filterPublished': '배포됨',
  'agents.filterDraft': '초안',
  'agents.publishedLive': '{n} 라이브',
  'agents.colAgent': '에이전트',
  'agents.colId': 'ID',
  'agents.colLanguage': '언어',
  'agents.colCreated': '생성일',

  // Sessions page
  'sessions.colAgent': '에이전트',
  'sessions.colParticipant': '참가자',
  'sessions.colStatus': '상태',
  'sessions.colDuration': '시간',
  'sessions.colTime': '시각',
  'sessions.qualityScore': '품질 점수',
  'sessions.loadMore': '더 보기',
  'sessions.channelVoice': '음성',
  'sessions.filterAll': '전체',
  'sessions.statusCreated': '생성됨',
  'sessions.statusConnected': '연결됨',
  'sessions.statusActive': '진행 중',
  'sessions.statusEnded': '종료됨',
  'sessions.statusExpired': '만료됨',
  'sessions.statusFailed': '실패',
  'sessions.channelChat': '채팅',
  'sessions.colSource': '유입 경로',
  'sessions.colTurns': '턴',
  'sessions.chatSourceWidget': '위젯',
  'sessions.chatSourceShare': '공유 링크',
  'sessions.chatSourceConsole': '테스트',
  'sessions.chatHandoff': '상담원 연결',
  'sessions.chatHandoffRequested': '상담원 연결 요청',
  'sessions.chatCallback': '콜백 요청',
  'sessions.noChatSessions': '아직 채팅 세션이 없습니다.',
  'sessions.chatLoadFailed': '채팅 세션을 불러오지 못했습니다.',
  'chatDetail.eyebrow': '채팅 세션',
  'chatDetail.back': '세션 목록으로',
  'chatDetail.loadFailed': '채팅 세션을 불러오지 못했습니다.',
  'chatDetail.empty': '이 세션에는 메시지가 없습니다.',
  'chatDetail.turns': '턴',
  'chatDetail.statusActive': '진행 중',
  'chatDetail.statusEnded': '종료됨',
  'chatDetail.contact': '연락처',
  'chatDetail.roleCustomer': '방문자',
  'chatDetail.roleAgent': '에이전트',
  'chatDetail.roleOperator': '상담원',
  'chatDetail.endUserRequest': '방문자 종료',
  'chatDetail.endIdle': '무응답 종료',
  'chatDetail.endTurnLimit': '턴 상한 도달',
  'chatDetail.endOperator': '상담원 종료',
  'chatDetail.endUnknown': '종료',
  'sessions.callType': '통화 주제 (통화 기록에서)',
  'sessions.loadError': '세션을 불러오지 못했습니다.',

  // Session summary
  'sessionSummary.status': '상태',
  'sessionSummary.duration': '시간',
  'sessionSummary.started': '시작',
  'sessionSummary.ended': '종료',
  'sessionSummary.participant': '참가자',
  'sessionSummary.sessionId': '세션 ID',
  'sessionSummary.transcript': '대화 기록',
  'sessionSummary.updating': '(업데이트 중…)',
  'sessionSummary.noTranscript': '대화 기록이 없습니다.',
  'sessionSummary.startAgain': '다시 시작',
  'sessionSummary.loading': '로딩 중…',
  'sessionSummary.loadError': '세션을 불러오지 못했습니다.',
  'agent.saved': '저장되었습니다 ✓',
  'agent.evalCriteria': '평가 기준',
  'agent.evalTabDesc': '통화 후 품질 평가에 대한 설정이에요. 라이브 대화에는 영향을 주지 않아요.',
  'agent.evalDefaultTitle': '기본 평가 기준 (기준을 비워두면 이대로 작동)',
  'agent.evalDefault1': '목적 달성도(0-100): 시스템 프롬프트가 정의한 목적이 결과로 달성됐는지 — 노력·규칙 준수가 아닌 결과 기준. 미해결·불만 종료면 규칙을 다 지켰어도 낮게 평가돼요.',
  'agent.evalDefault2': '정확성·규정 준수(0-100): 안내가 사실에 맞고 시스템 프롬프트의 정책을 지켰는지.',
  'agent.evalDefault3': '잘한 점과 안내 이슈를 인용·심각도(높음/보통/낮음)와 함께 기록하고, 목적 미달성 시 갭 출처(프롬프트 커버리지/에이전트 실행)를 구분해요.',
  'agent.evalDefault4': '종합(0-100): 두 축을 반영하며, 목적이 미달성이면 60을 넘지 않아요.',

  'agent.evalCriteriaPlaceholder': '예: 이 에이전트의 목적은 예약 완료입니다. 예약이 이뤄지지 않으면 목적 달성도를 50 이하로 평가하세요. 요금 오안내는 심각도 높음으로 판정하세요.',
  'agent.evalCriteriaHint': '통화 후 품질 평가에 쓰는 에이전트별 기준입니다. 비워두면 기본 평가 기준만 사용해요.',
  'sessionSummary.info': '세션 정보',
  'sessionSummary.liveContext': '실시간 컨텍스트',
  'sessionSummary.liveContextStatus': '조회 결과',
  'sessionSummary.liveContextFetched': '조회 시각',
  'sessionSummary.liveContextItems': 'AI 에게 전달된 항목',
  'sessionSummary.liveContextNotFound': "AI 에게 '조회한 정보가 없음'이 전달됐어요.",
  'sessionSummary.liveContextEmpty': 'AI 에게 전달된 항목이 없어요.',
  'sessionSummary.extraction': '통화 기록',
  'sessionSummary.extracting': '통화 기록 생성 중… 통화 종료 후 잠시 뒤에 표시돼요.',
  'sessionSummary.noExtraction': '이 세션에는 통화 기록이 없습니다 — 기록이 생성되지 않았거나 완료되지 못했어요.',
  'sessionSummary.keyPoints': '핵심 내용',
  'sessionSummary.extractedBy': '추출 모델',
  'sessionSummary.res.resolved': '해결됨',
  'sessionSummary.res.unresolved': '미해결',
  'sessionSummary.res.escalated': '상담원 이관',
  'sessionSummary.res.abandoned': '중단',
  'sessionSummary.sent.positive': '긍정',
  'sessionSummary.sent.neutral': '중립',
  'sessionSummary.sent.negative': '부정',
  'sessionSummary.evaluation': '품질 평가',
  'sessionSummary.noEvaluation': '이 세션에는 품질 평가가 없습니다 — 평가가 실행되지 않았거나 완료되지 못했어요.',
  'sessionSummary.evaluating': '평가 생성 중… 통화 종료 후 잠시 뒤에 표시돼요.',
  'sessionSummary.evaluatingNow': '평가 실행 중… 최대 1분 정도 걸릴 수 있어요.',
  'sessionSummary.runEvaluation': '평가 실행',
  'sessionSummary.reEvaluate': '다시 평가',
  'sessionSummary.evaluateFailed': '평가에 실패했어요 — 다시 시도할 수 있어요.',
  'sessionSummary.overallScore': '종합',
  'sessionSummary.goalAchievement': '목적 달성도',
  'sessionSummary.gap.prompt_coverage': '갭: 프롬프트 커버리지',
  'sessionSummary.gap.agent_execution': '갭: 에이전트 실행',
  'sessionSummary.accuracy': '정확성·규정 준수',
  'sessionSummary.strengths': '잘한 점',
  'sessionSummary.good': '잘함',
  'sessionSummary.guidanceIssues': '안내 이슈',
  'sessionSummary.evaluatedBy': '평가 모델',
  'sessionSummary.criteria': '평가요소',
  'sessionSummary.criteriaFailed': '판정 실패',
  'sessionSummary.criteriaPending': '평가요소 판정 중… 통화 종료 후 잠시 뒤에 표시돼요.',
  'sessionSummary.criteriaNone': '이 세션에는 평가요소 판정 결과가 없어요 — 판정이 실행되지 않았거나 완료되지 못했어요.',
  'sessionSummary.criteriaFailedReason': '판정 결과를 받지 못했어요',
  'sessionSummary.criteriaYes': '예',
  'sessionSummary.criteriaNo': '아니오',
  'sessionSummary.criteriaUnknown': "판단 불가",
  'sessionSummary.criteriaStandard': "기본",
  'evaluation.loading': "평가 설정을 불러오는 중…",
  'evaluation.loadError': "평가 설정을 불러오지 못했어요.",
  'evaluation.standardsUnavailable': "평가 서비스가 응답하지 않아 지금은 기본 평가를 보여 줄 수 없어요.",
  'evaluation.standardBadge': "기본",
  'evaluation.copyStandard': "복제",
  'evaluation.copyName': "{name} 사본",
  'evaluation.languageLabel': "판정 이유 언어",
  'evaluation.languageHint': "결과와 함께 보이는 판정 이유를 이 언어로 써요. 판정 기준은 바뀌지 않아요.",
  'evaluation.language.auto': "통화 언어 따라가기",
  'evaluation.language.ko-KR': "한국어",
  'evaluation.language.en-US': "영어",
  'evaluation.language.ja-JP': "일본어",
  'evaluation.standard.success': "성공",
  'evaluation.standard.success.desc': "고객 용건을 이 통화에서 끝까지 처리했는지",
  'evaluation.standard.task_completion': "용건 해결",
  'evaluation.standard.task_completion.desc': "고객 용건을 이 통화에서 끝까지 처리했는지",
  'evaluation.standard.user_sentiment': "고객 감정",
  'evaluation.standard.user_sentiment.desc': "통화가 끝날 때 고객이 어떤 감정이었는지",
  'evaluation.value.resolved': "해결",
  'evaluation.value.unresolved': "미해결",
  'evaluation.value.positive': "긍정",
  'evaluation.value.neutral': "중립",
  'evaluation.value.negative': "부정",
  'evaluation.value.unknown': "판단 불가",
  'evaluation.itemsEmpty': "아직 붙인 평가 항목이 없어요.",
  'evaluation.type.categorical': "선택지 중 하나",
  'evaluation.type.boolean': "예 / 아니오",
  'evaluation.type.numeric': "점수 (0~100)",
  'evaluation.edit': "수정",
  'evaluation.detach': "빼기",
  'evaluation.adminItem': "Neona 설정",
  'evaluation.standardViewNote': "모든 에이전트에 붙는 평가라 뺄 수 없어요. 판정 기준은 이 에이전트에 맞게 고칠 수 있고, 결과는 예 · 아니오 중 하나예요.",
  'evaluation.resetInstruction': "기본으로 되돌리기",
  'evaluation.viewNamed': "{name} 보기",
  'evaluation.languageOption': "판정 이유: {language}",
  'evaluation.view': "보기",
  'evaluation.viewTitle': "평가 보기",
  'evaluation.addEmpty': "아직 만든 평가가 없어요. 위에서 새로 만들어 보세요.",
  'evaluation.codeEditNote': "통화 기록으로 자동 계산하는 항목이라 이름만 바꿀 수 있어요.",
  'evaluation.search': "이름이나 기준으로 찾기",
  'evaluation.searchEmpty': "맞는 평가가 없어요.",
  'evaluation.addOne': "추가",
  'evaluation.addNamed': "{name} 추가",
  'evaluation.added': "추가됨 ✓",
  'evaluation.removeOne': "빼기",
  'evaluation.removeNamed': "{name} 빼기",
  'evaluation.createSection': "새로 만들기",
  'evaluation.createNew': "새 평가 만들기",
  'evaluation.createNewDesc': "직접 쓰거나 템플릿을 불러와요.",
  'evaluation.loadTemplate': "템플릿 불러오기",
  'evaluation.templateApplied': "‘{name}’ 템플릿으로 채웠어요.",
  'evaluation.undo': "되돌리기",
  'evaluation.codeDetail': "통화 기록으로 자동 계산해요.",
  'evaluation.notAttached': "이 에이전트에는 아직 추가되지 않았어요.",
  'evaluation.detached': "‘{name}’을(를) 이 에이전트에서 뺐어요.",
  'evaluation.copied': "사본을 추가했어요. 원본 ‘{name}’도 함께 판정돼요.",
  'evaluation.detachOriginal': "원본 빼기",
  'evaluation.existingSection': "기존 평가 가져오기",
  'evaluation.value.true': "예",
  'evaluation.moreValues': "외 {n}개",
  'evaluation.value.false': "아니오",
  'evaluation.title': "통화 후 평가",
  'evaluation.desc': "통화가 끝나면 아래 항목으로 통화를 평가해요.",
  'evaluation.saving': "저장하는 중…",
  'evaluation.more': "{name} 더 보기",
  'evaluation.add': "+ 평가 추가",
  'evaluation.addTitle': "평가 추가",
  'evaluation.codeInstruction': "통화 기록으로 자동 계산해요 ({template}).",
  'evaluation.kind.code': "자동 계산",
  'evaluation.saved': "저장했어요",
  'evaluation.readOnly': "평가 설정은 조직 owner · admin 만 바꿀 수 있어요.",
  'evaluation.error.forbidden': "평가 설정은 조직 owner · admin 만 바꿀 수 있어요.",
  'evaluation.error.unavailable': "평가 서비스가 응답하지 않아요. 잠시 뒤 다시 시도해 주세요.",
  'evaluation.error.generic': "저장하지 못했어요. 다시 시도해 주세요.",
  'evaluation.deleteTitle': "평가 항목을 지울까요?",
  'evaluation.deleteDesc': "‘{name}’ 이(가) 붙어 있는 모든 에이전트에서 빠져요. 지난 판정 결과는 남아요.",
  'evaluation.delete': "삭제",
  'evaluation.editTitle': "평가 수정",
  'evaluation.newTitle': "새 평가",
  'evaluation.editSharedNote': "이 항목을 붙인 모든 에이전트에 다음 통화부터 적용돼요.",
  'evaluation.name': "이름",
  'evaluation.instruction': "판정 기준",
  'evaluation.instructionHint.boolean': "어떤 경우에 ‘예’인지 적어 주세요. 판정은 통화 전체와 에이전트 시스템 프롬프트를 읽고 해요.",
  'evaluation.instructionHint.categorical': "어떤 경우에 어떤 선택지를 고를지 적어 주세요. 판정은 통화 전체와 에이전트 시스템 프롬프트를 읽고 해요.",
  'evaluation.instructionHint.numeric': "어떤 통화가 높은 점수인지 적어 주세요. 판정은 통화 전체와 에이전트 시스템 프롬프트를 읽고 해요.",
  'evaluationPreview.run': "테스트",
  'evaluationPreview.rerun': "다시 테스트",
  'evaluationPreview.judging': "판정 중…",
  'evaluationPreview.colResult': "판정",
  'evaluationPreview.intro': "이 기준으로 최근 통화 한 건을 판정해 봐요. 저장되지는 않아요.",
  'evaluationPreview.timeout': "판정이 오래 걸려요. 다시 테스트해 주세요.",
  'evaluationPreview.needInstruction': "판정 기준을 적으면 테스트할 수 있어요.",
  'evaluationPreview.needCategories': "선택지를 2개 이상 적으면 테스트할 수 있어요.",
  'evaluationPreview.failed': "실패",
  'evaluationPreview.title': "최근 통화로 테스트",
  'evaluationPreview.call': "통화",
  'evaluationPreview.noCalls': "끝난 통화가 아직 없어요. 테스트 통화를 먼저 걸어 주세요.",
  'evaluationPreview.stale': "기준이나 통화가 바뀌었어요. 아래 결과는 바뀌기 전이에요.",
  'evaluationPreview.transcript': "통화 내용",
  'evaluationPreview.role.user': "고객",
  'evaluationPreview.role.agent': "AI",
  'evaluationPreview.role.operator': "상담원",
  'evaluationPreview.noUserText': "(고객 발화 없음)",
  'evaluationPreview.openCall': "통화 열기",
  'evaluationPreview.note': "경계에 있는 통화는 판정할 때마다 결과가 달라질 수 있어요.",
  'evaluationPreview.saveNote': "저장한 기준은 다음 통화부터 적용돼요.",
  'evaluation.typeLabel': "결과 형태",
  'evaluation.typeLocked': "결과 형태는 만든 뒤 바꿀 수 없어요 — 바꾸려면 새 항목을 만들어 주세요.",
  'evaluation.categoriesChanged': "{names} 선택지가 없어져요. 지난 결과는 예전 선택지 이름으로 남아서 새 선택지와 따로 집계돼요.",
  'evaluation.categoriesTooFew': "선택지를 2개 이상 적어 주세요.",
  'evaluation.categoriesDuplicate': "{name} 선택지가 두 번 있어요.",
  'evaluation.categories': "선택지",
  'evaluation.categoryN': "선택지 {n}",
  'evaluation.addCategory': "+ 선택지 추가",
  'evaluation.unknownLabel': "정보가 부족한 통화는 ‘판단 불가’로 두기",
  'evaluation.createAndAttach': "만들고 추가",
  'sessionSummary.sev.high': '높음',
  'sessionSummary.sev.medium': '보통',
  'sessionSummary.sev.low': '낮음',

  // Early access
  'earlyAccess.revoking': '초대를 취소하는 중…',
  'earlyAccess.revoked': '초대가 취소되었습니다.',
  'earlyAccess.revokeError': '문제가 발생했습니다. 링크가 만료되었을 수 있습니다.',

  // Session page
  'session.liveConversation': '실시간 대화',
  'session.startError': '세션을 시작하지 못했습니다. 다시 시도해 주세요.',
  'session.agentSpeaking': '에이전트 말하는 중',
  'session.listening': '듣는 중…',
  'session.connecting': '연결 중…',
  'session.avatarConnecting': '아바타 연결 중…',
  'session.mute': '음소거',
  'session.unmute': '음소거 해제',
  'session.endCall': '통화 종료',
  'session.backToAgent': '← 에이전트로 돌아가기',
  'session.setupTitle': '통화 시작 전 설정',
  'session.outboundSetupTitle': '아웃바운드 통화 걸기',
  'session.destinationLabel': '대상 전화번호',
  'session.destinationHint': 'E.164 형식, 예: +821012345678',
  'session.callerIdLabel': '발신번호',
  'session.callerIdHint': '수신자에게 표시되는 번호. 조직 번호 중 발신 회선이 있는 번호만 고를 수 있습니다',
  'session.callerIdPlaceholder': '번호 선택…',
  'session.noOutboundLine': '발신 회선이 없습니다 — 운영팀에 요청하세요.',
  'session.callerLoadError': '번호 목록을 불러오지 못했습니다. 새로고침 후 다시 시도하세요.',
  'session.invalidPhone': '올바른 E.164 번호를 입력하세요, 예: +821012345678',
  'session.dynamicVarsLabel': '동적 변수 (Dynamic variables)',
  'session.dynamicVarsHint': '이번 통화에 한해 이 에이전트의 시스템 프롬프트에 있는 {{key}} 자리표시자를 치환할 값입니다.',
  'session.dynamicVarsDetected': '프롬프트에서 발견된 {{…}} 자리표시자로 미리 채웠습니다.',
  'session.noDynamicVars': '이 에이전트의 프롬프트에는 {{자리표시자}}가 없습니다. 아래에 키를 추가해 직접 값을 보내거나, 그대로 통화를 시작하세요.',
  'session.varKeyPlaceholder': '키 (예: user_name)',
  'session.varValuePlaceholder': '값 (예: Alice)',
  'session.addVar': '+ 변수 추가',
  'session.removeVar': '변수 삭제',
  'session.invalidVarKey': '영문/숫자/언더스코어만 사용할 수 있습니다.',
  'session.duplicateVarKey': '중복된 키 — 마지막 값이 사용됩니다.',
  'session.loadingAgent': '에이전트 불러오는 중…',

  // Agent auto-generate
  'autoGen.breadcrumb': 'AI 자동 생성',
  'autoGen.title': '에이전트 자동 생성',
  'autoGen.desc': '한 문단으로 설명하면 AI가 음성 에이전트를 만들어드려요.',
  'autoGen.createManually': '직접 만들기',
  'autoGen.inputLabel': '어떤 상황에서 대화하는 에이전트를 만들까요?',
  'autoGen.inputPlaceholder': '예: 해외여행 입국심사 상황에서 쓰는 영어를 연습하는 시나리오. 심사관 역할로 질문하고, 학습자가 답하면 피드백해준다.',
  'autoGen.inputHint': '에이전트가 대화할 시나리오를 알려주세요 — 누가, 왜 전화하고, 에이전트가 무엇을 해야 하는지.',
  'autoGen.submit': '에이전트 생성하기',
  'autoGen.examples': '예시 템플릿',
  'autoGen.generating': '에이전트 만드는 중...',
  'autoGen.generatingHint': 'AI가 페르소나, 음성, 시스템 프롬프트를 생성하고 있어요. 10-20초 정도 걸려요.',
  'autoGen.result': '생성 결과',
  'autoGen.fieldName': '이름',
  'autoGen.fieldRole': '역할',
  'autoGen.fieldGreeting': '인사말',
  'autoGen.fieldTone': '톤',
  'autoGen.fieldUseCase': '활용 분야',
  'autoGen.fieldSampleQueries': '예상 질문',
  'autoGen.recommendedVoice': '추천 음성',
  'autoGen.noVoiceMatch': '적합한 보이스를 찾지 못했습니다. 에이전트 상세에서 직접 선택해주세요.',
  'autoGen.voiceUpdating': '음성 변경 중...',
  'autoGen.stop': '중단',
  'autoGen.publish': '에이전트 확인하기',
  'autoGen.reset': '다시 만들기',
  'autoGen.generateError': '생성에 실패했습니다. 다시 시도해주세요.',
  'autoGen.agentLanguage': '에이전트 언어',
  'autoGen.agentLanguageHint': '에이전트가 대화할 언어를 선택하세요.',
  'autoGen.exEnglish': '영어 학습',
  'autoGen.exEnglishText': '해외여행 입국심사 상황에서 쓰는 영어를 학습하는 시나리오. 입국심사관이 "What is the purpose of your visit?", "How long will you stay?" 같은 질문을 하면, 학습자가 영어로 답해보고 피드백을 받는다. 틀려도 격려하면서 자연스러운 표현으로 교정해준다.',
  'autoGen.exFaq': 'FAQ 문의 응대',
  'autoGen.exFaqText': '온라인 쇼핑몰에 고객이 전화해서 배송 상태, 환불, 교환을 물어보는 시나리오. 주문번호를 확인하고 안내해준다. 배송 2-3일, 환불 7일 이내, 교환 무료.',
  'autoGen.exIntro': '셀프 소개',
  'autoGen.exIntroText': '보컬 코치 김아무개를 대신 소개해주는 시나리오. 문의자가 어떤 레슨인지, 가격이 얼마인지, 어떻게 예약하는지 물어보면 답해준다. 발성/믹스보이스 전문, 정규 레슨 회당 8만원, 첫 상담 무료.',
  'autoGen.female': '여성',
  'autoGen.male': '남성',
  'autoGen.youngAdult': '20-30대',
  'autoGen.middleAge': '30-40대',
  'autoGen.senior': '50대+',
  'autoGen.remaining': '회 남음',
  'autoGen.quotaExhausted': '자동생성 한도를 초과했습니다. 관리자에게 문의하세요.',
  'autoGen.preview': '미리듣기',
  'autoGen.pickOtherVoice': '다른 보이스 선택하기',
  'autoGen.voiceChangeAnytime': '보이스는 언제든지 에이전트 설정에서 변경할 수 있어요.',
  'autoGen.urlLabel': '참고 URL (선택)',
  'autoGen.urlHint': '홈페이지, FAQ 페이지 등의 URL을 입력하면 내용을 참고해서 에이전트를 만듭니다.',
  'autoGen.fieldSystemPrompt': '시스템 프롬프트',
  'autoGen.chars': '자',
  'autoGen.publishError': '에이전트 활성화에 실패했습니다. 다시 시도해주세요.',
  'autoGen.edit': '수정',
  'autoGen.save': '저장',
  'autoGen.cancel': '취소',
  'autoGen.pickTemplate': '어떤 에이전트를 만들까요?',
  'autoGen.tplFaq': 'FAQ / 고객 문의',
  'autoGen.tplAppointment': '예약 안내',
  'autoGen.tplIntro': '셀프 소개',
  'autoGen.tplEducation': '학습 코치',
  'autoGen.tplCustom': '직접 입력',
  'autoGen.qIndustry': '어떤 업종인가요?',
  'autoGen.qIndustryOpts': 'IT/SaaS,쇼핑몰,카페/음식점,부동산,교육/학원,뷰티/미용',
  'autoGen.qTopics': '주로 어떤 질문에 답하나요?',
  'autoGen.qTopicsOpts': '배송/반품/교환,가격/결제,영업시간/위치,서비스 안내,계정/회원,예약/취소',
  'autoGen.qTone': '어떤 느낌으로 답하면 좋을까요?',
  'autoGen.qToneOpts': '친근하고 따뜻하게,전문적이고 신뢰감 있게,밝고 활기차게,차분하고 정중하게',
  'autoGen.qBusinessType': '어떤 곳의 예약인가요?',
  'autoGen.qBusinessTypeOpts': '미용실/네일,병원/치과,음식점,호텔/펜션,스튜디오/촬영,학원/PT',
  'autoGen.qInfo': '안내할 정보를 골라주세요',
  'autoGen.qInfoOpts': '영업시간/휴무일,메뉴/서비스 종류,가격표,위치/주차,담당자/디자이너 지정,주의사항',
  'autoGen.qWho': '누구를 소개하나요?',
  'autoGen.qWhoOpts': '프리랜서/크리에이터,코치/강사,아티스트/뮤지션,사업가/대표,전문가/컨설턴트',
  'autoGen.qHighlight': '어떤 내용을 소개할까요?',
  'autoGen.qHighlightOpts': '약력/경력,전문 분야,포트폴리오/작품,가격/상담 방법,연락처/SNS',
  'autoGen.qIntroTone': '어떤 느낌으로 소개하면 좋을까요?',
  'autoGen.qIntroToneOpts': '담담하고 전문적으로,따뜻하고 친근하게,자신감 있고 활기차게,유머러스하게',
  'autoGen.qSubject': '어떤 과목/주제인가요?',
  'autoGen.qSubjectOpts': '영어 회화,한국어,일본어,수학,코딩,비즈니스 스킬',
  'autoGen.qAudience': '학습자는 누구인가요?',
  'autoGen.qAudienceOpts': '초등학생,중고등학생,대학생,직장인,시니어,외국인',
  'autoGen.qStyle': '교육 스타일을 골라주세요',
  'autoGen.qStyleOpts': '대화형 연습 (롤플레이),퀴즈/문제 풀이,설명 위주 (강의),격려/동기부여 중심',
  'autoGen.confirmTitle': '확인 및 생성',
  'autoGen.confirmLang': '언어',
  'autoGen.confirmComment': '에이전트가 어떻게 답하면 좋을까요?',
  'autoGen.confirmCommentPh': '예: 항상 2문장 이내로, 캐주얼하게, 무료 체험을 꼭 언급해줘',
  'autoGen.confirmCommentHint': '구체적으로 적을수록 더 정확한 에이전트가 만들어져요.',
  'autoGen.confirmUrlLabel': '홈페이지를 넣으면 더 똑똑해져요',
  'autoGen.confirmUrlHint': '영업시간, 가격, FAQ 등을 페이지에서 자동으로 학습합니다.',
  'autoGen.confirmCreate': '에이전트 만들기',
  'autoGen.confirmBack': '← 이전',
  'autoGen.templateBack': '← 템플릿 선택',
  'autoGen.prevStep': '← 이전',
  'autoGen.next': '다음',
  'autoGen.selected': '선택됨',
  'autoGen.customInput': '직접 입력',
  'autoGen.customConfirm': '확인',
  'autoGen.addUrl': '+ URL 추가',
  'autoGen.answerRequired': '최소 1개 항목을 선택해주세요.',

  // Widget embed
  'widget.title': '위젯 임베드',
  'widget.desc': '고객 웹사이트에 스크립트 한 줄을 추가하면 플로팅 버튼이 나타납니다. 클릭하면 이 에이전트와 통화할 수 있습니다.',
  'widget.themeColor': '테마 색상',
  'widget.colorHint': '#FE7E43 같은 헥스 색상을 사용하세요.',
  'widget.position': '위치',
  'widget.posBottomRight': '오른쪽 하단',
  'widget.posBottomLeft': '왼쪽 하단',
  'widget.posCenter': '중앙 하단',
  'widget.cornerRadius': '모서리 둥글기',
  'widget.radiusSharp': '각진',
  'widget.radiusSoft': '부드러운',
  'widget.radiusRound': '둥근',
  'widget.radiusPill': '알약형',
  'widget.showAgentName': '에이전트 이름 표시',
  'widget.muteButton': '음소거 버튼',
  'widget.liveTranscript': '실시간 자막',
  'widget.postCallRating': '통화 후 평가',
  'widget.whitelabel': '화이트라벨',
  'widget.brandName': '브랜드명',
  'widget.brandNamePlaceholder': '커스텀 브랜드명 (비우면 에이전트 이름 사용)',
  'widget.logoUrl': '로고 URL',
  'widget.logoUrlPlaceholder': 'https://... (비우면 기본 아이콘 사용)',
  'widget.hidePoweredBy': '\'Powered by Neona\' 표시 숨기기',
  'widget.saveSettings': '위젯 설정 저장',
  'widget.embedCode': '임베드 코드',
  'widget.widgetKey': '위젯 키',
  'widget.noWidgetKey': '먼저 {link} 페이지에서 Widget 타입 키를 발급하세요 (고객 사이트 도메인을 허용 도메인에 등록 필요).',
  'widget.apiKeysLink': 'API Keys',
  'widget.allowedDomains': '이 키의 허용 도메인: {domains}',
  'widget.noDomains': '(없음)',
  'widget.copyCode': '코드 복사',
  'widget.replaceKeyHint': 'nsk_wgt_YOUR_KEY_HERE를 실제 발급받은 키로 교체해 주세요. 키는 발급 시 한 번만 표시됩니다.',
  'widget.preview': '미리보기',
  'widget.readyToTalk': '대화할 준비가 되었습니다.',

  // Graph inspector
  'inspector.start': '시작',
  'inspector.routerModel': '라우터 모델',
  'inspector.routerModelHint': '각 턴을 어떤 에이전트가 처리할지 결정하는 LLM',
  'inspector.defaultVoice': '기본 음성',
  'inspector.defaultVoiceHint': '개별 설정이 없으면 모든 에이전트가 이 음성을 사용합니다',
  'inspector.routingRules': '라우팅 규칙',
  'inspector.addRule': '+ 규칙 추가',
  'inspector.rulePlaceholder': '사용자가 요청할 때...',
  'inspector.outputMode': '출력 모드',
  'inspector.fullHistory': '전체 기록',
  'inspector.lastMessage': '마지막 메시지',
  'inspector.fullHistoryHint': '에이전트가 모든 대화 메시지를 수신합니다',
  'inspector.lastMessageHint': '에이전트가 마지막 메시지만 수신합니다',
  'inspector.agent': '에이전트',
  'inspector.name': '이름',
  'inspector.nameHint': '소문자, 영숫자, 밑줄. 문자로 시작해야 합니다.',
  'inspector.namePlaceholder': 'lowercase_name',
  'inspector.model': '모델',
  'inspector.systemPrompt': '시스템 프롬프트',
  'inspector.promptPlaceholder': '이 에이전트의 역할과 동작을 설명하세요...',
  'inspector.tools': '툴',
  'inspector.toolsHint': '이 에이전트가 대화 중 툴을 호출할 수 있게 합니다. 해당 배포 키가 설정돼 있어야 실제로 동작합니다.',
  'tools.webSearch.desc': '웹 검색 (Brave/Tavily)',
  'tools.readOnly': 'read-only',
  'tools.requiresKey': '배포 키 필요',
  'tools.whenToUsePlaceholder': '이 에이전트가 언제 사용할지 안내 (예: "주가·날씨·뉴스는 반드시 검색하고, 모른다고 답하지 마라")',
  'inspector.voice': '음성',
  'inspector.voiceHint': '이 에이전트의 음성을 개별 설정합니다 (비우면 기본 음성 사용)',
  'inspector.voiceInherit': '기본 (상속)',
  'inspector.selectVoice': '음성 선택...',
  'inspector.confirm': '확인',
  'inspector.removeAgent': '에이전트 제거',
  'inspector.handler': '핸들러',
  'inspector.selectHandler': '핸들러 선택...',
  'inspector.maxAttempts': '최대 시도 횟수',
  'inspector.vocabItems': '어휘 항목',
  'inspector.wordPlaceholder': '단어 (EN)',
  'inspector.meaningPlaceholder': '의미 (KO)',
  'inspector.exprItems': '표현 항목',
  'inspector.exprEnPlaceholder': '영어',
  'inspector.exprKoPlaceholder': '한국어',
  'inspector.configJson': '설정 (JSON)',
  'inspector.invalidJson': '유효하지 않은 JSON',

  // Landing page (lp.*) — current marketing landing
  'lp.nav.manifesto': '매니페스토',
  'lp.nav.useCases': '활용 사례',
  'lp.nav.how': '작동 방식',
  'lp.nav.voices': '음성',
  'lp.nav.signin': '로그인',
  'lp.nav.early': '얼리 액세스',
  'lp.nav.switchTo': 'Switch to English',

  'lp.hero.eyebrow': '<b>얼리 액세스</b> · 지금 오픈 · 2026',
  'lp.hero.title': '말이 통하는<br /><span class="coral">콘텐츠.</span><span class="cursor"></span>',
  'lp.hero.lead': '일방향 콘텐츠의 시대는 끝났습니다. 학습자와 대화하고, 시청자를 사로잡고, 고객을 상담하는 <b>음성 에이전트</b>를 만드세요. 단순히 재생되는 것이 아니라 <b>이야기를 들려주는</b> 에이전트입니다.',
  'lp.hero.emailPlaceholder': 'work@yourcompany.com',
  'lp.hero.meta1': 'STT · LLM · TTS를 하나의 파이프라인으로',
  'lp.hero.meta2': '700개 이상의 캐릭터 음성',
  'lp.hero.meta3': '노코드',
  'lp.hero.cardTag': 'LIVE · Neona Agent',
  'lp.hero.cardName': 'Mia',
  'lp.hero.cardRole': '영어 튜터',
  'lp.hero.cardPersona': '페르소나 · 친근하고 격려하는',
  'lp.hero.cardTranscript': '안녕하세요! 오늘 배울 내용을 같이 살펴볼까요? 영어로 커피 주문하기, 한번 해볼 준비 됐나요?',

  'lp.cta.request': '얼리 액세스 신청',

  'lp.trust.label': '<b>Neosapience</b>의 음성 AI로 구동됩니다 — <b>Typecast</b>를 만든 바로 그 팀',
  'lp.trust.stat1': '활성 사용자',
  'lp.trust.stat2': '국가',
  'lp.trust.stat3': '캐릭터 음성',
  'lp.trust.stat4': 'Pre-IPO 투자 유치',

  'lp.manifesto.eyebrow': '전환점',
  'lp.manifesto.title': '일방적으로 전달하는 콘텐츠에서,<br />함께 <span class="coral">대화하는</span> 콘텐츠로.',
  'lp.manifesto.blurb': '매번 똑같이 재생되는 영상, 오디오, 텍스트만으로는 충분하지 않습니다. Neona는 콘텐츠를 사용자와 함께 이야기를 만들어가는 <b>스토리텔러</b>로 바꿉니다.',
  'lp.manifesto.before': 'Before',
  'lp.manifesto.beforeH': '기존 콘텐츠',
  'lp.manifesto.beforeP': '제작자가 한 번 만들어 모두에게 똑같이 전달됩니다. 시청자의 반응, 질문, 수준은 콘텐츠에 닿지 못합니다. 참여는 좋아요와 댓글에서 멈춥니다.',
  'lp.manifesto.after': 'With Neona',
  'lp.manifesto.afterH': '<span class="coral">대화하는</span> 콘텐츠',
  'lp.manifesto.afterP': '이야기를 만들고, 정보를 전달하고, 가르치고, 상담하는 대화를 사용자와 함께 나눕니다. 감정을 표현하는 음성이 몰입감 있는 양방향 경험을 만듭니다.',

  'lp.cases.eyebrow': '활용 사례',
  'lp.cases.title': '콘텐츠 제작자, 운영자,<br /><span class="coral">PM</span>을 위해.',
  'lp.cases.blurb': '<b>교육, 마케팅, 미디어, 엔터테인먼트</b> 팀이 코드 없이 설계하고, 배포하고, 개선합니다.',
  'lp.cases.eduNum': '01 · 교육',
  'lp.cases.eduH': '모든 학습자와 <span class="coral">대화하는</span> AI 튜터',
  'lp.cases.eduDesc': '교재나 수업 스크립트를 넣기만 하세요. AI 튜터가 자료를 읽고 학생별로 예습·복습·1:1 세션을 진행합니다. 무제한 연습과 수준별 맞춤 피드백을 제공합니다.',
  'lp.cases.eduTags': '어학,시험 대비,예습·복습,1:1 튜터링',
  'lp.cases.mktNum': '02 · 마케팅',
  'lp.cases.mktH': '제품을 <span class="coral">설명하는</span> 마케팅',
  'lp.cases.mktDesc': '잠재 고객과 대화하며 니즈를 파악하고, 맥락에 맞게 제품을 안내하는 음성 에이전트. 정적인 랜딩 페이지가 살아 있는 브랜드 경험이 됩니다.',
  'lp.cases.mktTags': '제품 데모,리드 검증,온보딩,FAQ',
  'lp.cases.medNum': '03 · 미디어',
  'lp.cases.medH': '뉴스를 듣는 것을 넘어 <span class="coral">대화하기</span>',
  'lp.cases.medDesc': '청취자와 대화하는 오디오. 추가 질문을 던지고, 맥락을 깊이 파고들며, 수동적 소비를 능동적 탐구로 바꿉니다.',
  'lp.cases.medTags': '뉴스·팟캐스트,오디오북,다큐멘터리,저널리즘',
  'lp.cases.entNum': '04 · 엔터테인먼트',
  'lp.cases.entH': '이야기 속에 <span class="coral">살아 있는</span> 캐릭터',
  'lp.cases.entDesc': '게임 NPC, 인터랙티브 픽션, 캐릭터 챗봇 — 700개 이상의 음성과 페르소나 빌더로 살아 있는 캐릭터를 만들고, 팬들이 직접 대화하게 하세요.',
  'lp.cases.entTags': '게임 NPC,캐릭터 IP,인터랙티브 픽션,가상 컴패니언',
  'lp.cases.exploreAll': '10가지 활용 사례 모두 보기',

  'lp.how.eyebrow': '작동 방식',
  'lp.how.title': '페르소나를 작성하고, 콘텐츠를 넣고, <span class="coral">배포하세요</span>.',
  'lp.how.blurb': '개발이 필요 없습니다. STT, LLM, TTS — 필요한 모든 음성 기술을 Neosapience가 직접 구축한 <b>하나의 파이프라인</b>으로 제공합니다.',
  'lp.how.step1Num': 'Step 01',
  'lp.how.step1H': '페르소나 작성',
  'lp.how.step1P': '프롬프트로 음성, 톤, 성격을 정의하세요. 700개 이상의 캐릭터 음성 중에서 고르거나 브랜드 고유의 음성을 복제할 수 있습니다.',
  'lp.how.step2Num': 'Step 02',
  'lp.how.step2H': '콘텐츠 연결',
  'lp.how.step2P': '기존 교재, 매뉴얼, 스크립트, FAQ를 업로드하세요. 에이전트가 자료를 이해하고 스스로 대화를 이끕니다.',
  'lp.how.step3Num': 'Step 03',
  'lp.how.step3H': '배포 및 운영',
  'lp.how.step3P': '웹, 앱, 위젯으로 임베드하세요. 대화 로그, 이탈 지점, 만족도를 대시보드에서 추적하고 페르소나를 실시간으로 업데이트합니다.',

  'lp.voices.eyebrow': '음성 라이브러리',
  'lp.voices.title': '<span class="coral">700개 이상</span>의 캐릭터 음성,<br />어떤 이야기든 준비되어 있습니다.',
  'lp.voices.desc': '들려줄 이야기에 어울리는 음성을 고르세요 — 내레이터, 튜터, 호스트, NPC까지. 또는 몇 분 만에 직접 음성을 복제할 수 있습니다.',

  'lp.role.tutor': '튜터',
  'lp.role.storyteller': '스토리텔러',
  'lp.role.coach': '코치',
  'lp.role.narrator': '내레이터',
  'lp.role.anchor': '앵커',
  'lp.role.instructor': '강사',
  'lp.role.companion': '컴패니언',
  'lp.role.gameNpc': '게임 NPC',
  'lp.role.counselor': '상담사',
  'lp.role.host': '호스트',
  'lp.role.mentor': '멘토',
  'lp.role.news': '뉴스',
  'lp.role.wellness': '웰니스',
  'lp.role.trainer': '트레이너',

  'lp.why.eyebrow': 'Why Neona',
  'lp.why.title': '몰입은 <span class="coral">디테일</span>에서 시작됩니다.',
  'lp.why.blurb': '오랜 시간 다져온 <b>감정 표현 음성</b> 기술이 대화를 진짜처럼 느껴지게 합니다. 서드파티 API를 짜깁기한 것이 아닙니다.',
  'lp.why.d1H': '스마트 이모션',
  'lp.why.d1P': '칭찬, 격려, 놀라움, 집중 — 대화가 전개되는 동안 실제 사람이 말하듯 감정이 실시간으로 변합니다.',
  'lp.why.d1S': '감정이 담긴 음성',
  'lp.why.d2H': '스트리밍 응답',
  'lp.why.d2P': '지연 없는 양방향 대화. 인터페이스가 아니라 사람과 이야기하는 듯한 호흡입니다.',
  'lp.why.d2S': '기다림 없이',
  'lp.why.d3H': '다국어 및 코드 스위칭',
  'lp.why.d3P': '실제 이중언어 화자처럼 문장 중간에도 언어를 전환합니다. 한국어-영어 전환도 자연스럽게 이어집니다.',
  'lp.why.d3S': '12개 이상 언어',
  'lp.why.d4H': '노코드 운영',
  'lp.why.d4P': '콘텐츠 팀이 개발자 없이 직접 만들고, 배포하고, 개선합니다. 페르소나, 스크립트, 음성을 실시간으로 업데이트하세요.',
  'lp.why.d4S': '콘텐츠 팀이 직접',
  'lp.why.d5H': '문서 인식 RAG',
  'lp.why.d5P': '기존 교재, 매뉴얼, 스크립트를 그대로 사용하세요. 에이전트가 이를 이해하고 세션을 자율적으로 진행합니다.',
  'lp.why.d5S': '내 콘텐츠 그대로',
  'lp.why.d6H': '풀스택 자체 개발',
  'lp.why.d6P': 'STT, LLM, TTS를 모두 직접 개발해 단일 파이프라인으로 제공합니다. 서드파티 API 의존이 없어 품질과 비용이 예측 가능합니다.',
  'lp.why.d6S': '엔드투엔드 음성 AI',

  'lp.study.eyebrow': '도입 사례',
  'lp.study.title': '이미 실제 교실에서<br /><span class="coral">운영 중</span>입니다.',
  'lp.study.partner': '윤선생 × Neosapience · Tuit 2026년 7월 출시',
  'lp.study.quote': '영어 수업 전 <span class="coral">5~10분</span> 동안, 학생들은 Neona로 만든 AI 튜터와 대화하며 그날 수업을 예습합니다. 교재로 수업을 이끄는 선생님처럼, AI 튜터가 <span class="coral">그날의 수업 자료를 따라</span> 학생별 예습을 안내합니다.',
  'lp.study.source': "<b>윤선생</b>이 원어민 화상영어 서비스 'Tuit'에 Neona를 도입했습니다<span class=\"news-tag\">전자신문 · 2026.05.19</span>",
  'lp.study.f1H': '스마트 이모션',
  'lp.study.f1D': '칭찬과 격려가 실시간으로 음성에 반영됩니다',
  'lp.study.f2H': '스트리밍 음성',
  'lp.study.f2D': '지연 없는 자연스러운 양방향 대화',
  'lp.study.f3H': '아이 친화적 페르소나',
  'lp.study.f3D': '학생 몰입을 위해 설계된 음성',
  'lp.study.f4H': '자율 세션 진행',
  'lp.study.f4D': '윤선생의 교육 방식을 반영합니다',

  'lp.final.eyebrow': '얼리 액세스',
  'lp.final.title': '당신의 콘텐츠가<br /><span class="coral">대화하게</span> 하세요.',
  'lp.final.lead': '교육, 마케팅, 미디어, 엔터테인먼트 분야의 콘텐츠 팀을 모집합니다. 얼리 액세스 참여 팀에는 1:1 온보딩과 전체 연동 지원을 제공합니다.',
  'lp.final.fine': '<b>한정 모집</b> · 도메인 우선 선정 · 4주 무료 PoC',

  'lp.foot.blurb': 'Interactive Storytelling Voice Agent — Typecast를 만든 Neosapience의 제품입니다.',
  'lp.foot.product': '제품',
  'lp.foot.why': 'Why Neona',
  'lp.foot.company': '회사',
  'lp.foot.contact': '문의',
  'lp.foot.sales': '영업팀 문의',
  'lp.foot.copyright': '© {year} <b>Neosapience, Inc.</b> All rights reserved.',
}

const _translations: Record<Locale, Record<string, string>> = { en, ko }

// ---------------------------------------------------------------------------
// Translation function
// ---------------------------------------------------------------------------

export function t(key: string, params?: Record<string, string | number>): string {
  const dict = _translations[_locale]
  let text = dict[key] ?? _translations.en[key] ?? key

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(`{${k}}`, String(v))
    }
  }
  return text
}

/**
 * `**굵게**` 마커가 섞인 문장을 렌더 가능한 조각으로 돌려준다.
 * 문장 일부만 강조하려고 번역문을 여러 키로 쪼개면 어순이 다른 언어에서 깨진다 —
 * 마커는 문장을 한 덩어리로 유지하기 위한 최소 장치다. 마커가 없으면 문자열 그대로.
 */
export function tRich(key: string, params?: Record<string, string | number>): ReactNode {
  const text = t(key, params)
  // split 의 캡처 그룹 덕에 홀수 인덱스가 강조 구간이 된다.
  const parts = text.split(/\*\*(.+?)\*\*/g)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    i % 2 === 1 ? createElement('strong', { key: i }, part) : part,
  )
}
